import { deriveKey, encryptFile, decryptFile } from '../../utils/patientFileCrypto';
import { createBufferedLiveAudio, LIVE_PCM_RATE } from '../../utils/bufferedLiveAudio';
import { useClientWorkspace } from './ClientWorkspaceContext';
import { supportsMeetingCapture } from './meetingCapture';
import { useDocumentSharing } from './useDocumentSharing';
import { createLiveStartupDiagnostics } from '../../utils/liveStartupDiagnostics';
import { createAudioCaptureOwner } from '../../utils/audioCaptureOwner';
import { audioDiagnostic, observeAudioStream, inspectRecordedAudio } from '../../utils/microphoneDiagnostics';
import { useLanguagePreferences } from '../../utils/useLanguagePreferences';
import { recordedAttemptKey, readRecordedAttempt, writeRecordedAttempt, clearRecordedAttempt } from '../../utils/recordedTranscriptionAttempt';
import { saveRecordedTranscript } from '../../utils/saveRecordedTranscript';
import { stopSpeechRecognizer } from '../../utils/stopSpeechRecognizer';
import { sophieRequestReportId, saveSophieWriteReport } from '../../utils/sophieReportContext';
import { findReferencingReports, markDeletedReportSources, deletedSourceId } from '../../utils/reportSources';
import { prepareReportSourceAttachments } from '../../utils/digitalDocumentSource';
import { reportSourceManifest } from '../../utils/reportSourcePreparation';
import { canReviewOcr } from '../../utils/attachmentOcr';
import { partitionPatientUploads, newPatientStorageName, patientAttachmentId, attachmentIndexingAlert } from '../../utils/patientAttachmentFiles';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRecordingNavigationGuard } from '../../context/RecordingNavigationGuard';
import { useLocation } from 'react-router-dom';
import { TikcareAssistantService } from '../../supabaseClient.js';
import { ResultReason } from 'microsoft-cognitiveservices-speech-sdk';
import { requireReportContent } from '../../utils/reportContent';
import { hasReportInputText, validateReportInput } from '../../utils/reportInput';
import { saveRefinedTranscript } from '../../utils/saveRefinedTranscript';
import { saveConsultationSource } from '../../utils/saveConsultationSource';
import { supabase } from '../../supabaseClient.js';
import { v4 as uuidv4 } from 'uuid';
import useAlert from '../../alert/useAlert.js';
import { toStorageKeySegment } from '../../utils/storageKey.js';
import { uploadWithProgress, createBatchProgress } from '../../utils/storageUpload.js';
import { CW_UNMETERED_PROVIDERS_ENABLED } from '../../constants/cwUnmeteredProviders.js'; // CW-006 kill-switch (add-speaker-labels-stream / medical-icd-codes)
import { useSelector, shallowEqual } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setDrawerOpen } from '../../store.js';
import axios, { all } from 'axios';
import { openLiveSession, heartbeatLiveSession, settleLiveSession } from '../../utils/token_util.js';
import { createLiveConnectionMonitor } from '../../utils/liveConnectionMonitor';
import { forgetLiveSession, markLiveSessionStopped, reconcilePendingLiveSessions, rememberLiveSession, settlePendingLiveSession } from '../../utils/liveSessionRecovery';
import { playConnectionLostSound } from '../../utils/connectionLostSound';
import { authedFetch } from '../../utils/backendFetch.js';
import { normalizeForMatch } from './sourceMatch';
import { prepareCheckSources } from './checkSources';
import { prepareHomePracticeTemplate } from './prepareHomePracticeTemplate';
import { taipeiDate } from './consultationSessionDate';
import { TRANSCRIPT_INDEX_ENABLED, TRANSCRIPT_IDLE_MS, buildTranscriptIndexForm, transcriptFingerprint, hasTranscriptContent } from './transcriptIndex';
import { createRefinementDiagnostics, refineTranscript, refinementMessage, safeRefinementSaveError } from '../../utils/refinementDiagnostics';
import { streamAskSophie, shouldPersistAnswer, isCompletedTurn } from '../../utils/askSophieStream.js';
import { ragStreamHeaders } from '../../utils/ragStreamAuth.js';
import { RAG_STREAM_ENABLED, RAG_STREAM_URL, ASK_SOPHIE_STREAM_PATH, STAGE_LABELS } from '../../constants/askSophieStreaming.js';
import { creditWalletOperationCompleted } from '../../creditWallet/WalletBalanceContext.js';
import { track, EVENTS } from '../../analytics';
import { logTranscriptSaveFailure } from '../../utils/transcriptSaveDiagnostics.js';
import { createNoSpeechWatchdog } from '../../utils/noSpeechWatchdog.js';
import levenshtein from 'fast-levenshtein';


import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { ImageResize } from '../Settings/Template/ImageResize.ts';
import mammoth from "mammoth";
import { languages, transcriptionLanguages } from '../../constants/languages.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import FileSaver from 'file-saver';
import { set } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import * as xlsx from 'xlsx';
import {
  GENERATE_BLOCKED,
  GENERATE_DIALOG,
  AFTER_ACCEPT_OFFER_REGENERATE,
  decideGenerateGate,
  decideAfterAccept,
  hasReportForConsultation,
} from '../../utils/refinePendingGate.js';


const speechsdk = require('microsoft-cognitiveservices-speech-sdk');
let liveHeartbeatInterval = null;
// Transcript saves give up (and free the save queue) after this long, queue time included.
const TRANSCRIPT_SAVE_TIMEOUT_MS = 20000;
// After a timed-out save, re-save the latest text once this long after the next success.
const STALE_WRITE_RESAVE_MS = 30000;
// Warns during a live session when nothing has been heard yet (noSpeechWatchdog.js).
let liveNoSpeechWatchdog = null;
const stopLiveNoSpeechWatchdog = () => { liveNoSpeechWatchdog?.stop(); liveNoSpeechWatchdog = null; };

// Report generation can go through the PII service (de-identifies before the LLM call)
// or the main backend. Default comes from REACT_APP_USE_PII_SERVICE; override at runtime
// in the browser console via localStorage.setItem('use_pii_service', 'true' | 'false') —
// takes effect on the next generation, no rebuild needed.
// Report generation is a metered Scribe operation: POST /scribe/reports debits a
// fixed 1 credit (2 halves) from the signed-in therapist's wallet, then
// generates the report.  The backend identifies the user from the JWT, so the
// body carries no user id.  The legacy /generateReportGPTv2 and the PII-service
// routes are unmetered and the main backend now answers them with 410.
//
// `formData` is the same FormData the callers have always built; it is read
// here and sent as JSON through the existing metered route.
// Digital documents are decrypted/extracted through the existing attachment reader
// and sent as notes. PDF/images remain authorized backend file references.
const skippedAttachmentMessage = (skipped) =>
  `Skipped for report generation (unsupported type): ${skipped.join(', ')}. ` +
  'DOCX, TXT, CSV, XLSX, PDF and image attachments are supported; audio must be transcribed first.';

// Pre-flight guards for the metered report call. Both are advisory — the
// backend stays the authority (wallet begin RPC, de-id fail-closed) — but
// they turn two slow failure modes into immediate, explained ones: a ≤0
// balance used to surface only after ~30s as a 402, and a cold PII service
// burned the whole 120s de-id timeout before failing with a blank report.
const hasCreditsForReport = async () => {
  try {
    const { data: { session } = {} } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return true;
    const { data } = await supabase
      .from('therapist_credit_wallets')
      .select('balance_halves')
      .eq('user_id', userId)
      .maybeSingle();
    if (data && Number(data.balance_halves) <= 0) return false;
  } catch (error) {
    // Balance unknown — let the backend decide rather than block the user.
  }
  return true;
};

const PII_WARMUP_POLL_MS = 5000;
const PII_WARMUP_MAX_MS = 180000;
// `setWarming` is a boolean setter (drives the Generating Report dialog's
// "Starting AI Service" state); it is guaranteed to be reset to false on
// every exit path.
const waitForPiiService = async (setWarming) => {
  const probe = async () => {
    const res = await authedFetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/scribe/pii-status`);
    if (!res.ok) throw new Error(`pii-status responded ${res.status}`);
    return res.json();
  };
  let status;
  try {
    status = await probe();
  } catch (error) {
    return; // probe endpoint missing or unreachable: proceed, backend enforces
  }
  if (status.warm) return;
  setWarming?.(true);
  try {
    const deadline = Date.now() + PII_WARMUP_MAX_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, PII_WARMUP_POLL_MS));
      try {
        status = await probe();
      } catch (error) {
        return;
      }
      if (status.warm) return;
    }
    // Still cold after 3 minutes: fall through and let the real request try.
  } finally {
    setWarming?.(false);
  }
};

const postGenerateReport = async (formData, action = 'generate', setWarming = null, getLanguageSettings) => {
  const template = formData.get('template') || '';
  const notes = formData.get('notes') || '';
  const fileReferences = formData.getAll('file_references').filter(Boolean);
  validateReportInput(template, notes, fileReferences);
  if (!(await hasCreditsForReport())) {
    throw new Error('Not enough credits to generate a report. Please top up your wallet.');
  }
  await waitForPiiService(setWarming);
  if (formData.getAll('files').length > 0) {
    // Raw uploads never reach the metered backend; attachments travel as
    // file_references (storage keys) appended by the call sites above.
    throw new Error('Attachments are not yet supported for report generation. Remove the attached files and try again.');
  }
  const languageSettings = await getLanguageSettings();
  const body = {
    operation_id: uuidv4(),
    action,
    notes: hasReportInputText(notes) ? notes : '',
    template,
    output_language: languageSettings.output_language,
    spelling_format: languageSettings.spelling_format,
    ...(fileReferences.length ? { file_references: fileReferences } : {}),
    ...(formData.has('source_manifest') ? { source_manifest: JSON.parse(formData.get('source_manifest')) } : {}),
  };
  const startedAt = performance.now();
  const response = await authedFetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/scribe/reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  console.log(`[report-gen] metered backend responded ${response.status} in ${((performance.now() - startedAt) / 1000).toFixed(1)}s`);
  if (!response.ok) {
    const detail = payload.error || payload.message || '';
    // The backend maps CW_INSUFFICIENT_CREDITS to a 402 with body "out of credits".
    if (response.status === 402 || /out of credits|CW_INSUFFICIENT_CREDITS/i.test(detail)) {
      throw new Error('Not enough credits to generate a report. Please top up your wallet.');
    }
    // 400 "notes required": generating with an empty transcript (e.g. right
    // after a seconds-long session). Nothing was charged.
    if (response.status === 400 && /notes required/i.test(detail)) {
      throw new Error('The transcript is empty — record or add notes before generating a report.');
    }
    // 400 "report attachments too large": the backend caps attachments at 15 MB in total
    // (metering.MAX_REPORT_ATTACHMENT_BYTES). Nothing was charged.
    if (response.status === 400 && /attachments too large/i.test(detail)) {
      throw new Error('The attached files are too large (15 MB total). Remove or replace some files and try again.');
    }
    if (response.status === 503 && /reconcil/i.test(detail)) {
      // The credit was taken but the provider outcome is unknown; a blind retry
      // would charge again.
      throw new Error('Report generation could not be confirmed and is under review. Please do not retry immediately.');
    }
    throw new Error(detail || `Report generation failed (${response.status}).`);
  }
  creditWalletOperationCompleted();
  // Callers read `response.data.summary`, matching the legacy response shape.
  return { data: { summary: payload.report ?? '' } };
};
const MEDICAL_OPTIONS = [
  { value: 'ICD-10', label: 'ICD 10', bgcolor: '#FEF0E0', textColor: '#F69C32'},
  { value: 'ICD-11', label: 'ICD 11', bgcolor: '#EFE3FF', textColor: '#A82EE1'},
//   { value: 'ICPC', label: 'ICPC', bgcolor: '#D9E8FF', textColor: '#0066FF'},
];


export function useMedicalScribeSummaryData() {
    const { setRecordingBusy, confirmNavigation } = useRecordingNavigationGuard();
    const recordingOperationRef = useRef(false);
    const recordingBusyRef = useRef(false);
    const recordedWorkRef = useRef(false);
    const recordedUnsavedRef = useRef(false);
    const recordedAttemptsRef = useRef(new Map());
    const [recordedTranscriptionRecovery, setRecordedTranscriptionRecovery] = useState(null);
    const recognizingRef = useRef(false);
    const liveSyncRef = useRef(null);
    // Watches the network while Live runs (see liveConnectionMonitor.js).
    const liveMonitorRef = useRef(null);
    // Always the latest handler, so SDK callbacks created at start never use a
    // stale consultation or save function.
    const liveConnectionLostRef = useRef(() => {});
    const recordingAudioRef = useRef(false);
    const recordingPageMountedRef = useRef(true);
    const captureOwnerRef = useRef(null);
    const activeRecorderRef = useRef(null);
    const liveAudioCleanupRef = useRef(null);
    const liveBufferedAudioRef = useRef(null);
    const liveInputEndedRef = useRef(null);
    const liveStartupDiagnosticsRef = useRef(null);
    const activeRecognizerRef = useRef(null);
    const getCaptureOwner = () => {
      if (!recordingPageMountedRef.current) throw new Error('Audio capture was cancelled');
      if (!captureOwnerRef.current) captureOwnerRef.current = createAudioCaptureOwner();
      return captureOwnerRef.current;
    };
    const releaseLiveAudio = () => {
      liveAudioCleanupRef.current?.();
      liveAudioCleanupRef.current = null;
    };

    const [isStarting, setIsStarting] = useState(false);
    const [startupStage, setStartupStage] = useState('');
    const updateRecordingBusy = useCallback(value => {
      recordingBusyRef.current = value;
      if (recordingPageMountedRef.current) setRecordingBusy(value);
    }, [setRecordingBusy]);
    const refreshRecordingBusy = () => updateRecordingBusy(Boolean(
      recordingOperationRef.current || recognizingRef.current || recordingAudioRef.current || liveSessionRef.current || recordedWorkRef.current || recordedUnsavedRef.current
      || (liveSyncRef.current && !liveSyncRef.current.done)
    ));
    useEffect(() => {
      recordingPageMountedRef.current = true;
      return () => {
        recordingPageMountedRef.current = false;
        liveStartupDiagnosticsRef.current?.finish('cancelled');
        // Navigation guards handle normal exits. Forced unmount must not keep capturing.
        const recorder = activeRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
          try { recorder.stop(); } catch (_) {}
        }
        try { activeRecognizerRef.current?.close(); } catch (_) {}
        activeRecognizerRef.current = null;
        releaseLiveAudio();
        if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
        stopLiveNoSpeechWatchdog();
        liveMonitorRef.current?.dispose();
        liveMonitorRef.current = null;
        if (staleResaveTimerRef.current) { clearTimeout(staleResaveTimerRef.current); staleResaveTimerRef.current = null; }
        captureOwnerRef.current?.dispose();
        captureOwnerRef.current = null;
        setRecordingBusy(false);
      };
    }, [setRecordingBusy]);
    // Router and location related
    const location = useLocation();
    const { state } = location;
    const navigate = useNavigate();
    const workspace = useClientWorkspace();
    const params = workspace?.params || state?.params;
    const { teamMembersData, latestComplaint } = params || {};
    const id = params?.patient?.id || null;
    const [patient, setPatient] = useState(params?.patient || null);
  
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [audioMimeType, setAudioMimeType] = useState('audio/webm');
    // Language and view states
    const [mainView, setMainView] = useState('Consultation');
    const [leftView, setLeftView] = useState('Transcription');
    const [rightView, setRightView] = useState('Select Template');
    const [transcriptionLanguage, setTranscriptionLanguage] = useState({ value: 'yue', label: 'Cantonese' });
    const [showTranscriptionLanguagePopup, setShowTranscriptionLanguagePopup] = useState(false);
    const [selectedAudioItemForTranscription, setSelectedAudioItemForTranscription] = useState(null);

    const [responseSources, setResponseSources] = useState([]);
    const [openSource, setOpenSource] = useState(false);
    
    // Popup states
    const [isPopupReport, setPopupReport] = useState(false);
    const [isPopupPdfFill, setPopupPdfFill] = useState(false);
    
    // Medical data states
    const [currentConsultation, setCurrentConsultation] = useState(null);
    const activeConsultationRef = useRef(null);
    activeConsultationRef.current = currentConsultation?.id;
    const refinementBusyRef = useRef(false);
    const refinementController = useRef(null);
    const refinementDone = useRef(Promise.resolve());
    const saveQueue = useRef(Promise.resolve());
    // The consultation whose save timed out (its aborted request may still land
    // after a newer one), and the pending "re-save the latest text" timer. The
    // timer is cancelled on unmount and when the consultation or user changes.
    const uncertainSaveRef = useRef(null);
    const staleResaveTimerRef = useRef(null);
    const cancelStaleResave = () => {
      if (staleResaveTimerRef.current) { clearTimeout(staleResaveTimerRef.current); staleResaveTimerRef.current = null; }
    };
    const [transcriptSaveStatus, setTranscriptSaveStatus] = useState('idle');
    const cancelActiveRefinement = async () => {
      refinementController.current?.abort();
      await refinementDone.current;
      // A complete result can still be waiting for persistence. Cancelling
      // the stream must not discard it, including when completion races a click.
      if (pendingRefinementResult.current) return;
      setIsPendingRefinement(false);
      setRefinedPreview('');
    };
    
    // Edit states
    const [editNote, setEditNote] = useState(false);
    const [editTranscription, setEditTranscription] = useState(false);
    const [editRemarks, setEditRemarks] = useState(false);
    
    // Content states
    const [transcription, setTranscription] = useState('');
    const [remarks, setRemarks] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [editReportContent, setEditReportContent] = useState(false);
    const [editPatientInsight, setEditPatientInsight] = useState(false);

    
    // Loading states
    const [loading, setLoading] = useState(false);
    const [savingConsultation, setSavingConsultation] = useState(false);
    const [transcriptionStage, setTranscriptionStage] = useState(null);
    // True while generation is blocked on the PII de-id service warming up
    // from a cold start; drives the "Starting AI Service" dialog state.
    const [piiWarming, setPiiWarming] = useState(false);
    const [isRefiningTranscript, setIsRefiningTranscript] = useState(false);
    const [refineTranscriptError, setRefineTranscriptError] = useState(false);
    const [refineTranscriptFailure, setRefineTranscriptFailure] = useState(null);
    const [noSpeechDetected, setNoSpeechDetected] = useState(false);
    const [hasCompletedTranscription, setHasCompletedTranscription] = useState(false);
    const [liveRecognitionInterrupted, setLiveRecognitionInterrupted] = useState(false);
    // After Live stops (Stop, or the connection was lost) until the transcript is
    // saved and the wallet session settled: { reason: 'stopped' | 'connection_lost',
    // sessionId, saved, settled, done }. Settlement always uses this ORIGINAL id.
    // A finished 'connection_lost' record is kept so its message stays visible.
    const [liveSync, setLiveSyncState] = useState(null);
    const setLiveSync = next => {
      liveSyncRef.current = next;
      setLiveSyncState(next);
      refreshRecordingBusy();
    };
    // Save and settlement retry independently: a hanging one never blocks the other.
    const liveSaveRunningRef = useRef(false);
    const liveSettleRunningRef = useRef(false);
    // SDK `disconnected` seen; stopping unless it reconnects within ~5 s.
    const [liveConnectionWarning, setLiveConnectionWarning] = useState(false);
    const [transcriptRevealToken, setTranscriptRevealToken] = useState(0);
    useEffect(() => {
      if (transcription?.trim()) setNoSpeechDetected(false);
    }, [transcription]);
    const [isPendingRefinement, setIsPendingRefinement] = useState(false);
    const [refinedPreview, setRefinedPreview] = useState('');
    const pendingRefinementResult = useRef(null);
    const [reportDateSelection, setReportDateSelection] = useState(() => taipeiDate());
    const selectedReportDateLabel = () => {
      const date = /^\d{4}-\d{2}-\d{2}$/.test(reportDateSelection) ? reportDateSelection : taipeiDate();
      return new Date(`${date}T12:00:00+08:00`).toLocaleDateString('en-US', {
        timeZone: 'Asia/Taipei', day: '2-digit', month: 'short', year: 'numeric',
      });
    };
    const [consultationLoading, setConsultationLoading] = useState(false);
    const [codeLoading, setCodeLoading] = useState(false);
    const [patientInsightsLoading, setPatientInsightsLoading] = useState(false);
    const [patientsInsightsData, setPatientsInsightsData] = useState('');
    const [popupPatientInsights, setPopupPatientInsights] = useState(false);
    const [editChiefComplaint, setEditChiefComplaint] = useState(false);
    
    // Speech recognition states
    const [recognizer, setRecognizer] = useState(null);
    const [isRecognizing, setRecognizingState] = useState(false);
    const setIsRecognizing = value => {
      recognizingRef.current = value;
      setRecognizingState(value);
      refreshRecordingBusy();
    };
    // True while a stop is in flight: the recognizer is being closed and the
    // wallet session settled. The Stop button is disabled and shows a spinner
    // until this clears, so the UI only returns to a recordable state after the
    // session is actually settled (credits debited, server session closed).
    const [isSettling, setIsSettling] = useState(false);
    const audioCaptureStartedAtRef = useRef(null);
    const recordingStartedAtRef = useRef(null); // analytics: recording duration
    const throttleTimeout = useRef(null);
    // Dedupes concurrent report generations: each call to /scribe/reports is
    // billed (1 credit), so overlapping invocations share one request.
    const summarizeInFlightRef = useRef(null);
    const [firstView, setFirstView] = useState(false);
    const textFieldRef = useRef(null);
    const transcriptionRef = useRef(transcription);
    const remarksRef = useRef(remarks);
    // Ask Sophie transcript index (transcriptIndex.js): the fingerprint of the
    // content last posted, consultations deleted in this session (a late idle
    // or leave flush must never re-create their document), the idle timer, and
    // the latest indexing helper so the timer/flush effects can call it without
    // re-subscribing on every render.
    const lastIndexedTranscriptRef = useRef(null);
    const deletedConsultationIdsRef = useRef(new Set());
    const transcriptIndexTimer = useRef(null);
    const indexTranscriptRef = useRef(() => {});

    // Analytics: if the user navigates away mid-recording, the stop branch in
    // sttFromMic() never runs and recording_completed would be lost (funnel
    // shows a fake drop-off). Fire it on unmount with success:false instead.
    useEffect(() => {
      return () => {
        if (recordingStartedAtRef.current) {
          track(EVENTS.RECORDING_COMPLETED, {
            duration_ms: Date.now() - recordingStartedAtRef.current,
            success: false,
          });
          recordingStartedAtRef.current = null;
        }
        // Leaving the recording page mid-recording (a route change, e.g. going to
        // /settings) never runs the Stop button, which is what settles the live
        // session. Without this, the session lingers OPEN and the NEXT "record"
        // fails (HTTP 502) until the 180s server-side stale sweep. Null the ref
        // first so the heartbeat interval and recognizer callbacks go inert, then
        // fire a best-effort keepalive settle that completes even as the page
        // unmounts.
        // The id stays remembered until the server confirms the settlement, so a
        // lost request is retried on the next visit.
        if (liveSessionRef.current) {
          const sessionId = liveSessionRef.current;
          liveSessionRef.current = null;
          const userId = userIdRef.current;
          markLiveSessionStopped(userId, sessionId);
          settleLiveSession(sessionId, { keepalive: true }).then(() => forgetLiveSession(userId, sessionId)).catch(() => {});
        }
      };
    }, []);

    // Warning only: beforeunload also runs when the user chooses to STAY.
    // Never stop or settle here. Persisted session recovery handles a reload.
    useEffect(() => {
      const onBeforeUnload = (e) => {
        if (!recordingBusyRef.current) return;
        e.preventDefault();
        e.returnValue = '';
        return '';
      };
      window.addEventListener('beforeunload', onBeforeUnload);
      const onPageHide = event => {
        // Only a confirmed document exit, never the cancellable warning. Keep
        // the persisted ID for recovery if this best-effort request is lost.
        // A BFCache page can return with its live state: don't settle it here.
        if (!event.persisted) liveStartupDiagnosticsRef.current?.finish('cancelled');
        if (!event.persisted && liveSessionRef.current) {
          const sessionId = liveSessionRef.current;
          const userId = userIdRef.current;
          markLiveSessionStopped(userId, sessionId);
          settleLiveSession(sessionId, { keepalive: true }).then(() => forgetLiveSession(userId, sessionId)).catch(() => {});
        }
      };
      window.addEventListener('pagehide', onPageHide);
      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        window.removeEventListener('pagehide', onPageHide);
      };
    }, []);

    // A hard refresh / crash mid-recording loses liveSessionRef, leaving the
    // session OPEN. The id persisted in
    // sessionStorage on open lets this fresh mount settle that orphan
    // immediately (settlement is idempotent), so the therapist can record again
    // without waiting for the 180s server-side sweep.
    // Sessions left unsettled (offline, tab closed) are remembered in localStorage
    // and settled with their original id here and whenever the browser comes back
    // online. An id is forgotten only after the server confirms it.

    const [pastConsultations, setPastConsultations] = useState([]);
    const [files, setFiles] = useState([]);
    const [reportDocs, setReportDocs] = useState([]);
    const [reportDocFiles, setReportDocFiles] = useState([]);
    
    // Dialog states
    const { setAlert } = useAlert();
    const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
    
    // Audio states
    const [inputSource, setInputSource] = useState(null); 
    const [inputSources, setInputSources] = useState([]);
    const [micStream, setMicStream] = useState(null);
    const [systemStream, setSystemStream] = useState(null);
    const [isTeleHealthActive, setIsTeleHealthActive] = useState(false);
    const meetingStopRef = useRef(null);
    const meetingInterruptedRef = useRef(false);
    meetingStopRef.current = sttFromMic;
    const [audioOption, setAudioOption] = useState('no_save');
    const shouldSaveAudio = () => ['transcribe_now', 'save_only'].includes(audioOption);
    const shouldTranscribeNow = () => audioOption === 'transcribe_now' || audioOption === 'no_save';
    const [selectedMicId, setSelectedMicId] = useState(null);

    //Audio Recording
    const audioChunksRef = useRef([]);
    const audioDiagnosticStops = useRef([]);
    const observeDiagnosticStream = (stream, stage, deviceId) => {
      audioDiagnosticStops.current.push(observeAudioStream(stream, stage, deviceId));
    };
    useEffect(() => () => audioDiagnosticStops.current.forEach(stop => stop()), []);
    const [audioUrl, setAudioUrl] = useState(null);
    // Local recording is available immediately; path is set only after cloud save succeeds.
    const [lastRecording, setLastRecording] = useState(null);
    const [isRecordingAudio, setRecordingAudioState] = useState(false);
    const setIsRecordingAudio = value => {
      recordingAudioRef.current = value;
      setRecordingAudioState(value);
      refreshRecordingBusy();
    };

    
    // Medical coding states
    const [addedCodes, setAddedCodes] = useState([]);
    const [suggestedCodes, setSuggestedCodes] = useState([]);
    const [selectedOption, setSelectedOption] = useState(MEDICAL_OPTIONS[0]);
    
    // Section states
    const [sectionSummary, setSectionSummary] = useState({id: null, content: ""});
    const sectionSummaryIdRef = useRef(sectionSummary.id);

    // Chat state management
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isChatLogoOpen, setIsChatLogoOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [isMessageProcessing, setIsMessageProcessing] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const chatBoxRef = useRef(null);
    
    // Highlighting state management
    const [highlightedTexts, setHighlightedTexts] = useState([]);
    const [highlightCount, setHighlightCount] = useState(0);
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
    const [retrieving, setRetrieving] = useState(false);
    const [highlightMode, setHighlightMode] = useState(false);

    // Auto-scroll to first highlight after new results arrive
    useEffect(() => {
      if (highlightedTexts.length === 0) return;
      const timer = setTimeout(() => {
        const firstEl = document.getElementById('highlight-1');
        if (firstEl) firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return () => clearTimeout(timer);
    }, [highlightedTexts]);

    const [selectedText, setSelectedText] = useState('');
    const transcriptionTextFieldRef = useRef(null);
    const previousSelectionRef = useRef(null);
    const [answerSource, setAnswerSource] = useState('current_report');
    const [isAnswerSourceOpen, setIsAnswerSourceOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [latestConsultationReport, setLatestConsultationReport] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    // Bytes in flight for the recording upload: `{ loaded, total, attempt, attempts }`,
    // or null when nothing is uploading. Drives the bar on the post-recording card.
    const [uploadProgress, setUploadProgress] = useState(null);
    // The recording whose upload failed, kept so "Retry upload" can re-send the same
    // bytes. Cleared on success; while set, the audio exists only in this tab.
    const pendingUploadRef = useRef(null);
    // Aggregate bytes for a file-picker upload (one or many files at once), separate
    // from `uploadProgress`, which belongs to the recording card.
    const [fileUploadProgress, setFileUploadProgress] = useState(null);

    const [subscriptionInfo, setSubscriptionInfo] = useState({
      plan_name: '',
      transcription_seconds_used: 0,
      transcription_seconds_limit: 0,
      limit_reached: false,
      report_generation_limit_reached: false,
      is_active: true,
      audio_transcription_seconds_used: 0,
      audio_transcription_seconds_limit: 0,
      audio_limit_reached: false,
    });

    const [saveReportDialog, setSaveReportDialog] = useState({
      open: false,
      messageContent: '',
      onConfirm: null
    });

    // "Refined transcript not saved" — a generate action was requested while
    // an AI refinement is still pending. Holds the deferred action in both
    // forms so the dialog can run it after saving, or as-is.
    const [refinePendingDialog, setRefinePendingDialog] = useState({
      open: false,
      onSaveAndRun: null,
      onRunOriginal: null
    });
    // "Regenerate report with refined transcript?" — offered right after the
    // user saves a refinement over a consultation that already has a report.
    // Separate from the gate above so it can be dropped on its own.
    const [refineRegenerateDialog, setRefineRegenerateDialog] = useState({ open: false });

    // User from Redux
    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const { settings: languageSettings, getCurrentSettings: getReportLanguageSettings, setPreference: setLanguagePreference, error: languageSettingsError } = useLanguagePreferences(userDetails?.id);
    const { input_language: language, output_language: outputLanguage, spelling_format: spellingFormat } = languageSettings;
    const setLanguage = value => setLanguagePreference('input_language', value);
    useEffect(() => {
      if (languageSettingsError) setAlert(languageSettingsError, 'warning');
    }, [languageSettingsError, setAlert]);
    const recordedScope = JSON.stringify([userDetails?.id, id]);
    // Pending live sessions are stored per user (liveSessionRecovery.js).
    const userIdRef = useRef(null);
    userIdRef.current = userDetails?.id || null;
    // Only sessions that were explicitly stopped are settled here (any tab may
    // finish those); a session still marked recording is left to the server's
    // stale-session recovery, since a sleeping tab looks just like a closed one.
    const liveUserId = userDetails?.id || null;
    useEffect(() => {
      if (!liveUserId) return undefined;
      const reconcile = () => { void reconcilePendingLiveSessions(liveUserId, settleLiveSession, { except: liveSessionRef.current }); };
      reconcile();
      window.addEventListener('online', reconcile);
      return () => window.removeEventListener('online', reconcile);
    }, [liveUserId]);
    const recordedScopeRef = useRef(recordedScope);
    recordedScopeRef.current = recordedScope;
    const selectedOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
    const selectedRole = useSelector((state) => state.globalState.role, shallowEqual);
    const currentOrg = workspace?.currentOrg || selectedOrg;
    const role = workspace ? workspace.role : selectedRole;
    const isAdminUser = role?.toLowerCase() === 'admin';
    const dispatch = useDispatch();
    const hasMounted = useRef(false);
    const liveSessionRef = useRef(null);
    const [timer, setTimer] = useState(0);
    // const userMode = userDetails?.mode || 'Corporates';
    const userMode = 'Mental Health';

    // Initial fetching of history items
    useEffect(() => {
      fetchHistoryItems();
    }, [id, currentOrg.id]);

    // useEffect(() => {
    //   setEditNote(!!sectionSummary.id);
    // }, [sectionSummary.id]);

    useEffect(() => {
      if (textFieldRef.current && !isRefiningTranscript) {
        textFieldRef.current.scrollTop = textFieldRef.current.scrollHeight;
      }
      transcriptionRef.current = transcription;
      remarksRef.current = remarks;
    }, [transcription, remarks, isRefiningTranscript]);

    useEffect(() => {
      if (hasMounted.current) { return; }
      getMicrophones();
      dispatch(setDrawerOpen(false));
      fetchSubscriptionInfo();
      hasMounted.current = true;
    }, [dispatch]);


    // Fetch chat messages when chat is opened
    useEffect(() => {
      if ((isChatOpen || isChatLogoOpen) && !isMessageProcessing) {
        fetchChatMessages();
      }
    }, [isChatOpen, isChatLogoOpen]);


    useEffect(() => {
      // Handler for receiving messages from the preview window
      const handleWindowMessage = async (event) => {
        if (event.data.type === 'export-to-word') {
          try {
            const { title, header, content, footer } = event.data.data;
            
            // Combine all sections for the document with proper HTML structure and styles
            const fullHtml = `
              <!DOCTYPE html>
              <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body {
                    font-family: Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                  }
                  .pdf-header {
                    width: 100%;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                  }
                  .pdf-content {
                    margin: 20px 0;
                  }
                  .pdf-footer {
                    width: 100%;
                    margin-top: 20px;
                    padding-top: 10px;
                  }
                  /* General tables in body content must have borders */
                  .pdf-content table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 16px 0;
                  }
                  .pdf-content td, .pdf-content th {
                    border: 1px solid #D1D5DB;
                    padding: 8px 12px;
                    text-align: left;
                  }
                  .pdf-content th {
                    background-color: #F9FAFB;
                    font-weight: 600;
                  }
                  /* Layout tables in header/footer must NOT have borders */
                  .pdf-header table, .pdf-footer table {
                    border-collapse: collapse;
                    border: none !important;
                  }
                  .pdf-header td, .pdf-header th, .pdf-footer td, .pdf-footer th {
                    border: none !important;
                  }
                </style>
              </head>
              <body>
                <div class="pdf-header">${header || ''}</div>
                <div class="pdf-content">${content || ''}</div>
                <div class="pdf-footer">${footer || ''}</div>
              </body>
              </html>
            `;
            
            // Clean filename - remove special characters
            const cleanFileName = title.replace(/[/\\?%*:|"<>]/g, '-');
            
            // Generate the Word document as a blob (much simpler with html-docx-js)
            const docBlob = htmlDocx.asBlob(fullHtml);
            
            // Use FileSaver to trigger the download
            FileSaver.saveAs(docBlob, `${cleanFileName}.docx`);
            
            setAlert('Word document created successfully!', 'success');
          } catch (error) {
            console.error('Error exporting to Word:', error);
            setAlert('Failed to create Word document', 'error');
          }
        } else if (event.data.type === 'export-to-excel') {
          try {
            const { title, content } = event.data.data;
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, 'text/html');
            const tables = doc.querySelectorAll('table');
            if (tables && tables.length > 0) {
              const workbook = xlsx.utils.book_new();
              tables.forEach((table, index) => {
                const worksheet = xlsx.utils.table_to_sheet(table);
                // 1. Get the caption element or preceding paragraph/heading text
                const captionNode = table.querySelector('caption');
                let rawSheetName = '';
                
                if (captionNode) {
                  rawSheetName = captionNode.textContent.trim();
                } else {
                  // Fallback: look back at preceding siblings (like <p> or headings) to find a title/caption
                  let sibling = table.previousElementSibling;
                  while (sibling) {
                    if (sibling.tagName === 'TABLE' || sibling.tagName === 'HR') {
                      break; // Stop looking back if we encounter another table or divider
                    }
                    const text = sibling.textContent.trim();
                    if (text) {
                      rawSheetName = text;
                      break;
                    }
                    sibling = sibling.previousElementSibling;
                  }
                }
                
                // 2. Fallback to 'Sheet X' if no title or caption was found
                if (!rawSheetName) {
                  rawSheetName = `Sheet ${index + 1}`;
                }
                
                // 3. Sanitize for Excel: Remove forbidden characters and limit to 31 chars
                let safeSheetName = rawSheetName
                  .replace(/[\\/?*[\]:]/g, '-') // Replace invalid characters with a hyphen
                  .substring(0, 31)             // Excel hard limit is 31 characters
                  .trim();
                  
                // 4. Ensure the name isn't empty after sanitization
                if (!safeSheetName) {
                  safeSheetName = `Sheet ${index + 1}`;
                }

                // Append sheet with the safe name
                xlsx.utils.book_append_sheet(workbook, worksheet, safeSheetName);
              });
              const cleanFileName = title.replace(/[/\\?%*:|"<>]/g, '-');
              xlsx.writeFile(workbook, `${cleanFileName}.xlsx`);
              setAlert('Excel document created successfully!', 'success');
            } else {
              setAlert('No table found in the content to export to Excel', 'error');
            }
          } catch (error) {
            console.error('Error exporting to Excel:', error);
            setAlert('Failed to create Excel document', 'error');
          }
        }
      };
      
      // Add event listener for messages
      window.addEventListener('message', handleWindowMessage);
      
      // Clean up the event listener
      return () => {
        window.removeEventListener('message', handleWindowMessage);
      };
    }, []);

    useEffect(() => {
      if(!firstView) {
        if (mainView === 'Consultation') {
          setAnswerSource('current_report');
        } else if (mainView === 'History') {
          setAnswerSource('all_records');
        }
      }
    }, [mainView, firstView]);

    useEffect(() => {
      if (!isRecognizing) {
        setTimer(0);
        return;
      }
      // Keep the final recorded duration visible while stopping/uploading.
      // The recorder is no longer capturing during this phase.
      if (isSettling) return;
      let seconds = 0;
      const interval = setInterval(() => {
        seconds += 1;
        const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
        const ss = String(seconds % 60).padStart(2, '0');
        setTimer(`${mm}:${ss}`);
      }, 1000);
      return () => clearInterval(interval);
    }, [isRecognizing, isSettling]);

    const fetchSubscriptionInfo = async () => {
      try {
        if (!userDetails || !userDetails.id) return;
        
        const { data, error } = await supabase
          .from('subscription_info')
          .select('plan_name, transcription_seconds_used, transcription_seconds_limit, report_generation_limit_reached, is_active, audio_transcription_seconds_used, audio_transcription_seconds_limit')
          .eq('user_id', userDetails.id)
          .single();
          
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching subscription info:', error);
          return;
        }
        if(!data) {
          setSubscriptionInfo({
            plan_name: 'free',
            transcription_seconds_used: 0,
            transcription_seconds_limit: 0,
            limit_reached: true,
            report_generation_limit_reached: true,
            is_active: false,
            audio_transcription_seconds_used: 0,
            audio_transcription_seconds_limit: 0,
            audio_limit_reached: true,
          });
          return;
        }
        if (data) {
          const isFreePlan = data.plan_name === 'free';
          const audioLimitReached = isFreePlan && 
            data.audio_transcription_seconds_limit > 0 && 
            data.audio_transcription_seconds_used >= data.audio_transcription_seconds_limit;
          
          setSubscriptionInfo({
            plan_name: data.plan_name || '',
            transcription_seconds_used: data.transcription_seconds_used || 0,
            transcription_seconds_limit: data.transcription_seconds_limit || 0,
            limit_reached: (data.transcription_seconds_limit > 0 && 
                            data.transcription_seconds_used >= data.transcription_seconds_limit),
            report_generation_limit_reached: data.report_generation_limit_reached,
            is_active: data.is_active,
            audio_transcription_seconds_used: data.audio_transcription_seconds_used || 0,
            audio_transcription_seconds_limit: data.audio_transcription_seconds_limit || 0,
            audio_limit_reached: audioLimitReached,
          });
        }
      } catch (error) {
        console.error('Error in fetchSubscriptionInfo:', error);
      }
    };

    const fetchSectionSummary = async (consultationId, latestReport) => {
      try {
        // const { data: sectionSummaryData, error: sectionSummaryError } = await supabase.from('generatedreports').select('*').eq('patientId', id).eq('consultationId', consultationId).eq('title', defaultTemplateTitle).maybeSingle();
        // if (sectionSummaryError) {
        //   console.error("Error fetching section summary data:", sectionSummaryError.message);
        //   return;
        // }
        // const { data: decryptedSectionSummary, error: decryptionError } = await supabase.rpc('decrypt_text', { encrypted_data: sectionSummaryData.content });
        // if(decryptionError) {
        //   console.error("Error decrypting section summary data:", decryptionError.message);
        //   return;
        // }
        if(latestReport && latestReport.originalData.content) {
          setSectionSummary({
              id: latestReport.id,
              content: latestReport.originalData.content,
          });
          setEditNote(true);
        } else {
          sectionSummaryIdRef.current = null;
          setSectionSummary({id: null, content: ""});
          setEditNote(false);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
      }
    };

    const fetchConsultationData = async () => {
      let { data, error } = await supabase.from('consultations').select('*').eq('patient_id', id);
      if (error){
        console.error(error);
          return;
      }
      const decryptionPromises = data.map(async (consultation) => {
        const [
          notesResult,
          transcriptionResult,
          chiefComplaintResult,
          createdByResult,
        ] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: consultation.notes }),
          supabase.rpc('decrypt_text', { encrypted_data: consultation.transcription }),
          supabase.rpc('decrypt_text', { encrypted_data: consultation.chiefComplaint }),
          supabase.rpc('decrypt_text', { encrypted_data: consultation.created_by })
        ]);

        if (notesResult.error || transcriptionResult.error || chiefComplaintResult.error || createdByResult.error) {
          return consultation;
        }

        consultation.notes = notesResult.data;
        consultation.transcription = transcriptionResult.data;
        consultation.chiefComplaint = chiefComplaintResult.data;
        consultation.created_by = createdByResult.data;

        return consultation;
      });
      const decryptedConsultations = await Promise.all(decryptionPromises);

      const sortedData = decryptedConsultations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setPastConsultations(sortedData);
      
      return sortedData;
    };

    const fetchAddedCodes = async (id) => {
      try {
        const { data, error } = await supabase
          .from('consultations')
          .select('codes')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching added codes:', error);
          return;
        }
        if(data.codes === null){
          setAddedCodes([]);
        } else {
          const formattedCodes = data.codes.map((codeString) => {
            const [code, description, type] = codeString.split('-');
            return {
              value: code,
              label: `${code}\n${description}`,
              description,
              type,
            };
          });
          setAddedCodes(formattedCodes);
        }
      } catch (error) {
        console.error('Error during data fetch:', error);
      }
    };

    const fetchSuggestedCodes = async (id) => {
      try {
        const { data, error } = await supabase
          .from('consultations')
          .select('suggestedCodes')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching suggested codes:', error);
          return;
        }
        if(!data.suggestedCodes){
          setSuggestedCodes([]);
        } else {
          const formattedCodes = data.suggestedCodes.map((codeString) => {
            const [code, description, type] = codeString.split('-');
            return {
              value: code,
              label: `${code}\n${description}`,
              description,
              type,
            };
          });
          setSuggestedCodes(formattedCodes);
        }
      } catch (error) {
        console.error('Error during data fetch:', error);
      }
    }

    const saveSuggestedCodes = async (summary) => {
      // Gated by CW_UNMETERED_PROVIDERS_ENABLED — protects the medical-icd-codes
      // Cloud Function (CW-006 owner-ask: sources unlocated, owner not yet
      // asked). While the flag is false this returns before any network call;
      // the free NIH ICD search UI is unaffected.
      if (!CW_UNMETERED_PROVIDERS_ENABLED) return;
      setCodeLoading(true);
      try {
          const response = await TikcareAssistantService.post('/medical-icd-codes', { summary, icd_type: selectedOption.value });
          const suggestedCodes = JSON.parse(response.data.body);
          const formattedSuggestedCodes = suggestedCodes.map((code) => ({
              value: code.code,
              label: `${code.code}\n${code.description}`,
              description: code.description,
              type: selectedOption.value === 'ICD-10' ? 'ICD10' : 'ICD11',
          }));

          if (currentConsultation && currentConsultation.id) {
            const codesToSave = formattedSuggestedCodes.map((c) => `${c.value}-${c.description.replace('-', ' ')}-${c.type}`);
            const { error } = await supabase.from('consultations').update({ suggestedCodes: codesToSave }).eq('id', currentConsultation.id);
            if (error) {
              console.error('Error saving suggested codes:', error);
            }
          }
          setSuggestedCodes(formattedSuggestedCodes);
      } catch (error) {
          console.error('Error in retreiving suggested codes:', error);
      } finally {
          setCodeLoading(false);
      }
    };

    const handleMainViewChange = (value) => {
        setSelectedText('');
        setMainView(value);
        setLeftView('Transcription');
        setRightView(rightOptions[0].value);

        if (value === 'Consultation') {
            setAnswerSource('current_report');
        } else if (value === 'History') {
            setAnswerSource('all_records');
        }
    }
  
    const handleLeftViewChange = (value) => {
      setLeftView(value);
    };

    const handleRightViewChange = (value) => {
      setEditNote(false);
      setRightView(value);
    };

    const handleCopy = (text) => {
      navigator.clipboard.writeText(text).then(() => {
        track(EVENTS.NOTE_COPIED);
        if(isPopupReport) {
          setAlert('Report content copied to clipboard!', 'success');
        } else {
          setAlert('Consultation notes copied to clipboard!', 'success');
        }
      }).catch((err) => {
        console.error('Failed to copy: ', err);
      });
    };

    const handleSaveTranscription = useCallback(async (consultationId = null, { silent = false } = {}) => {
      const targetId = consultationId || currentConsultation?.id;
      if (!targetId) return false;
      const { error } = await supabase
        .from('consultations')
        .update({ transcription: transcriptionRef.current })
        .eq('id', targetId);

      if (error) {
        setAlert('Failed to save transcription!', 'error');
        return false;
      }

      if (!silent) {
        setAlert('Transcription saved successfully!', 'success');
      }

      setCurrentConsultation(prev =>
        prev && prev.id === targetId ? { ...prev, transcription: transcriptionRef.current } : prev
      );
      setPastConsultations(prev =>
        prev.map(consultation =>
          consultation.id === targetId ? { ...consultation, transcription: transcriptionRef.current } : consultation
        )
      );
      return true;
    }, [currentConsultation?.id, setAlert]);

    const handleSaveRemarks = useCallback(async (consultationId = null, { silent = false } = {}) => {
      const targetId = consultationId || currentConsultation?.id;
      if (!targetId) return;
      const { error } = await supabase
        .from('consultations')
        .update({ notes: remarksRef.current })
        .eq('id', targetId);

      if (error) {
        setAlert('Failed to save remarks!', 'error');
        return;
      }

      if (!silent) {
        setAlert('Manual notes saved successfully!', 'success');
      }

      setCurrentConsultation(prev =>
        prev && prev.id === targetId ? { ...prev, notes: remarksRef.current } : prev
      );
      setPastConsultations(prev =>
        prev.map(consultation =>
          consultation.id === targetId ? { ...consultation, notes: remarksRef.current } : consultation
        )
      );
    }, [currentConsultation?.id, setAlert]);

    // Index the consultation's transcript + clinician notes into Ask Sophie's
    // document store (one document per consultation; the backend dedups
    // unchanged content and supersedes changed content). Called at settle
    // points — Stop/Regenerate (saveConsultation), Generate, audio file
    // transcription, Accept refinement, Save Changes — and by the idle/leave
    // effects below; never from the 600 ms autosave. Reads the refs, i.e. the
    // latest typed text whether or not the autosave has landed yet, so the
    // fingerprint makes a repeat call with unchanged content free.
    // A blank transcript + blank notes is a delete on the server, so the
    // idle/leave callers pass `requireContent` — a reset or a delete clears
    // the refs before the last flush for that consultation runs.
    const indexConsultationTranscript = (consultation = currentConsultation, { requireContent = false, sourceText } = {}) => {
      if (!TRANSCRIPT_INDEX_ENABLED || !consultation?.id) return;
      if (deletedConsultationIdsRef.current.has(consultation.id)) return;
      const transcript = sourceText?.transcription ?? transcriptionRef.current ?? '';
      const notes = sourceText?.notes ?? remarksRef.current ?? '';
      if (requireContent && !hasTranscriptContent(transcript, notes)) return;
      const fingerprint = transcriptFingerprint(consultation.id, transcript, notes);
      if (lastIndexedTranscriptRef.current === fingerprint) return;
      lastIndexedTranscriptRef.current = fingerprint;

      const formDataTranscript = buildTranscriptIndexForm({
        patientId: id, teamId: currentOrg.id, consultation, transcript, notes,
      });
      // Fire-and-forget, like the report add_embedding calls; the axios
      // interceptor (utils/ragAxiosAuth.js) attaches the JWT.
      axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataTranscript, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*',
        },
      }).then(response => {
        if (response.data?.status === 'failed') {
          setAlert('Transcript saved, but could not be added to Ask Sophie', 'warning');
        }
      }).catch(error => {
        // Let the next settle point or idle tick retry. Log the error only —
        // never the transcript.
        lastIndexedTranscriptRef.current = null;
        console.error('Background transcript indexing failed:', error);
      });
    };

    // One debounce and one serial queue for transcript and notes.
    useEffect(() => {
      if (isRefiningTranscript || isPendingRefinement) return;
      if (!currentConsultation?.id && !transcription?.trim() && (!remarks?.trim() || remarks === '<p></p>')) return;
      setTranscriptSaveStatus('saving');
      const timeout = setTimeout(() => { saveTextOnly({ index: false }); }, 600);
      return () => clearTimeout(timeout);
    }, [transcription, remarks, currentConsultation?.id, isRefiningTranscript, isPendingRefinement]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the latest indexing helper reachable from the timer and cleanups
    // below without re-running those effects on every render.
    useEffect(() => {
      indexTranscriptRef.current = indexConsultationTranscript;
    });

    // Idle re-index for typed edits: 30 s after the last change to the
    // transcript or notes. Waits out a live recording (Stop indexes through
    // saveConsultation) so a pause mid-session does not embed a partial
    // transcript.
    useEffect(() => {
      if (!currentConsultation?.id) return undefined;
      const fire = () => {
        if (recognizingRef.current) {
          transcriptIndexTimer.current = setTimeout(fire, TRANSCRIPT_IDLE_MS);
          return;
        }
        indexTranscriptRef.current(undefined, { requireContent: true });
      };
      transcriptIndexTimer.current = setTimeout(fire, TRANSCRIPT_IDLE_MS);
      return () => clearTimeout(transcriptIndexTimer.current);
    }, [transcription, remarks, currentConsultation?.id]);

    // Flush when the open consultation changes or the page unmounts, so edits
    // from the last 30 s are not lost. At cleanup time the refs still hold
    // THAT consultation's text (the ref sync effect above runs after
    // cleanups), which is why the id and date are captured here rather than
    // read from state. Right after a settle point this is a fingerprint no-op.
    const openConsultationId = currentConsultation?.id;
    const openConsultationCreatedAt = currentConsultation?.createdAt;
    useEffect(() => {
      if (!openConsultationId) return undefined;
      const consultation = { id: openConsultationId, createdAt: openConsultationCreatedAt };
      return () => indexTranscriptRef.current(consultation, { requireContent: true });
    }, [openConsultationId, openConsultationCreatedAt]);

    const medicalScribeGetChiefComplaint = async (notes) => {
      try {
        const response = await TikcareAssistantService.post('/medical-scribe-get-chief-complaint', { notes });
        return response.data.body;
      } catch (error) {
        console.error('Error in medicalScribeSummarizeAllConsultation:', error);
        return 'Not Available';
      }
    };

    const throttledFunction = (func, delay) => {
      if (throttleTimeout.current) {
        return;
      }
      throttleTimeout.current = setTimeout(() => {
        func();
        throttleTimeout.current = null;
      }, delay);
    };

    const checkIfTemplateAvailable = async (reportID) => {
      if(reportID) {
        const report = historyItems.find(item => item.id === reportID);
        if (report && report.originalData && report.originalData.templateId) {
          const templateId = report.originalData.templateId;
          const { data, error } = await supabase.from('templates').select('*').eq('id', templateId).maybeSingle();
          if (error) {
            console.error('Error fetching template:', error);
            return defaultTemplateContent;
          }
          if (data) {
            const decryptedContent = await supabase.rpc('decrypt_text', { encrypted_data: data.content });
            if (decryptedContent.error) {
              console.error('Error decrypting template content:', decryptedContent.error);
              return defaultTemplateContent;
            }
            data.content = decryptedContent.data;
            setSelectedTemplate(data);
            // const cleanTemplateContent = extractParagraphText(decryptedContent.data);
            const cleanTemplateContent = decryptedContent.data
            return cleanTemplateContent;
          }
        }
      }
      // console.log("No template found, using default template content.");
      // console.log("the default template is", defaultTemplate);
      // console.log("the default template content is", defaultTemplateContent);
      return defaultTemplateContent;
    }

    const medicalScribeSummarizeInner = async (templateOverride = null) => {
        // Check if limit is reached before proceeding
        if (subscriptionInfo.limit_reached) {
          setAlert('Transcription limit reached. Please upgrade your subscription to continue.', 'error');
          setIsRecognizing(false);
          if (recognizer) {
            recognizer.stopTranscribingAsync(() => {
              recognizer.close();
              if (micStream) {
                micStream.getTracks().forEach(track => track.stop());
              }
              if (isTeleHealthActive && systemStream) {
                systemStream.getTracks().forEach(track => track.stop());
              }
            });
          }
          return "";
        }

        const notes = `${transcriptionRef.current ?? ""}\n\n${remarksRef.current ?? ""}`;
        if (!hasTranscriptContent(transcriptionRef.current, remarksRef.current)) {
          setAlert('Add transcription or notes before generating a report.', 'info');
          return '';
        }
        const templateContent = templateOverride ? templateOverride.content
          : selectedTemplate ? selectedTemplate.content : defaultTemplateContent;
        try {
          validateReportInput(templateContent, notes);
        } catch (error) {
          setAlert(error.message, 'info');
          return '';
        }
        
        //check if template id is present for the report
        //if present, use that template id to fetch the template content
        //else just use the default template content

        // console.log("the default template is ", defaultTemplate);
        // console.log("the default template is content is ", defaultTemplateContent);
        // console.log('the template content is ', templateContent);
        try {
          const formData = new FormData();
          formData.append('notes', notes);
          formData.append('template', templateContent || '');
          formData.append('output_language', outputLanguage);
          formData.append('spelling_format', spellingFormat);
          const response = await postGenerateReport(formData, 'generate', setPiiWarming, getReportLanguageSettings);
          const formattedSummaryHTML = requireReportContent(response.data.summary);
          // const formattedSummaryHTML = formatAIResponseToHTML(formattedSummary, defaultTemplate.content)
          setSectionSummary(prev => ({
            ...prev,
            content: formattedSummaryHTML ?? "",
          }));

          if (currentConsultation && currentConsultation.id) {
            await supabase.from('consultations').update({
              notes: remarksRef.current,
              transcription: transcriptionRef.current
            }).eq('id', currentConsultation.id);
          }

          // Use the ref for the latest ID instead of stale state
          // console.log("the sectionSummaryIdRef.current is ", sectionSummaryIdRef.current);
          if (sectionSummaryIdRef.current) {
            await supabase.from('generatedreports').update({
              content: formattedSummaryHTML ?? "",
              updated_at: new Date().toISOString(),
            }).eq('id', sectionSummaryIdRef.current);
          }

          return formattedSummaryHTML;
        } catch (error) {
          console.error('Error in medicalScribeSummarize:', error);
          // Tell the user why the report is blank — this path used to fail
          // silently (console only), which read as "generate did nothing".
          setAlert(error.message || 'Report generation failed. Please try again.', 'error');
          // Reset using functional updater
          setSectionSummary(prev => ({
            ...prev,
            id: null,
            content: "",
          }));
          return "";
        }
      };

    // Every run of medicalScribeSummarizeInner debits 1 credit at the metered
    // backend, so concurrent callers must share a single in-flight request
    // instead of each paying for their own.
    const medicalScribeSummarize = (templateOverride = null) => {
      if (summarizeInFlightRef.current) return summarizeInFlightRef.current;
      const run = medicalScribeSummarizeInner(templateOverride).finally(() => {
        summarizeInFlightRef.current = null;
      });
      summarizeInFlightRef.current = run;
      return run;
    };

    // Save and generate functions
    const saveConsultation = async ({ background = false, templateOverride = null } = {}) => {
      const work = async () => {
        try {
          // The Generate button can be pressed before any consultation record
          // exists (e.g. transcript pasted in after a reload) — create one first.
          let consultation = currentConsultation;
          if (!consultation?.id) {
            consultation = await insertConsultation();
            if (!consultation?.id) return;
          }
          const updatedSummary = await medicalScribeSummarize(templateOverride);
          const chiefComplaint = consultation?.chiefComplaint || `Consultation - ${convertToGMT8Date(
            consultation?.createdAt || new Date()
          )}`;

          const { error } = await supabase.from('consultations').update({
            chiefComplaint: chiefComplaint !== "null" ? chiefComplaint : "Not Available",
            notes: remarksRef.current,
            transcription: transcriptionRef.current
          }).eq('id', consultation.id);

          if (!error) {
            // Stop and Regenerate both land here: the transcript is settled.
            indexConsultationTranscript(consultation);
            if (updatedSummary) await saveReport(updatedSummary, consultation, templateOverride);
            await fetchHistoryItems({ background: background });
            setCurrentConsultation(currentConsultation => ({
              ...(currentConsultation ?? consultation),
              transcription: transcriptionRef.current ?? "",
              notes: remarksRef.current ?? "",
              chiefComplaint: chiefComplaint !== "null" ? chiefComplaint : "Not Available"
            }));
            handleLeftViewChange('Transcription');
          } else {
            console.error(error);
          }
        } catch (err) {
          console.error('Error saving consultation:', err);
        } finally {
          // Ensure spinner turns off even if background task errors
          if (!background) {
            setSavingConsultation(false);
          }
        }
      };

      if (background) {
        work();
        setSavingConsultation(false);
      } else {
        await work();
      }
    };

    // Lightweight save: creates the consultation record if it doesn't exist yet,
    // then persists only the raw transcription + notes text (no AI summarization).
    // Used when the user types into the transcription or manual notes field.
    // quiet: no error alert (after a lost connection the panel already says it is
    // waiting to save, and the save is retried automatically).
    // Every save, autosave included, has a deadline counted from when it was
    // REQUESTED, so time waiting behind an earlier save counts too: a hanging
    // request is aborted at its deadline and frees the queue, and a save whose
    // deadline passed while queued gives up without writing (callers retry).
    // After a timeout the database may still apply that aborted, older write
    // later, so the next successful save re-saves the latest text once more a
    // little later; the final stored transcript is always the newest one.
    const saveTextOnly = ({ index = true, quiet = false, timeoutMs = TRANSCRIPT_SAVE_TIMEOUT_MS } = {}) => {
      const snapshot = { transcription: transcriptionRef.current, notes: remarksRef.current };
      const target = currentConsultation;
      const deadline = Date.now() + timeoutMs;
      setTranscriptSaveStatus('saving');
      const operation = saveQueue.current.then(async () => {
        let timedOut = false;
        try {
          if (Date.now() >= deadline) { timedOut = true; throw new Error('Transcript save timed out while queued'); }
          const consultation = target || await insertConsultation();
          if (!consultation) throw new Error('Could not create consultation');
          let query = supabase.from('consultations').update(snapshot).eq('id', consultation.id);
          const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
          if (controller && typeof query.abortSignal === 'function') query = query.abortSignal(controller.signal);
          let timer;
          let result;
          try {
            result = await Promise.race([query, new Promise((_, reject) => {
              timer = setTimeout(() => {
                timedOut = true;
                controller?.abort();
                reject(new Error('Transcript save timed out'));
              }, Math.max(0, deadline - Date.now()));
            })]);
          } finally { clearTimeout(timer); }
          const { error, status } = result;
          if (error) throw Object.assign(error, { status });
          if (uncertainSaveRef.current === consultation.id) {
            uncertainSaveRef.current = null;
            const scheduledUser = userIdRef.current;
            cancelStaleResave();
            staleResaveTimerRef.current = setTimeout(() => {
              staleResaveTimerRef.current = null;
              // Only for the workspace that scheduled it: still mounted, same
              // consultation open, same user. Never another page's text.
              if (!recordingPageMountedRef.current) return;
              if (activeConsultationRef.current !== consultation.id || userIdRef.current !== scheduledUser) return;
              void saveTextOnlyRef.current({ index: false, quiet: true });
            }, STALE_WRITE_RESAVE_MS);
          }
          setHistoryItems(prev => {
            if (prev.some(item => item.id === consultation.id)) {
              return prev.map(item => item.id === consultation.id
                ? { ...item, originalData: { ...item.originalData, ...snapshot } } : item);
            }
            // Write can open immediately after saving, before the library has
            // fetched this consultation. Keep the saved source available.
            return [{
              id: consultation.id,
              createdAt: consultation.createdAt,
              sessionDate: consultation.session_date,
              title: consultation.chiefComplaint || 'Consultation',
              type: 'Consultation',
              originalData: { ...consultation, ...snapshot },
              createdBy: consultation.created_by || userDetails?.name || 'Unknown User',
              createdByUserId: consultation.created_by_user_id || null,
              isSelected: false,
              parentId: null,
              isSubItem: false,
              isShared: consultation.isShared || false,
              share_expires_at: consultation.share_expires_at,
            }, ...prev];
          });
          setCurrentConsultation(prev => prev?.id === consultation.id ? { ...prev, ...snapshot } : prev);
          if (index) indexConsultationTranscript(consultation, { sourceText: snapshot });
          if (transcriptionRef.current === snapshot.transcription && remarksRef.current === snapshot.notes) setTranscriptSaveStatus('saved');
          return true;
        } catch (error) {
          if (timedOut && target?.id) uncertainSaveRef.current = target.id;
          logTranscriptSaveFailure(error, {
            where: 'save_text_only',
            transcriptChars: snapshot.transcription?.length ?? 0,
            notesChars: snapshot.notes?.length ?? 0,
          });
          setTranscriptSaveStatus('error');
          if (!quiet) setAlert('Could not save your transcript. Your text is still here. Please retry before leaving.', 'error');
          return false;
        }
      });
      saveQueue.current = operation;
      return operation;
    };
    const saveTextOnlyRef = useRef(saveTextOnly);
    saveTextOnlyRef.current = saveTextOnly;
    // Opening another consultation or signing in as someone else cancels a
    // pending re-save and forgets the timed-out consultation.
    useEffect(() => () => { cancelStaleResave(); uncertainSaveRef.current = null; },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentConsultation?.id, userDetails?.id]);

    const saveSelectedConsultationSource = async (item, changes) => {
      const updated = await saveConsultationSource(supabase, id, item, changes);
      setHistoryItems(items => items.map(entry => entry.id === item.id ? { ...entry, originalData: updated } : entry));
      setPastConsultations(items => items.map(entry => entry.id === item.id ? { ...entry, ...updated } : entry));
      if (currentConsultation?.id === item.id) {
        if ('transcription' in changes) { transcriptionRef.current = updated.transcription; setTranscription(updated.transcription); }
        if ('notes' in changes) { remarksRef.current = updated.notes; setRemarks(updated.notes); }
      }
      indexConsultationTranscript(updated, { sourceText: updated });
      return updated;
    };

    const regenerateReportConsultation = async (templateOverride = null) => {
      if (isGeneratingReport) return;
      setIsGeneratingReport(true);
      setLoading(true);
      try {
        // Always insert a fresh report — never overwrite a previously generated one.
        sectionSummaryIdRef.current = null;
        await saveConsultation({ templateOverride });
      } finally {
        setLoading(false);
        setIsGeneratingReport(false);
      }
    }

    // Report endpoints authorize and debit wallet credits. Legacy monthly Free
    // counters are retained for compatibility, not used to authorize spending.
    const regenerateHistoryReport = async () => {
      if(!subscriptionInfo.is_active) {
        setAlert('Your subscription is not active. Please activate your subscription to generate reports.', 'error');
        return;
      }

      setIsGeneratingReport(true);
      setConsultationLoading(true);

      try {
        // Prepare data for API call
        let notes = "";

        reportDocs.forEach(item => {
          if (!hasReportInputText(item.content)) return;
          if (item.type === 'Consultation') {
            const currentConsultation = `
            Consultation: ${item.name}:\n\n
            ${item.content}\n\n
            `
            notes += currentConsultation;
          } else if (item.type === 'Document') {
            const currentDocument = `
            Document: ${item.name}\n\n
            ${item.content}\n\n
            `
            notes += currentDocument;
          }
        });
        const templateContent = selectedTemplate ? selectedTemplate.content : defaultTemplateContent;

        const formData = new FormData();
        const manifest = reportSourceManifest(reportDocs, selectedReport?.documents || []);
        if (manifest.length) formData.append('source_manifest', JSON.stringify(manifest));
        const { refs, skipped, text: sourceText } = await prepareReportSourceAttachments(
          reportDocs.filter((docItem) => docItem.type === 'Attachment'), retrieveAttachmentFile, { preparedWord: true });
        notes += `\n\n${sourceText}`;
        if (skipped.length) {
          setAlert(skippedAttachmentMessage(skipped), 'warning');
        }
        refs.forEach((ref) => formData.append('file_references', ref));
        formData.append('notes', notes);
        formData.append('template', templateContent || '');
        formData.append('output_language', outputLanguage);
        formData.append('spelling_format', spellingFormat);

        const response = await postGenerateReport(formData, 'regenerate', setPiiWarming, getReportLanguageSettings);
        const formattedSummaryHTML = requireReportContent(response.data.summary);

        // Always INSERT a new report — never overwrite the existing one
        const reportId = uuidv4();
        const reportDate = new Date().toISOString();
        const reportTitle = `${(selectedTemplate || defaultTemplate).title} - ${selectedReportDateLabel()}`;
        const consultationId = selectedReport?.originalData?.consultationId || null;

        const { error } = await supabase.from('generatedreports').insert([{
          id: reportId,
          patientId: id,
          consultationId: consultationId,
          title: reportTitle,
          content: formattedSummaryHTML,
          locked: false,
          created_by: userDetails.name,
          created_by_user_id: userDetails?.id || null,
          documents: selectedReport?.documents || [],
          template_id: (selectedTemplate || defaultTemplate).id,
        }]);

        if (error) {
          console.error('Error saving report:', error);
          setAlert('Failed to save report', 'error');
          return;
        }

        // Fire-and-forget: add RAG embedding for the new report
        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', formattedSummaryHTML);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', reportTitle);

        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).catch(err => {
          console.error('Background error adding embedding:', err);
        });

        // Build the in-memory item matching historyItems shape
        const newReportItem = {
          id: reportId,
          createdAt: reportDate,
          title: reportTitle,
          type: 'Document',
          originalData: {
            content: formattedSummaryHTML,
            consultationId: consultationId,
            templateId: (selectedTemplate || defaultTemplate).id,
          },
          createdBy: userDetails?.name || 'Unknown User',
          createdByUserId: userDetails?.id || null,
          isSelected: false,
          parentId: consultationId,
          isSubItem: !!consultationId,
          isLocked: false,
          isShared: false,
          documents: selectedReport?.documents || [],
        };

        // Open the newly generated report
        setSelectedReport(newReportItem);
        await setReportDocuments(newReportItem);

        // Update in-memory list and refresh sidebar
        setHistoryItems(prev => [newReportItem, ...prev]);
        fetchHistoryItems({ background: true });

        handleMenuClose();
        clearAllSelections();
        clearHighlights();
        setAlert('Report generated successfully!', 'success');
      } catch (error) {
        console.error('Error generating report:', error);
        // Surface the real reason (empty transcript, out of credits, under
        // review) instead of a dead-end generic message.
        setAlert(error?.message ? `Failed to generate report: ${error.message}` : 'Failed to generate report', 'error');
      } finally {
        setIsGeneratingReport(false);
        setConsultationLoading(false);
        setReportTemplateSelected(false);
      }
    }
    
    const saveReport = async (updatedSummary, overrideConsultation, templateOverride = null) => {
      requireReportContent(updatedSummary);
      let reportId;
      let reportTitle;
      if(sectionSummaryIdRef.current) {
        reportId = sectionSummaryIdRef.current;
        const { data, error } = await supabase.from('generatedreports').update({
          content: updatedSummary,
          updated_at: new Date().toISOString(),
        }).eq('id', sectionSummaryIdRef.current).select('*');

        if (error) {
          console.error('Error updating report:', error);
          return;
        }

        const decryptedTitle = await supabase.rpc('decrypt_text', { encrypted_data: data[0].title });
        if (decryptedTitle.error) {
          console.error('Error decrypting report title:', decryptedTitle.error);
          return;
        }
        reportTitle = decryptedTitle.data;

        // Request the new version without removing the current searchable version.
        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', updatedSummary);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', reportTitle);

        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).then(response => {
          if(response.data.status === 'failed') {
            setAlert('Failed to save report embedding!', 'error');
          }
        }).catch(error => {
          console.error('Background embedding operation failed:', error);
        });
      } 
      else {
        const effectiveConsultation = overrideConsultation || currentConsultation;
        reportId = uuidv4();
        reportTitle = `${(templateOverride || selectedTemplate || defaultTemplate).title} - ${selectedReportDateLabel()}`;
        const sourceDocuments = [];
        if (effectiveConsultation && effectiveConsultation.id) {
          sourceDocuments.push(effectiveConsultation.id);
        }
        if (files && files.length > 0) {
          files.forEach(file => {
            sourceDocuments.push(`file-${effectiveConsultation.id}-${file.name}`);
          });
        }
        const { error } = await supabase.from('generatedreports').insert([{
          id: reportId,
          patientId: id,
          consultationId: effectiveConsultation.id,
          title: reportTitle,
          content: updatedSummary,
          locked: false,
          created_by: userDetails.name,
          created_by_user_id: userDetails?.id || null,
          documents: sourceDocuments,
          template_id: (templateOverride || selectedTemplate || defaultTemplate).id,
        }]).select('*');
        if (error) {
          console.error('Error inserting report:', error);
          return;
        }

        // For new reports, just add the embedding (no delete needed)
        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', updatedSummary);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', reportTitle);

        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).then(response => {
          if(response.data.status === 'failed') {
            setAlert('Failed to save report embedding!', 'error');
          }
        }).catch(error => {
          console.error('Background add_embedding failed:', error);
        });
      }

      setSectionSummary({
        id: reportId,
        content: updatedSummary,
      });
      sectionSummaryIdRef.current = reportId;
      setLatestConsultationReport({
        id: reportId,
        title: reportTitle,
        type: 'Document',
        originalData: {
          content: updatedSummary,
          consultationId: (overrideConsultation || currentConsultation)?.id,
          templateId: (templateOverride || selectedTemplate || defaultTemplate)?.id,
        },
      });
    }

    // `handleLimitReached` / `handleUsageUpdate` lived here. They were
    // local-state-only mirrors of the live-transcription limiter driven by the
    // dead mic-animation component deleted in Phase 2, and nothing has called
    // them since. Live metering becomes server-side in Phase 3, so they are
    // not worth keeping as scaffolding.

    const isSupportedTranscriptionFile = (filename) => {
      if (!filename) return false;
      const lowerCaseFilename = filename.toLowerCase();
      // const audioExtensions = ['.wav', '.mp3', '.flac', '.aac', '.m4a', '.webm', '.mp4']; //For SpeechMatics
      const supportedExtensions = ['.wav', '.mp3', '.flac', '.aac', '.m4a', '.ogg', '.opus', '.aiff', '.webm', '.mp4', '.mpeg', '.mov'];  //For video support (ElevenLabs)                           
      return supportedExtensions.some(ext => lowerCaseFilename.endsWith(ext));
    };

    const isAudioFileFormat = (filename) => {
      if (!filename) return false;
      const lowerCaseFilename = filename.toLowerCase();
      // const audioExtensions = ['.wav', '.mp3', '.flac', '.aac', '.m4a', '.webm', '.mp4']; //For SpeechMatics
      const audioExtensions = ['.wav', '.mp3', '.flac', '.aac', '.m4a', '.ogg', '.opus', '.aiff', '.webm'];
      return audioExtensions.some(ext => lowerCaseFilename.endsWith(ext));
    };

    const startAudioRecording = async (sharedMic = null, diagnostics) => {
      let stream;
      const owner = getCaptureOwner();
      try {
        if (!shouldSaveAudio()) return;

        audioChunksRef.current = [];
        setAudioUrl(null);
        setLastRecording(null);

        // Mono: a consultation is speech, and a stereo track would split the bitrate
        // budget below across two channels for no clinical gain. Plain value, not
        // `{ exact: 1 }` — a device that cannot do mono ignores it instead of throwing
        // OverconstrainedError. This stream feeds only the recorder; live transcription
        // uses a separately owned track. Live clones the shared mic source for recording.
        stream = sharedMic ? owner.clone(sharedMic) : await owner.acquire({
          audio: { deviceId: selectedMicId, channelCount: 1 }
        });

        observeDiagnosticStream(stream, 'record:input', selectedMicId);
        const preferredMimeTypes = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/ogg;codecs=opus',
          'audio/ogg',
        ];
        const supportedMimeType =
          preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || undefined;

        // Cap the bitrate: left unset, the browser defaults to roughly 128 kbps, which
        // turned a 90-minute session into an 87 MB upload — ~5 minutes to encrypt in one
        // buffer and PUT in a single non-resumable request, with no progress shown after
        // Stop. 48 kbps mono takes the same session to ~33 MB.
        //
        // Why 48 and not the 32 that Opus alone would justify (32 kbps mono is the top of
        // its fullband-speech range): Safari supports none of the types above, falls back
        // to AAC-LC, and AAC-LC is markedly weaker than Opus at the same rate. 48 kbps is
        // comfortable for both, so the recording does not depend on the clinician's
        // browser. These are recordings of a clinical session that may be re-listened to
        // as a record, so the conservative number wins over the extra 11 MB.
        //
        // It is a hint, not a guarantee: a browser that ignores it still records normally.
        const recorder = new MediaRecorder(stream, {
          audioBitsPerSecond: 48000,
          ...(supportedMimeType ? { mimeType: supportedMimeType } : {}),
        });

        setAudioMimeType(recorder.mimeType || supportedMimeType || 'audio/webm');
        activeRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        recorder.start(1000);
        audioCaptureStartedAtRef.current = performance.now();
        setIsRecordingAudio(true);
        return true;
      } catch (error) {
        owner.release(stream);
        activeRecorderRef.current = null;
        diagnostics?.fail('recorder', error);
        console.error('Error starting audio recording:', error);
        setAlert('Failed to start audio recording', 'error');
        return false;
      }
    };

    // Startup audio is provisional until the metered session and SDK both succeed.
    // A refusal/cancel must not create or upload a recording from that attempt.
    const discardStartupRecording = () => {
      const recorder = activeRecorderRef.current;
      if (!recorder) return;
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try { if (recorder.state !== 'inactive') recorder.stop(); } catch (_) {}
      captureOwnerRef.current?.release(recorder.stream);
      activeRecorderRef.current = null;
      audioChunksRef.current = [];
      audioCaptureStartedAtRef.current = null;
      setIsRecordingAudio(false);
    };

    const stopAudioRecording = async () => {
      const audioRecorder = activeRecorderRef.current;
      if (!audioRecorder || audioRecorder.state === 'inactive') return;
      const owner = captureOwnerRef.current;
      const durationSeconds = audioCaptureStartedAtRef.current == null ? null : Math.max(0, (performance.now() - audioCaptureStartedAtRef.current) / 1000);
      audioCaptureStartedAtRef.current = null;

      return new Promise((resolve) => {
        audioRecorder.onstop = async () => {
          try {
            // ondataavailable runs before onstop; use a ref to include the final chunk.
            // Keep the native compressed format: decoding long recordings into PCM
            // and rebuilding WAV consumes hundreds of MB before encryption/upload.
            const finalMimeType = audioRecorder.mimeType || audioMimeType;
            const recordedBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
            void inspectRecordedAudio(recordedBlob);
            owner?.release(audioRecorder.stream);

            if (audioUrl) {
              URL.revokeObjectURL(audioUrl);
            }
            const url = URL.createObjectURL(recordedBlob);
            setAudioUrl(url);
            setAudioMimeType(finalMimeType);

            if (audioOption !== 'no_save') {
              await uploadAudioToSupabase(recordedBlob, finalMimeType, url, undefined, durationSeconds);
            }

            audioChunksRef.current = [];
            activeRecorderRef.current = null;
            setIsRecordingAudio(false);
            resolve();
          } catch (error) {
            console.error('Error stopping audio recording:', error);
            owner?.release(audioRecorder.stream);
            setAlert('Failed to process audio recording', 'error');
            activeRecorderRef.current = null;
            setIsRecordingAudio(false);
            resolve();
          }
        };

        audioRecorder.stop();
      });
    };

    const uploadAudioToSupabase = async (audioBlob, mimeType_override, playbackUrl, existingFileName, durationSeconds = null) => {
      try {
        if (!audioBlob) return;

        const now = new Date();
        const formattedDate = convertToGMT8Date(now);
        const formattedTime = convertToGMT8Time(now).substring(0, 5);
        const timestamp = `${formattedDate} ${formattedTime}`;

        const mimeType = mimeType_override || 'audio/wav';
        const extensionMap = {
          'audio/webm;codecs=opus': 'webm',
          'audio/webm': 'webm',
          'audio/ogg;codecs=opus': 'ogg',
          'audio/ogg': 'ogg',
          'audio/mp4': 'm4a',
          'audio/mpeg': 'mp3',
          'audio/wav': 'wav',
          'audio/x-wav': 'wav',
          'audio/wave': 'wav',
        };
        const extension = extensionMap[mimeType.split(';')[0]] || 'webm';

        // A retry keeps the name the first attempt used. Minting a new one would send
        // the same recording to a second object key, so a half-written object from the
        // failed attempt would be orphaned in the bucket and the file_metadata row would
        // describe a different key than the one the earlier attempt may have created.
        const fileName = existingFileName || `recording_${timestamp}_${now.getTime()}.${extension}`;
        // Remember what is needed to re-send before anything can fail, so a failed
        // upload leaves "Retry upload" something to work with.
        pendingUploadRef.current = { audioBlob, mimeType_override, playbackUrl, fileName, durationSeconds };
        // Make the local recording recoverable before encryption or any network work.
        if (playbackUrl) {
          setLastRecording({ url: playbackUrl, path: null, fileName, durationSeconds });
        }
        // For save_only (no consultation), save as a patient-level file.
        const filePath = currentConsultation
          ? `${id}/${currentConsultation.id}/${fileName}`
          : `${id}/${fileName}`;

        const key = await deriveKey(id);
        const audioFile = new File([audioBlob], fileName, { type: mimeType });
        const encryptedAudio = await encryptFile(audioFile, key);

        // Progress + retry (utils/storageUpload): a consultation recording is tens of
        // megabytes, so the clinician needs to see it move, and a dropped connection
        // must not cost them the session — the only other copy is the blob URL in this
        // tab. `setUploadProgress` drives the bar on the post-recording card.
        setUploadProgress({ loaded: 0, total: encryptedAudio.size, attempt: 1, attempts: 4 });
        try {
          await uploadWithProgress('tikcare-attachments', filePath, encryptedAudio, {
            contentType: mimeType,
            cacheControl: '3600',
            upsert: false,
            onProgress: setUploadProgress,
          });
        } finally {
          setUploadProgress(null);
        }

        if (!currentConsultation) {
          // Register metadata so it appears in the Files tab
          const { error: metadataError } = await supabase.from('file_metadata').insert({
            file_path: filePath,
            uploaded_by: userDetails.id,
            uploader_name: userDetails.name,
            patient_id: id,
            consultation_id: null,
            file_name: fileName,
            file_size: audioBlob.size,
          });
          if (metadataError) throw metadataError;
          setAlert('Your recording has been saved. You can view it in the Recordings tab.', 'success');
        } else {
          setAlert('Your recording has been saved.', 'success');
        }
        // The object is in storage: nothing left to retry.
        pendingUploadRef.current = null;

        // Feed the inline post-recording player (and, for save_only, its
        // "Transcribe now" button) regardless of whether the file landed at
        // the patient level or inside an open consultation.
        if (playbackUrl) {
          setLastRecording({ url: playbackUrl, path: filePath, fileName, durationSeconds });
        }

        // Refresh the file list without holding the Stop button open. The
        // recording is already uploaded and its metadata is saved at this
        // point; history refresh can be slow for patients with many reports.
        void fetchHistoryItems({ background: true });
      } catch (error) {
        console.error('Error uploading audio to Supabase:', error);
        const errorText = [error?.message, error?.code, error?.error, error?.details]
          .filter(Boolean)
          .join(' ');
        const isFileTooLarge = /entitytoolarge|payload too large|maximum allowed size|file.?size/i.test(errorText);
        const formatBytes = (bytes) => {
          if (!Number.isFinite(bytes) || bytes <= 0) return '';
          const units = ['B', 'KB', 'MB', 'GB'];
          const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
          const value = bytes / (1024 ** index);
          return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
        };

        if (isFileTooLarge) {
          setAlert(
            `Recording is too large for cloud storage (${formatBytes(audioBlob?.size)}). Download the backup below, then record a shorter clip or ask an administrator to increase the file limit.`,
            'error'
          );
        } else {
          // Automatic retries are already spent by this point (utils/storageUpload), so
          // this is a sustained failure, not a blip — point at the manual retry rather
          // than only at the backup download.
          setAlert('Could not save recording to cloud storage. Use Retry upload below, or download your recording backup before leaving or starting another recording.', 'error');
        }
      }
    };

    /** Re-send a recording whose upload failed. No-op once it has landed. */
    const retryRecordingUpload = async () => {
      const pending = pendingUploadRef.current;
      if (!pending || uploadProgress) return;
      await uploadAudioToSupabase(pending.audioBlob, pending.mimeType_override, pending.playbackUrl, pending.fileName, pending.durationSeconds);
    };

    const handleTranscribeAudio = async (audioItem) => {
      handleMenuClose();
      setSelectedAudioItemForTranscription(audioItem);
      setShowTranscriptionLanguagePopup(true);
    };

    // Recorded-transcription usage is accumulated server-side. The RPC derives
    // the user from the verified session, adds atomically (no read-modify-write
    // race between tabs) and returns the authoritative totals. It rejects
    // p_additional_seconds outside 1..21600, so guard before calling.
    // Removed in Phase 3, when the credit wallet meters this instead.
    const AUDIO_USAGE_MIN_SECONDS = 1;
    const AUDIO_USAGE_MAX_SECONDS = 21600; // 6 hours

    const handleAudioUsageUpdate = async (additionalSeconds) => {
      const seconds = Math.ceil(additionalSeconds);

      if (!Number.isFinite(seconds) || seconds < AUDIO_USAGE_MIN_SECONDS) {
        // Nothing measurable to record; not an error worth alerting on.
        return;
      }
      if (seconds > AUDIO_USAGE_MAX_SECONDS) {
        console.error('Audio usage not recorded: duration exceeds the 6-hour limit', seconds);
        setAlert(
          'This recording is longer than the 6-hour limit, so its usage could not be recorded. Please contact support.',
          'error'
        );
        return;
      }

      const { data, error } = await supabase.rpc('record_audio_transcription_usage', {
        p_additional_seconds: seconds,
      });

      if (error) {
        console.error('Error updating audio transcription usage:', error);
        setAlert('Could not record your audio transcription usage: ' + error.message, 'error');
        return;
      }

      const result = Array.isArray(data) ? data[0] : data;

      if (!result || result.updated !== true) {
        console.error('Audio transcription usage was not recorded', result);
        setAlert('Could not record your audio transcription usage. Please try again.', 'error');
        return;
      }

      setSubscriptionInfo(prev => ({
        ...prev,
        audio_transcription_seconds_used: result.audio_transcription_seconds_used,
        audio_transcription_seconds_limit: result.audio_transcription_seconds_limit,
        audio_limit_reached: result.limit_reached,
      }));
    };

    const recordedReviewMessage = 'The transcription result could not be confirmed. Use Check result to retrieve this operation without transcribing or charging again. Your saved audio is still available.';

    const performAudioTranscription = async (audioItem, selectedLanguage, checkOnly = false) => {
      // A ref protects same-tick clicks before React can disable the button.
      if (recordedWorkRef.current) return false;
      if (recordingOperationRef.current || recognizingRef.current || recordingAudioRef.current || liveSessionRef.current) {
        setAlert('Please finish stopping the current recording before transcribing an audio file.', 'warning');
        return false;
      }
      const scope = recordedScope;
      if (!userDetails?.id || !id || !audioItem?.path) return false;
      const unsaved = [...recordedAttemptsRef.current.values()].find(item => item.status === 'save_pending');
      if (unsaved && (unsaved.scope !== scope || unsaved.audioItem.path !== audioItem.path)) {
        setAlert('Please retry saving your returned transcription before starting another transcription.', 'warning');
        return false;
      }
      const key = recordedAttemptKey(process.env.REACT_APP_MAIN_BACKEND_URL, userDetails.id, id, audioItem.path);
      let attempt = recordedAttemptsRef.current.get(key);
      if (!attempt) {
        try {
          const previous = readRecordedAttempt(key);
          if (previous) {
            attempt = { ...previous, status: 'needs_review', scope, audioItem };
            recordedAttemptsRef.current.set(key, attempt);
          }
        } catch (_) {
          setAlert('Cannot read transcription recovery information. Please check browser storage before trying again.', 'error');
          return false;
        }
      }
      const recovering = attempt?.status === 'needs_review';
      // A stale recovery callback can never create a new paid request.
      if (checkOnly && !attempt) return false;
      // A returned result is retained independently of the visible consultation.
      // Retry saving bypasses both billing and subscription checks.
      if (!attempt?.transcription && !recovering) {
        if (!transcriptionLanguages.some(option => option.value === selectedLanguage?.value)) {
          setAlert('Please select a supported language for audio transcription.', 'warning');
          return false;
        }
        if (!subscriptionInfo.is_active) {
          setAlert('Your subscription is not active. Please activate your subscription to continue.', 'error');
          return false;
        }
        // The recorded-transcription endpoint checks available wallet credits.
        // Legacy monthly Free counters can outlive the one-time trial and must
        // not block otherwise valid credits after their reset job is retired.
        attempt = { operationId: uuidv4(), languageCode: selectedLanguage.value, scope, audioItem, status: 'needs_review' };
        // Write before sending: a reload or lost response must not create a new
        // billable operation. Fail closed if browser storage is unavailable.
        try { writeRecordedAttempt(key, attempt); }
        catch (_) {
          setAlert('Cannot keep transcription recovery information. Please enable browser storage before transcribing.', 'error');
          return false;
        }
        recordedAttemptsRef.current.set(key, attempt);
      }
      recordedWorkRef.current = true;
      refreshRecordingBusy();
      setSavingConsultation(true);
      setNoSpeechDetected(false);
      setHasCompletedTranscription(false);
      try {
        if (!attempt.transcription) {
          setTranscriptionStage('transcribing');
          let response;
          let payload;
          try {
            response = recovering
              ? await authedFetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/scribe/recorded-transcriptions/${encodeURIComponent(attempt.operationId)}`)
              : await authedFetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/scribe/recorded-transcriptions`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
                operation_id: attempt.operationId, storage_path: audioItem.path,
                language_code: attempt.languageCode,
              }),
            });
            payload = await response.json();
          } catch (_) {
            setRecordedTranscriptionRecovery(attempt);
            creditWalletOperationCompleted();
            setAlert(recordedReviewMessage, 'warning');
            return false;
          }
          if (!response.ok) {
            // Validation/auth/balance rejection precedes provider work. A 409,
            // 5xx or unreadable response cannot prove that no charge occurred.
            const rejectedBeforeProvider = !recovering && ((response.status >= 400 && response.status < 500 && response.status !== 409)
              || (response.status === 503 && ['owned audio is unavailable', 'audio authorization unavailable'].includes(payload?.error)));
            if (rejectedBeforeProvider) {
              clearRecordedAttempt(key);
              recordedAttemptsRef.current.delete(key);
              setRecordedTranscriptionRecovery(null);
              setAlert(payload?.error || 'Audio transcription failed.', 'error');
            } else {
              setRecordedTranscriptionRecovery(attempt);
              creditWalletOperationCompleted();
              setAlert(recordedReviewMessage, 'warning');
            }
            return false;
          }
          creditWalletOperationCompleted();
          if ((payload?.patient_id && payload.patient_id !== id) || (payload?.storage_path && payload.storage_path !== audioItem.path)) {
            setRecordedTranscriptionRecovery({ ...attempt, message: 'The returned operation belongs to a different recording. No transcript was saved. Contact support with the operation number.' });
            return false;
          }
          if (payload?.operation_id && payload.operation_id !== attempt.operationId) {
            // Another tab/device already claimed this resource. Follow its
            // existing operation; do not create a second charge.
            attempt.operationId = payload.operation_id;
            attempt.languageCode = payload.language_code || attempt.languageCode;
            writeRecordedAttempt(key, attempt);
          }
          if (payload?.status === 'failed_refunded') {
            clearRecordedAttempt(key);
            recordedAttemptsRef.current.delete(key);
            setRecordedTranscriptionRecovery(null);
            setAlert(`Transcription failed. ${payload.refunded_credits} credits were returned. Check the language and audio before trying again.`, 'warning');
            return false;
          }
          if (typeof payload?.transcription !== 'string') {
            attempt.message = payload?.status === 'processing'
              ? 'This transcription is still processing. Check result again shortly; this does not start another transcription.'
              : payload?.status === 'failed_refund_pending'
                ? 'Transcription failed. The credit refund is pending. Check result again to confirm the refund before retrying.'
                : payload?.status === 'completed_result_unavailable'
                  ? 'This older transcription completed, but no recoverable result was stored. Check Files or contact support with the operation number.'
                  : 'No result has been confirmed yet. Check result again later. Another transcription is blocked to avoid an additional charge.';
            setRecordedTranscriptionRecovery({ ...attempt });
            setAlert(attempt.message, 'warning');
            return false;
          }
          if (!payload.transcription.trim()) {
            clearRecordedAttempt(key);
            recordedAttemptsRef.current.delete(key);
            setRecordedTranscriptionRecovery(null);
            setAlert('Transcription returned no text. If you can hear speech, check the selected language before trying again. Another attempt may use credits.', 'warning');
            return false;
          }
          attempt.transcription = payload.transcription;
          attempt.resultPersisted = payload.result_persisted === true || ['completed', 'result_ready'].includes(payload.status);
          attempt.status = 'save_pending';
          attempt.row = {
            id: payload.consultation_id || attempt.consultationId || uuidv4(), patient_id: id, transcription: attempt.transcription, notes: null,
            chiefComplaint: `Consultation - ${convertToGMT8Date(new Date().toISOString())}`,
            createdAt: payload.created_at || new Date().toISOString(), created_by: userDetails.name || 'Unknown User',
            created_by_user_id: userDetails.id,
          };
          attempt.consultationId = attempt.row.id;
          recordedUnsavedRef.current = true;
          try { writeRecordedAttempt(key, attempt); } catch (_) { /* Initial operation marker is already stored; keep the returned draft. */ }
          setRecordedTranscriptionRecovery({ ...attempt });
        }
        setTranscriptionStage('saving');
        // A stable consultation ID makes an ambiguous database response safe to
        // retry. Store the transcript in the same write that creates the row;
        // never switch the current workspace to a half-created consultation.
        const saved = await saveRecordedTranscript(supabase, attempt.row);
        attempt.status = 'saved';
        recordedAttemptsRef.current.delete(key);
        try { clearRecordedAttempt(key); } catch (_) { /* Keep conservative reload protection. */ }
        recordedUnsavedRef.current = [...recordedAttemptsRef.current.values()].some(item => item.status === 'save_pending');
        setRecordedTranscriptionRecovery(null);
        // Navigation during a request must not populate a different patient.
        if (recordedScopeRef.current !== scope) return true;
        indexConsultationTranscript(undefined, { requireContent: true });
        resetNewConsultationWorkspace();
        setFirstView(false);
        setPopupReport(false);
        setCurrentConsultation(saved);
        setPastConsultations(previous => [saved, ...previous.filter(item => item.id !== saved.id)]);
        setTranscription(saved.transcription);
        transcriptionRef.current = saved.transcription;
        setTranscriptSaveStatus('saved');
        indexConsultationTranscript(saved);
        await fetchHistoryItems();
        setAlert('Audio transcription completed successfully!', 'success');
        setHasCompletedTranscription(true);
        setTranscriptRevealToken(previous => previous + 1);
        applyStreamingSpeakerLabels({ consultation: saved });
        if (lastRecording?.path === audioItem.path) {
          setLastRecording(current => current?.path === audioItem.path ? { ...current, transcribed: true } : current);
        }
        setSelectedAudioItemForTranscription(null);
        return true;
      } catch (_) {
        if (attempt.status === 'saved') {
          setAlert('Your transcription was saved. Please reopen it from Files if the workspace could not refresh.', 'warning');
          return true;
        }
        setRecordedTranscriptionRecovery({ ...attempt });
        if (attempt.status === 'save_pending') {
          setAlert(attempt.resultPersisted ? 'Your transcription is ready, but could not be saved to Files. Retry saving will not transcribe or charge again.' : 'Your transcription is ready, but server result storage and saving to Files failed. Keep this page open and retry saving; refreshing may lose the text.', 'error');
        } else {
          setAlert(recordedReviewMessage, 'warning');
        }
        return false;
      } finally {
        recordedWorkRef.current = false;
        refreshRecordingBusy();
        setSavingConsultation(false);
        setTranscriptionStage(null);
      }
    };

    const retryRecordedTranscriptionSave = () => {
      if (recordedTranscriptionRecovery?.scope !== recordedScope || recordedTranscriptionRecovery?.status !== 'save_pending') return false;
      const key = recordedAttemptKey(process.env.REACT_APP_MAIN_BACKEND_URL, userDetails.id, id, recordedTranscriptionRecovery.audioItem.path);
      // Even an old UI callback must never turn Retry saving into a fresh paid
      // request after another save has already completed.
      if (recordedAttemptsRef.current.get(key)?.status !== 'save_pending') return false;
      return performAudioTranscription(recordedTranscriptionRecovery.audioItem, { value: recordedTranscriptionRecovery.languageCode });
    };

    const checkRecordedTranscriptionResult = () => {
      if (recordedTranscriptionRecovery?.scope !== recordedScope || recordedTranscriptionRecovery?.status !== 'needs_review') return false;
      return performAudioTranscription(recordedTranscriptionRecovery.audioItem,
        { value: recordedTranscriptionRecovery.languageCode }, true);
    };

    // Selecting a saved recording after reload restores its operation controls.
    // The browser stores only identifiers, never the returned clinical text.
    useEffect(() => {
      const audio = selectedAudioItemForTranscription || lastRecording;
      if (!audio?.path || !userDetails?.id || !id || recordedWorkRef.current) return;
      const key = recordedAttemptKey(process.env.REACT_APP_MAIN_BACKEND_URL, userDetails.id, id, audio.path);
      try {
        const previous = readRecordedAttempt(key);
        if (previous && !recordedAttemptsRef.current.has(key)) {
          const attempt = { ...previous, status: 'needs_review', scope: recordedScope, audioItem: audio };
          recordedAttemptsRef.current.set(key, attempt);
          setRecordedTranscriptionRecovery(attempt);
        }
      } catch (_) { /* Submission remains fail-closed if recovery storage is unreadable. */ }
    }, [selectedAudioItemForTranscription, lastRecording, recordedScope, userDetails?.id, id]);

    const getAudioProperties = async (audioBlob) => {
      return new Promise((resolve, reject) => {
        try {
          // Create an audio context to analyze the audio file
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          
          // Convert blob to array buffer
          const reader = new FileReader();
          reader.onload = async (event) => {
            try {
              const arrayBuffer = event.target.result;
              
              // Decode the audio data
              const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
              
              const properties = {
                channels: audioBuffer.numberOfChannels,
                sampleRate: audioBuffer.sampleRate,
                duration: audioBuffer.duration,
                length: audioBuffer.length
              };
              
              // Clean up audio context
              await audioContext.close();
              
              resolve(properties);
            } catch (decodeError) {
              console.error('Error decoding audio:', decodeError);
            }
          };
          
          reader.onerror = () => {
            reject(new Error('Failed to read audio file'));
          };
          
          reader.readAsArrayBuffer(audioBlob);
          
        } catch (error) {
          console.error('Error in getAudioProperties:', error);
          
          // Ultimate fallback - assume mono for safety
          resolve({
            channels: 1,
            sampleRate: 44100,
            duration: 0,
            length: 0
          });
        }
      });
    };

    // Helper function to get audio duration
    const getAudioDuration = async (audioBlob) => {
      return new Promise((resolve, reject) => {
        // Create object URL from the blob
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio();
        
        // Set up event listeners before setting src
        let durationCheckAttempts = 0;
        const maxAttempts = 10;
        
        const checkDuration = () => {
          // Check if we have a valid duration
          if (isFinite(audio.duration) && audio.duration > 0) {
            clearInterval(durationCheckInterval);
            URL.revokeObjectURL(audioUrl);
            resolve(audio.duration);
            return true;
          }
          
          // Increment attempt counter
          durationCheckAttempts++;
          
          // If we've exceeded max attempts, use file size to estimate duration
          if (durationCheckAttempts >= maxAttempts) {
            clearInterval(durationCheckInterval);
            URL.revokeObjectURL(audioUrl);
            
            // Fallback: estimate duration based on file size
            // Assuming average bitrate of 128 kbps for MP3
            // (128000 bits per second / 8 = 16000 bytes per second)
            const estimatedDuration = audioBlob.size / 16000;
            // console.log('Could not determine audio duration directly, estimating based on file size:', estimatedDuration);
            resolve(estimatedDuration);
            return true;
          }
          
          return false;
        };
        
        // Set up interval to check duration periodically
        const durationCheckInterval = setInterval(checkDuration, 300);
        
        // Still set up the loadedmetadata event
        audio.addEventListener('loadedmetadata', () => {
          if (checkDuration()) {
            clearInterval(durationCheckInterval);
          }
        });
        
        // Handle errors
        audio.addEventListener('error', (err) => {
          clearInterval(durationCheckInterval);
          URL.revokeObjectURL(audioUrl);
          console.error('Error loading audio:', err);
          
          // Fallback to size-based estimation on error
          const estimatedDuration = audioBlob.size / 16000;
          resolve(estimatedDuration);
        });
        
        // Start loading the audio
        audio.preload = 'metadata';
        audio.src = audioUrl;
        audio.load();
      });
    };

    const persistRefinementResult = (result, signal) => {
      const { consultation, rawTranscript, accumulated, originalNotes } = result;
      const operation = saveQueue.current.then(async () => {
        if (activeConsultationRef.current === consultation.id && transcriptionRef.current !== rawTranscript) {
          throw new Error('Your newer edits were kept. The refined result has not replaced them.');
        }
        await saveRefinedTranscript(supabase, id, consultation.id, rawTranscript, accumulated, signal);
        if (recordingPageMountedRef.current && activeConsultationRef.current === consultation.id && transcriptionRef.current === rawTranscript) {
          transcriptionRef.current = accumulated;
          setTranscription(accumulated);
          setTranscriptSaveStatus('saved');
        }
        setCurrentConsultation(prev => prev?.id === consultation.id ? { ...prev, transcription: accumulated } : prev);
        setPastConsultations(prev => prev.map(item => item.id === consultation.id ? { ...item, transcription: accumulated } : item));
        setHistoryItems(prev => prev.map(item => item.id === consultation.id ? { ...item, originalData: { ...item.originalData, transcription: accumulated } } : item));
        indexConsultationTranscript(consultation, { sourceText: { transcription: accumulated, notes: originalNotes } });
      });
      // A rejected refinement must not poison subsequent manual saves.
      saveQueue.current = operation.catch(() => false);
      return operation;
    };

    // Applies LLM-based speaker labels to the transcript via SSE streaming
    // Gated by CW_UNMETERED_PROVIDERS_ENABLED — protects the
    // add-speaker-labels-stream Cloud Function (CW-006 owner-ask: sources
    // unlocated, owner not yet asked). While the flag is false this returns
    // before touching the network, so all three callers — the "Refine with AI"
    // button, auto-refine after file transcription, and auto-refine after mic
    // stop — are inert (REACT_APP_STREAM_BACKEND_URL becomes unreachable).
    const applyStreamingSpeakerLabels = async ({ consultation = currentConsultation } = {}) => {
      if (!CW_UNMETERED_PROVIDERS_ENABLED) return;
      const rawTranscript = transcriptionRef.current;
      if (!rawTranscript?.trim() || !consultation?.id || refinementBusyRef.current || pendingRefinementResult.current || refineTranscriptFailure?.retryAt > Date.now()) return;
      refinementBusyRef.current = true;
      const controller = new AbortController();
      refinementController.current = controller;
      let finishRefinement;
      refinementDone.current = new Promise(resolve => { finishRefinement = resolve; });
      const originalNotes = remarksRef.current;

      // Keep partial streamed output in a preview. Only the complete result
      // replaces the saved transcription.
      setIsRefiningTranscript(true);
      setRefineTranscriptError(false);
      setRefineTranscriptFailure(null);
      setIsPendingRefinement(false);
      setRefinedPreview('');

      let accumulated = '';
      let completedResult = null;
      const diagnostics = createRefinementDiagnostics('live', rawTranscript);
      pendingRefinementResult.current = null;

      try {
        accumulated = await refineTranscript({ transcript: rawTranscript, signal: controller.signal, diagnostics,
          onPreview: text => { if (activeConsultationRef.current === consultation.id) setRefinedPreview(text); },
        });
        completedResult = { consultation, rawTranscript, accumulated, originalNotes, diagnostics };
        diagnostics.emit('save_started');
        await persistRefinementResult(completedResult, controller.signal);
        diagnostics.emit('save_succeeded');
        setRefinedPreview('');
        setIsPendingRefinement(false);
        setAlert('Refined transcript saved.', 'success');

      } catch (err) {
        if (controller.signal.aborted && !completedResult) return;
        if (completedResult) diagnostics.saveFailed(err);
        if (completedResult) {
          // Persistence errors may echo row data. Keep the existing save event,
          // but pass a generated message rather than the raw provider payload.
          logTranscriptSaveFailure(safeRefinementSaveError(err), {
            where: 'refinement_persist',
            transcriptChars: accumulated.length,
            notesChars: originalNotes?.length ?? 0,
          });
        }
        if (completedResult && activeConsultationRef.current === consultation.id) {
          pendingRefinementResult.current = completedResult;
          setRefinedPreview(accumulated);
          setIsPendingRefinement(true);
          setTranscriptSaveStatus('error');
          setAlert('Could not save the refined transcript. The result is still here; use Save changes to retry.', 'error');
        } else {
          setRefinedPreview('');
          setRefineTranscriptError(true);
          setRefineTranscriptFailure(err);
          if (!completedResult) setAlert(refinementMessage(err), 'error');
        }
      } finally {
        finishRefinement();
        refinementController.current = null;
        refinementBusyRef.current = false;
        if (!pendingRefinementResult.current) setRefinedPreview('');
        setIsRefiningTranscript(false);
      }
    };

    // `offerRegenerate: false` is used by the pending-refinement gate below:
    // the caller is about to generate anyway, so the regenerate prompt would
    // be noise. The Save Changes button calls this with no arguments.
    const acceptRefinement = async ({ offerRegenerate = true, useCompletedText = false } = {}) => {
      const result = pendingRefinementResult.current;
      if (result) {
        try {
          setTranscriptSaveStatus('saving');
          result.diagnostics?.emit('save_started');
          await persistRefinementResult({ ...result, accumulated: useCompletedText ? result.accumulated : refinedPreview });
          result.diagnostics?.emit('save_succeeded');
          pendingRefinementResult.current = null;
          setIsPendingRefinement(false);
          setRefinedPreview('');
          setRefineTranscriptError(false);
          setRefineTranscriptFailure(null);
        } catch (error) {
          result.diagnostics?.saveFailed(error);
          setTranscriptSaveStatus('error');
          setAlert('Could not save the refined transcript. The result is still here; use Save changes to retry.', 'error');
          return false;
        }
      } else {
        transcriptionRef.current = refinedPreview;
        setTranscription(refinedPreview);
        if (!await saveTextOnly()) return false;
        setIsPendingRefinement(false);
        setRefinedPreview('');
      }
      if (offerRegenerate) offerRegenerateAfterRefine();
      return true;
    };

    const saveTranscriptBeforeLeaving = async () => {
      const alreadyPending = Boolean(pendingRefinementResult.current);
      await cancelActiveRefinement();
      // A result completed while waiting for cancellation may not have rendered
      // yet. Use its captured text instead of the caller's stale preview.
      if (pendingRefinementResult.current && !await acceptRefinement({
        offerRegenerate: false, useCompletedText: !alreadyPending,
      })) return false;
      return saveTextOnly();
    };

    const discardRefinement = () => {
      pendingRefinementResult.current = null;
      // transcription state is already the original — just clear the preview
      setRefinedPreview('');
      setIsPendingRefinement(false);
    };

    // Gate for the report-generation entry points ("Generate Document" and
    // the template picker's Generate). Reports are built from
    // transcriptionRef, which still holds the ORIGINAL text while a refinement
    // is pending, so ask before spending a credit on the wrong transcript.
    // While the stream is in flight the buttons are disabled in the UI; the
    // blocked branch here is a safety net for any other caller.
    const pendingRefineGate = async (action) => {
      const decision = decideGenerateGate({ isRefiningTranscript, isPendingRefinement });
      if (decision === GENERATE_BLOCKED) return;
      if (decision === GENERATE_DIALOG) {
        if (!await acceptRefinement({ offerRegenerate: false })) return;
      }
      return action();
    };

    const closeRefinePendingDialog = () => {
      setRefinePendingDialog({ open: false, onSaveAndRun: null, onRunOriginal: null });
    };

    // After Save Changes: if this consultation already has a report, it was
    // built from the original transcript — offer a fresh one. historyItems is
    // the source of truth here (sectionSummaryIdRef is only set right after a
    // generation in this session, see utils/refinePendingGate.js).
    const offerRegenerateAfterRefine = () => {
      const hasExistingReport = hasReportForConsultation(historyItems, currentConsultation?.id);
      if (decideAfterAccept({ hasExistingReport }) === AFTER_ACCEPT_OFFER_REGENERATE) {
        setRefineRegenerateDialog({ open: true });
      }
    };

    const closeRefineRegenerateDialog = () => setRefineRegenerateDialog({ open: false });

    const regenerateAfterRefine = async () => {
      closeRefineRegenerateDialog();
      await regenerateReportConsultation();
    };

    // Settle the current live session (start-failure paths). The backend settle
    // call records the final heartbeat itself. rethrow=true: the caller wants to
    // know it is not settled yet. Either way an unconfirmed id stays remembered
    // (liveSessionRecovery) and is settled when the connection returns or on the
    // next visit, so a failure never loses it.
    const settleCurrentLiveSession = async ({ rethrow = false } = {}) => {
      const sessionId = liveSessionRef.current;
      if (!sessionId) return;
      markLiveSessionStopped(userIdRef.current, sessionId);
      const outcome = await settlePendingLiveSession(userIdRef.current, sessionId, settleLiveSession);
      if (outcome === 'pending' && rethrow) throw new Error('Live settlement is pending');
      if (liveSessionRef.current === sessionId) liveSessionRef.current = null;
    };

    // Merge a result into the post-Live record, unless a newer start replaced it.
    // Done = SDK finished, transcript saved and session settled. A finished normal
    // Stop clears the record; a finished connection-lost record stays for its notice.
    const updateLiveSync = (base, change) => {
      const current = liveSyncRef.current;
      if (!current || current.token !== base.token) return;
      const next = { ...current, ...change };
      next.done = Boolean(next.recognizerDone && next.saved && next.settled);
      setLiveSync(next.done && next.reason === 'stopped' ? null : next);
    };

    const runLiveSave = async sync => {
      liveSaveRunningRef.current = true;
      try {
        const saved = await saveTextOnly({ quiet: sync.reason === 'connection_lost' });
        updateLiveSync(sync, { saved });
        if (saved && !sync.labelled && transcriptionRef.current?.trim()) {
          updateLiveSync(sync, { labelled: true });
          void applyStreamingSpeakerLabels();
        }
      } finally {
        liveSaveRunningRef.current = false;
      }
    };
    const runLiveSettle = async sync => {
      liveSettleRunningRef.current = true;
      try {
        const outcome = await settlePendingLiveSession(userIdRef.current, sync.sessionId, settleLiveSession);
        updateLiveSync(sync, { settled: outcome !== 'pending' });
      } finally {
        liveSettleRunningRef.current = false;
      }
    };
    // Save the transcript (once the SDK delivered its final result) and settle the
    // session, IN PARALLEL with their own timeouts and running flags, so a hanging
    // save never blocks settlement or a later retry, and vice versa. Retried on
    // `online` and every 15 s until both are confirmed.
    const runLiveSync = async () => {
      const sync = liveSyncRef.current;
      if (!sync || sync.done) return;
      const tasks = [];
      if (!sync.settled && !liveSettleRunningRef.current) tasks.push(runLiveSettle(sync));
      if (sync.recognizerDone && !sync.saved && !liveSaveRunningRef.current) tasks.push(runLiveSave(sync));
      await Promise.all(tasks);
    };
    const runLiveSyncRef = useRef(runLiveSync);
    runLiveSyncRef.current = runLiveSync;
    const retryRecordingUploadRef = useRef(retryRecordingUpload);
    retryRecordingUploadRef.current = retryRecordingUpload;
    useEffect(() => {
      if (!liveSync || liveSync.done) return undefined;
      const retry = () => {
        if (navigator.onLine === false) return;
        void runLiveSyncRef.current();
        // A recording whose upload failed during the outage keeps its retry and
        // download controls; also retry it once the connection is back.
        if (liveSyncRef.current?.reason === 'connection_lost' && pendingUploadRef.current) void retryRecordingUploadRef.current();
      };
      window.addEventListener('online', retry);
      const interval = setInterval(retry, 15000);
      return () => { window.removeEventListener('online', retry); clearInterval(interval); };
    }, [liveSync]);

    // Stopping Live. The LOCAL part happens at once: monitoring, heartbeat, the
    // microphone and the timer stop, the recorder is finalized and the panel shows
    // the stopped state (and, after a lost connection, the sound and notice). Then,
    // separately: settlement starts immediately, the SDK gets up to its 5 s
    // deadline to deliver the final result, and only then is the transcript saved.
    // The saved-audio upload runs on its own and never delays the stopped state.
    const completeLiveStop = async ({ connectionLost = false } = {}) => {
      liveMonitorRef.current?.dispose();
      liveMonitorRef.current = null;
      setLiveConnectionWarning(false);
      if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
      stopLiveNoSpeechWatchdog();
      if (throttleTimeout.current) { clearTimeout(throttleTimeout.current); throttleTimeout.current = null; }
      const inputEnded = liveInputEndedRef.current;
      liveInputEndedRef.current = null;
      const finishingAudio = liveBufferedAudioRef.current?.finish();
      liveBufferedAudioRef.current = null;
      const stoppingRecognizer = activeRecognizerRef.current || recognizer;
      activeRecognizerRef.current = null;
      const sessionId = liveSessionRef.current;
      // From here the session lives only in the recovery store until settled.
      liveSessionRef.current = null;
      if (sessionId) markLiveSessionStopped(userIdRef.current, sessionId);
      if (recordingAudioRef.current && shouldSaveAudio()) {
        // The recorder stops now; its upload (and any offline failure, which keeps
        // the existing retry/download controls) continues in the background.
        void Promise.resolve(stopAudioRecording()).catch(error => console.error('Saving the recording failed:', error));
      }
      releaseLiveAudio();
      if (micStream) micStream.getTracks().forEach(track => track.stop());
      if (isTeleHealthActive && systemStream) systemStream.getTracks().forEach(track => track.stop());
      if (connectionLost) playConnectionLostSound();
      const sync = { token: Symbol('liveSync'), reason: connectionLost ? 'connection_lost' : 'stopped', sessionId,
        recognizerDone: false, saved: false, settled: !sessionId, done: false };
      setLiveSync(sync);
      setRecognizer(null);
      setLiveRecognitionInterrupted(false);
      setIsRecognizing(false);
      setHasCompletedTranscription(true);
      setTranscriptRevealToken(previous => previous + 1);

      const settling = runLiveSync();
      if (finishingAudio) await finishingAudio;
      await stopSpeechRecognizer(stoppingRecognizer, { inputEnded: connectionLost ? undefined : inputEnded });
      if (!connectionLost) {
        if (transcriptionRef.current && transcriptionRef.current.trim()) {
          setNoSpeechDetected(false);
        } else {
          setNoSpeechDetected(true);
          setAlert('No speech was detected. Check the microphone and transcription language, then try recording again.', 'warning');
        }
      }
      updateLiveSync(sync, { recognizerDone: true });
      await Promise.all([settling, runLiveSync()]);
    };

    // Connection lost during Live (see liveConnectionMonitor.js): stop now, keep
    // the transcript, and do not resume by itself.
    const handleLiveConnectionLost = async () => {
      if (!recognizingRef.current || !liveSessionRef.current || recordingOperationRef.current) return;
      recordingOperationRef.current = true;
      updateRecordingBusy(true);
      setIsSettling(true);
      try {
        if (recordingStartedAtRef.current) {
          track(EVENTS.RECORDING_COMPLETED, {
            duration_ms: Date.now() - recordingStartedAtRef.current,
            language: language?.value || undefined,
            success: false,
          });
          recordingStartedAtRef.current = null;
        }
        await completeLiveStop({ connectionLost: true });
      } catch (error) {
        console.error('Stopping after connection loss failed:', error);
      } finally {
        setIsSettling(false);
        recordingOperationRef.current = false;
        refreshRecordingBusy();
      }
    };
    liveConnectionLostRef.current = handleLiveConnectionLost;

    // Speech recognition functions
    async function sttFromMic() {
      // Lock before the first await, including microphone permission and
      // consultation creation. React state alone permits rapid double clicks.
      if (recordingOperationRef.current || refinementBusyRef.current) return;
      if (!recognizingRef.current && liveSyncRef.current && !liveSyncRef.current.done) {
        setAlert('Waiting for connection to save and settle the previous transcription. You can continue once it is saved.', 'warning');
        return;
      }
      if (!recognizingRef.current && (recordedWorkRef.current || recordedUnsavedRef.current)) {
        setAlert('Please finish saving your returned transcription before starting a recording.', 'warning');
        return;
      }
      recordingOperationRef.current = true;
      updateRecordingBusy(true);
      const starting = !recognizingRef.current;
      setIsStarting(starting);
      try {
        await performSttFromMic();
      } catch (error) {
        liveStartupDiagnosticsRef.current?.finish('failed');
        if (starting) {
          try { activeRecognizerRef.current?.close(); } catch (_) {}
          activeRecognizerRef.current = null;
          releaseLiveAudio();
          stopLiveNoSpeechWatchdog();
          if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
          if (liveSessionRef.current) await settleCurrentLiveSession();
        }
        console.error('Recording operation failed:', error);
        setAlert(starting ? 'Could not start recording. Please check your microphone and try again.' : 'Could not stop recording. Please try Stop again before leaving this page.', 'error');
      } finally {
        liveStartupDiagnosticsRef.current?.finish();
        liveStartupDiagnosticsRef.current = null;
        recordingOperationRef.current = false;
        setIsStarting(false);
        setStartupStage('');
        // A partially failed start can still own audio/a server session. Keep
        // Stop available so the user can explicitly finish and save it. (After a
        // Stop, the saved-audio upload legitimately continues in the background.)
        if (starting && !recognizingRef.current && (recordingAudioRef.current || liveSessionRef.current)) setIsRecognizing(true);
        refreshRecordingBusy();
        // The connection was lost while the SDK was still starting: the stop
        // handler could not act then, so honour it now that startup finished.
        if (recognizingRef.current && liveMonitorRef.current?.isLost) {
          void liveConnectionLostRef.current();
        }
        if (meetingInterruptedRef.current) {
          meetingInterruptedRef.current = false;
          if (recordingPageMountedRef.current && recognizingRef.current) {
            void meetingStopRef.current();
          }
        }
      }
    }

    async function performSttFromMic() {
        if (recognizingRef.current) {
          if (recordingStartedAtRef.current) {
            track(EVENTS.RECORDING_COMPLETED, {
              duration_ms: Date.now() - recordingStartedAtRef.current,
              language: language?.value || undefined,
            });
            recordingStartedAtRef.current = null;
          }
          // A saved recording never uses speech recognition. A recognizer left
          // from an earlier Live session must not turn Stop Recording into a
          // transcription/settlement action.
          if (audioOption !== 'save_only' && (recognizer || liveSessionRef.current)) {
            // Stop always finishes locally (recognizer, audio, timer). Saving the
            // transcript (the report endpoint is metered separately and runs only
            // on Generate Document) and settling the session follow in
            // runLiveSync; if either fails it is retried and the panel says so,
            // instead of leaving Live looking like it is still running.
            setIsSettling(true);
            try {
              await completeLiveStop();
            } catch (error) {
              console.error('Stop failed:', error);
              setAlert('Could not stop transcription cleanly. Your transcript is kept; please check it before leaving.', 'error');
            } finally {
              setIsSettling(false);
            }
          } else {
            if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
            stopLiveNoSpeechWatchdog();
            if (throttleTimeout.current) { clearTimeout(throttleTimeout.current); throttleTimeout.current = null; }
            // save_only recordings run with no recognizer and no wallet
            // session, so they land here on stop: finish and upload the audio
            // before clearing the flag. (Also covers the degenerate state of a
            // recognizing flag with nothing behind it — stopAudioRecording is a
            // no-op when no recorder is active.)
            if (isRecordingAudio && shouldSaveAudio()) {
              setIsSettling(true);
              try {
                await stopAudioRecording();
              } catch (error) {
                console.error('Error stopping save-only recording:', error);
              } finally {
                setIsSettling(false);
              }
            }
            setIsRecognizing(false);
          }
        } else {
            if(selectedMicId === null) {
              setAlert('Please select a microphone!', 'error');
              return;
            }
            // Recording-only mode creates neither a consultation nor a wallet session.
            if (audioOption === 'save_only') {
              if (isTeleHealthActive) {
                setAlert('Google Meet capture is available in Live mode. Select Live or switch to an in-person microphone recording.', 'warning');
                return;
              }
              if (!await startAudioRecording()) return;
              setNoSpeechDetected(false);
              setHasCompletedTranscription(false);
              setLiveRecognitionInterrupted(false);
              setIsRecognizing(true);
              recordingStartedAtRef.current = Date.now();
              track(EVENTS.RECORDING_STARTED, { type: 'save_only' });
              return;
            }

            if(language.value === null) {
              setAlert('Please select a transcription language!', 'error');
              return;
            }

            const diagnostics = createLiveStartupDiagnostics({ type: isTeleHealthActive ? 'telehealth' : 'in_person' });
            liveStartupDiagnosticsRef.current = diagnostics;
            const startupStartedAt = performance.now();
            setStartupStage('Preparing consultation and microphone…');
            let systemStream;
            let micStream;
            let mixedContext;
            let mixedStream;
            let audioStream;
            let bufferedAudio;
            let startupAccepted = false;
            const captureAbort = new AbortController();
            let released = false;
            const meetingListeners = [];
            const owner = getCaptureOwner();
            const releasePreparedAudio = () => {
              released = true;
              captureAbort.abort();
              bufferedAudio?.dispose();
              if (liveBufferedAudioRef.current === bufferedAudio) liveBufferedAudioRef.current = null;
              if (!startupAccepted) {
                liveInputEndedRef.current = null;
                discardStartupRecording();
                if (recordingPageMountedRef.current) setStartupStage('Audio capture stopped — finishing setup…');
              }
              meetingListeners.forEach(([track, listener]) => track.removeEventListener?.('ended', listener));
              owner.release(micStream);
              owner.release(systemStream);
              owner.release(mixedStream);
              if (mixedContext) { void mixedContext.close().catch(() => {}); mixedContext = null; }
            };
            liveAudioCleanupRef.current = releasePreparedAudio;
            // Independent setup starts together after the explicit click. Do not
            // open a metered session until BOTH setup operations have succeeded.
            const preparation = await Promise.allSettled([
              (async () => {
                // Continue must retain the browser click for the share picker;
                // save the existing text here, concurrently with audio selection.
                if (isTeleHealthActive && currentConsultation && !await saveTextOnly()) {
                  throw new Error('Could not save your current transcript. Retry saving before continuing.');
                }
                const consultation = await diagnostics.run(currentConsultation ? 'consultation_reuse' : 'consultation_create',
                  () => currentConsultation || insertConsultation(), Boolean);
                if (!consultation) throw new Error('Could not prepare the consultation. Please try again.');
                return consultation;
              })(),
              (async () => {
                if (isTeleHealthActive) {
                  systemStream = await diagnostics.run('call_audio', () => captureSystemAudio(diagnostics), Boolean);
                  if (!systemStream) throw new Error('Select the Google Meet tab and enable Share tab audio, then try again.');
                  let sharingEnded = false;
                  const onSharingEnded = () => {
                    if (released || sharingEnded) return;
                    sharingEnded = true;
                    setLiveRecognitionInterrupted(true);
                    setAlert('Google Meet sharing ended. Transcription is stopping; your transcript so far is kept. Share the Meet tab again to continue.', 'warning');
                    if (recordingOperationRef.current) {
                      meetingInterruptedRef.current = true;
                      releasePreparedAudio();
                    } else if (recognizingRef.current) {
                      void meetingStopRef.current();
                    }
                  };
                  systemStream.getTracks().forEach(track => {
                    track.addEventListener?.('ended', onSharingEnded);
                    meetingListeners.push([track, onSharingEnded]);
                  });
                  if (released) { owner.release(systemStream); throw new Error('Audio capture was cancelled'); }
                }
                micStream = await diagnostics.run('microphone', () => captureMicrophoneAudio(diagnostics), Boolean);
                if (!micStream) throw new Error('Could not access the selected microphone. Check its permission and try again.');
                if (released) { owner.release(micStream); throw new Error('Audio capture was cancelled'); }
                audioStream = micStream;
                if (isTeleHealthActive) {
                  if (!systemStream.getAudioTracks().some(track => track.readyState !== 'ended')) throw new Error('Tab sharing ended before audio capture started.');
                  mixedContext = new AudioContext();
                  await mixedContext.resume?.();
                  audioStream = mixedStream = owner.own(combineAudioStreams(systemStream, micStream, mixedContext));
                }
                // Save mode and provider input start from the same source before any
                // network wait. PCM is memory-only in no-save mode.
                if (shouldSaveAudio() && !await diagnostics.run('recorder', () => startAudioRecording(audioStream, diagnostics), Boolean)) {
                  throw new Error('Could not start audio recording. Please try again.');
                }
                bufferedAudio = await diagnostics.run('audio_buffer', () => createBufferedLiveAudio(audioStream, {
                  signal: captureAbort.signal,
                  onError: error => {
                    if (startupAccepted && recognizingRef.current) {
                      void liveConnectionLostRef.current();
                    } else {
                      releasePreparedAudio();
                      if (recordingPageMountedRef.current) setAlert(error.message, 'error');
                    }
                  },
                }));
                liveBufferedAudioRef.current = bufferedAudio;
                await diagnostics.run('audio_capture', () => bufferedAudio.ready);
                if (released) throw new Error('Audio capture was cancelled');
                setStartupStage('Recording audio — preparing transcription…');
              })(),
            ]);
            const preparationFailure = preparation.find(result => result.status === 'rejected');
            if (preparationFailure) {
              releasePreparedAudio();
              setAlert(preparationFailure.reason?.message || 'Could not prepare transcription. Please try again.', 'error');
              return;
            }
            setNoSpeechDetected(false);
            setHasCompletedTranscription(false);
            setLiveRecognitionInterrupted(false);
            handleLeftViewChange('Transcription');
            const preparationReadyAt = performance.now();
            if (released || !recordingPageMountedRef.current) { releasePreparedAudio(); return; }

            // Server opens and persists the wallet-backed session before returning a token.
            // A refusal here (402 out of credits, 409 session already open) must
            // release the microphone and tell the user — an uncaught rejection
            // leaves the mic captured with no feedback.
            let tokenObj;
            try {
              setStartupStage('Recording audio — starting transcription session…');
              tokenObj = await diagnostics.run('session', () => openLiveSession(diagnostics));
            } catch (error) {
              setLiveRecognitionInterrupted(true);
              releasePreparedAudio();
              const detail = error?.message || '';
              if (/out of credits/i.test(detail)) {
                setAlert('Not enough credits to start live transcription. Please top up your wallet or upgrade your plan.', 'error');
              } else {
                setAlert(`Could not start live transcription: ${detail || 'unknown error'}`, 'error');
              }
              return;
            }
            if (!recordingPageMountedRef.current || released) {
              meetingInterruptedRef.current = false;
              releasePreparedAudio();
              liveSessionRef.current = tokenObj.sessionId;
              rememberLiveSession(userIdRef.current, tokenObj.sessionId);
              try {
                await settleCurrentLiveSession({ rethrow: true });
              } catch (_) {
                // Keep the ID for Stop/reload recovery if sharing ends while
                // the server is opening the session and settlement fails.
                if (recordingPageMountedRef.current) setIsRecognizing(true);
                setAlert('Sharing ended, but the transcription session could not be settled. Please tap Stop again before leaving.', 'error');
              }
              return;
            }
            const sessionReadyAt = performance.now();
            setStartupStage('Recording audio — connecting to speech service…');
            diagnostics.begin('azure_connect');
            liveSessionRef.current = tokenObj.sessionId;
            // Remember the OPEN session id so a refresh, a closed tab or an
            // offline Stop can still settle it later with this same id, instead of
            // leaving it OPEN until the 180s server sweep. Forgotten once settled.
            rememberLiveSession(userIdRef.current, tokenObj.sessionId);
            setLiveSync(null);
            const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
            speechConfig.speechRecognitionLanguage = language.value;

            observeDiagnosticStream(audioStream, 'azure:input');
            const pcmStream = speechsdk.AudioInputStream.createPushStream(
              speechsdk.AudioStreamFormat.getWaveFormatPCM(LIVE_PCM_RATE, 16, 1));
            bufferedAudio.attach(pcmStream);
            const audioConfig = speechsdk.AudioConfig.fromStreamInput(pcmStream);
            const newRecognizer = new speechsdk.ConversationTranscriber(speechConfig, audioConfig);
            activeRecognizerRef.current = newRecognizer;
            // Diagnostics for the silent mid-session stop: Azure renegotiates its
            // websocket roughly every 10 minutes, and a reconnect that dies on an
            // expired token is otherwise invisible (no canceled event). Logging the
            // connection cycle makes the failure point visible in the console.
            liveMonitorRef.current?.dispose();
            const liveMonitor = createLiveConnectionMonitor({
              onWarning: () => setLiveConnectionWarning(true),
              onRecovered: () => setLiveConnectionWarning(false),
              onLost: reason => {
                console.warn('[live] connection lost:', reason);
                void liveConnectionLostRef.current();
              },
            });
            liveMonitorRef.current = liveMonitor;
            const liveConnection = speechsdk.Connection.fromRecognizer(newRecognizer);
            liveConnection.connected = () => {
              console.log('[live] azure connected', new Date().toISOString());
              liveMonitor.sdkConnected();
            };
            liveConnection.disconnected = () => {
              console.log('[live] azure disconnected', new Date().toISOString());
              liveMonitor.sdkDisconnected();
            };
            let resolveInputEnded;
            liveInputEndedRef.current = new Promise(resolve => { resolveInputEnded = resolve; });
            newRecognizer.sessionStopped = () => {
              console.log('[live] azure session stopped', new Date().toISOString());
              resolveInputEnded();
            };

            setRecognizer(newRecognizer);
            setHasCompletedTranscription(false);

            // No cookie/global token cache. The server's last_seen_at is the billing bound.
            // Each heartbeat also returns a fresh Azure speech token for this still-OPEN
            // session; reassigning it keeps the recognizer alive past Azure's 10-minute
            // token expiry (without this, transcription silently dies at ~10-15 min when
            // the SDK's periodic websocket reconnect presents the expired token).
            liveHeartbeatInterval = setInterval(() => {
              if (!liveSessionRef.current) return;
              heartbeatLiveSession(liveSessionRef.current)
                .then((data) => {
                  liveMonitor.heartbeatSucceeded();
                  if (data && data.speech_token) {
                    newRecognizer.authorizationToken = data.speech_token;
                    console.log('[live] fresh speech token applied', new Date().toISOString());
                  } else {
                    console.warn('[live] heartbeat returned no speech token — backend predates the token-refresh fix?');
                  }
                })
                .catch(error => {
                  console.error('Live heartbeat failed:', error);
                  liveMonitor.heartbeatFailed(error);
                });
            }, 60 * 1000);

            const previousTimes = [...transcriptionRef.current.matchAll(/\[(\d{2}):(\d{2}):(\d{2})\]/g)];
            const lastTime = previousTimes.at(-1);
            const continuationOffset = lastTime ? Number(lastTime[1]) * 3600 + Number(lastTime[2]) * 60 + Number(lastTime[3]) + 1 : 0;
            // Partial results arrive while someone is still speaking, so they
            // clear the no-speech warning sooner than the final result would.
            newRecognizer.transcribing = (s, e) => {
              if (e.result?.text?.trim()) liveNoSpeechWatchdog?.heard();
            };
            newRecognizer.transcribed = (s, e) => {
              if(e.result.reason === ResultReason.RecognizedSpeech) {
                if (e.result.text?.trim()) liveNoSpeechWatchdog?.heard();
                // Get timing information from the result
                const offsetInSeconds = (e.result.offset || 0) / 10000000;
                const timestamp = formatTimestamp(offsetInSeconds + continuationOffset);
                
                const previous = transcriptionRef.current;
                const updated = previous
                  ? `${previous}\n\n[${timestamp}] ${e.result.speakerId}\n${e.result.text}`
                  : `[${timestamp}] ${e.result.speakerId}\n${e.result.text}`;
                // Stop can be acknowledged in the same SDK event as the final
                // utterance. Persist from the ref before React's next render.
                transcriptionRef.current = updated;
                setTranscription(updated);
                // No per-utterance report generation: /scribe/reports is
                // metered and should run only after explicit user action.
              }
            };
            
            newRecognizer.canceled = (s, e) => {
              audioDiagnostic('azure:cancelled', { reason: e.reason, code: e.errorCode });
              console.error(`CANCELED: Reason=${e.reason}`);
              if (e.reason === speechsdk.CancellationReason.Error) {
                console.error(`CANCELED: ErrorCode=${e.errorCode}`);
                console.error(`CANCELED: ErrorDetails=${e.errorDetails}`);
                // While Live runs this stops it with the connection-lost message.
                if (recognizingRef.current) { liveMonitor.fatalError(); return; }
                setLiveRecognitionInterrupted(true);

                if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
                stopLiveNoSpeechWatchdog();
                settleCurrentLiveSession();
                // Recording appears to continue after a canceled recognizer, so the
                // user must be told nothing more is being transcribed.
                setAlert('Live transcription stopped unexpectedly. Please stop the recording, then start again — your transcript so far is kept.', 'error');
              }
            };

            // Do not announce a live session before Azure confirms that it is
            // listening. The former UI started its timer during connection setup,
            // inviting the user to speak into an unready recognizer.
            try {
              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  diagnostics.end('azure_connect', 'timeout', 'startup_timeout');
                  reject(new Error('Speech recognition startup timed out'));
                }, 20000);
                newRecognizer.startTranscribingAsync(
                  () => { clearTimeout(timeout); resolve(); },
                  error => { clearTimeout(timeout); reject(error); },
                );
              });
              diagnostics.end('azure_connect', 'completed');
            } catch (error) {
              diagnostics.end('azure_connect', 'failed', 'connection_failed');
              setLiveRecognitionInterrupted(true);
              console.error('Failed to start continuous transcription:', error);
              if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
              stopLiveNoSpeechWatchdog();
              releasePreparedAudio();
              activeRecognizerRef.current = null;
              try { newRecognizer.close(); } catch (closeError) { console.error(closeError); }
              setRecognizer(null);
              await settleCurrentLiveSession();
              setAlert('Live transcription could not start. Please try again after checking your microphone and connection.', 'error');
              return;
            }
            if (!recordingPageMountedRef.current || released) {
              if (liveHeartbeatInterval) { clearInterval(liveHeartbeatInterval); liveHeartbeatInterval = null; }
              try { newRecognizer.close(); } catch (_) {}
              activeRecognizerRef.current = null;
              releasePreparedAudio();
              await settleCurrentLiveSession();
              setRecognizer(null);
              return;
            }
            bufferedAudio.markLive();
            startupAccepted = true;
            diagnostics.finish('completed');
            // Timings contain no client data, audio or tokens. They separate
            // local preparation, backend response and Azure connection latency.
            console.info('[live] startup timings (ms)', {
              preparation: Math.round(preparationReadyAt - startupStartedAt),
              session: Math.round(sessionReadyAt - preparationReadyAt),
              connection: Math.round(performance.now() - sessionReadyAt),
            });
            setIsRecognizing(true);
            recordingStartedAtRef.current = Date.now();
            // Same banner as after Stop, but shown while still recording so the
            // microphone or language can be fixed before the session is lost.
            stopLiveNoSpeechWatchdog();
            liveNoSpeechWatchdog = createNoSpeechWatchdog({
              onSilence: () => setNoSpeechDetected(true),
              onSpeech: () => setNoSpeechDetected(false),
            });
            track(EVENTS.RECORDING_STARTED, {
              type: isTeleHealthActive ? 'telehealth' : 'in_person',
              language: language?.value || undefined,
            });
        }
    }


    function formatTimestamp(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    // Consultation management
    const insertConsultation = async () => {
      const createdAt = new Date().toISOString();
      const chiefComplaint = `Consultation - ${convertToGMT8Date(createdAt)}`;

      const { data, error } = await supabase.from('consultations').insert([{
        id: uuidv4(),
        patient_id: pastConsultations.length ? pastConsultations[0].patient_id : id,
        transcription: null,
        notes: null,
        chiefComplaint,
        createdAt,
        created_by: userDetails?.name || 'Unknown User',
        created_by_user_id: userDetails?.id || null,
      }]).select('*');
      
      if (error) {
        console.error('Error upserting post:', error);
        return null;
      }
      else {
        setCurrentConsultation({ ...data[0], chiefComplaint });
        setPastConsultations((prev) => [{ ...data[0], chiefComplaint }, ...prev]);
        const consultationItem = {
          id: data[0].id,
          createdAt: createdAt,
          title: chiefComplaint,
          type: 'Consultation',
          originalData: data[0],
          createdBy: userDetails?.name || 'Unknown User',
          createdByUserId: userDetails?.id || null,
          isSelected: false,
          parentId: null,
          isSubItem: false,
          isShared: false,
        }
        setHistoryItems([consultationItem, ...historyItems]);
        if(firstView){
          setFirstView(false);
        }
        setPopupReport(false);
        return data[0];
      }
    };

    // Resets the UI to an empty consultation state WITHOUT creating a Supabase record.
    // Used when switching to the Live workflow or clicking "New Consultation" from the UI.
    const resetConsultationUI = () => {
      if (refinementBusyRef.current || !confirmNavigation()) return false;
      // A finished connection-lost notice belongs to the old transcript. A
      // pending one (still saving/settling) blocks leaving via confirmNavigation.
      if (liveSyncRef.current?.done) setLiveSync(null);
      // Leaving the consultation: index edits from the last 30 s while the
      // refs still hold its text (they are cleared below).
      indexConsultationTranscript(undefined, { requireContent: true });
      setCurrentConsultation(null);
      setMainView('Consultation');
      setLeftView('Transcription');
      setRightOptions([
        { value: defaultTemplate ? defaultTemplate.title : 'Select Template', label: defaultTemplate ? defaultTemplate.title : 'Select Template' },
        // { value: 'Medical Coding', label: 'Medical Coding' },
      ]);
      setRightView(defaultTemplate ? defaultTemplate.title : 'Select Template');
      setFiles([]);
      setTranscription('');
      setRemarks('');
      sectionSummaryIdRef.current = null;
      setSectionSummary({ id: null, content: "" });
      setAddedCodes([]);
      setSuggestedCodes([]);
      setEditNote(false);
      setEditTranscription(false);
      setEditRemarks(false);
      clearHighlights();
      handleMenuClose();
      setTimer(0);
      transcriptionRef.current = '';
      remarksRef.current = '';
      setRefineTranscriptError(false);
      setRefineTranscriptFailure(null);
      setNoSpeechDetected(false);
      setHasCompletedTranscription(false);
      setIsPendingRefinement(false);
      setRefinedPreview('');
      setPopupReport(false);
      return true;
    };

    const resetNewConsultationWorkspace = () => {
      setMainView('Consultation');
      setLeftView('Transcription');
      setRightOptions(
        [
          { value: defaultTemplate ? defaultTemplate.title : 'Select Template', label: defaultTemplate ? defaultTemplate.title : 'Select Template' },
          // { value: 'Medical Coding', label: 'Medical Coding' },
        ]
      );
      setRightView(defaultTemplate ? defaultTemplate.title : 'Select Template');
      setFiles([]);
      setTranscription('');
      setRemarks('');
      sectionSummaryIdRef.current = null;
      setSectionSummary({ id: null, content: "" });
      setAddedCodes([]);
      setSuggestedCodes([]);
      setEditNote(false);
      setEditTranscription(false);
      setEditRemarks(false);
      clearHighlights();
      handleMenuClose();
      setTimer(0);
      transcriptionRef.current = '';
      remarksRef.current = '';
      setRefineTranscriptError(false);
      setRefineTranscriptFailure(null);
      setIsPendingRefinement(false);
      setRefinedPreview('');
    };

    const addNewConsultation = () => {
      indexConsultationTranscript(undefined, { requireContent: true });
      const newConsultation = insertConsultation();
      resetNewConsultationWorkspace();
      return newConsultation;
    };

    const viewPastConsultation = async (index, item) => {
      try {
        let latestReport = null;
        setMainView('Consultation');
        setLeftView('Transcription');

        if(item.type === 'Consultation') {
          // Always start with an empty right panel — never pre-load a previous report.
          // A report will appear only when the user generates a new one this session.
          // latestReport stays null → fetchSectionSummary(id, null) resets right panel to empty state.
          setRightOptions([
            { value: selectedTemplate?.title || defaultTemplate?.title || 'Select Template', label: selectedTemplate?.title || defaultTemplate?.title || 'Select Template' },
          ]);
          setRightView(selectedTemplate?.title || defaultTemplate?.title || 'Select Template');
        } else if(item.type === 'Document') {
          latestReport = item;
          setRightOptions(
            [
              { value: item.title, label: item.title },
              // { value: 'Medical Coding', label: 'Medical Coding' },
            ]
          );
          setRightView(item.title);
        }
        //set right options and right view
        //if consultation has at least one report or more, set the right view to the first report
        //otherwise set it to the default template
        // setRightView(defaultTemplate ? defaultTemplate.title : 'Select Template');
        setLatestConsultationReport(latestReport);
        setConsultationLoading(true);
        setEditTranscription(false);
        setEditRemarks(false);
        setFirstView(false);
        handleMenuClose();
        await viewUploadedFiles(pastConsultations[index].id);
        await fetchSectionSummary(pastConsultations[index].id, latestReport);
        setCurrentConsultation(pastConsultations[index]);
        setNoSpeechDetected(false);
        setHasCompletedTranscription(true);
        setTranscription(pastConsultations[index].transcription);
        setRemarks(pastConsultations[index].notes);
      } catch (error) {
        console.error('Error viewing past consultation:', error);
        setAlert('Failed to load consultation data', 'error');
      } finally {
        setConsultationLoading(false);
      }
    };

    // Write removes only the selected transcript and preserves generated reports.
    // Legacy consultation deletion also removes its reports and index entries.
    const removeConsultation = async (originalData, { preserveReports = false, refresh = true } = {}) => {
      const consultationId = originalData.id;
      // A late idle/leave flush must not re-create the document after this.
      deletedConsultationIdsRef.current.add(consultationId);

      const postDeleteEmbedding = (formData) =>
        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_embedding`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).catch(err => {
          console.error('Background error deleting embedding:', err);
        });

      const { data: reports, error: reportsError } = preserveReports ? { data: [] } : await supabase
        .from('generatedreports').select('id').eq('consultationId', consultationId);
      if (reportsError) {
        console.error('Error listing consultation reports:', reportsError);
      }
      for (const report of reports ?? []) {
        const { error: reportError } = await supabase.from('generatedreports').delete().eq('id', report.id);
        if (reportError) {
          console.error('Error deleting report:', reportError);
          continue;
        }
        const formData = new FormData();
        formData.append('patient_id', id);
        formData.append('document_type', 'report');
        formData.append('report_id', report.id);
        formData.append('team_id', currentOrg.id);
        postDeleteEmbedding(formData);
      }

      const formDataTranscript = new FormData();
      formDataTranscript.append('patient_id', id);
      formDataTranscript.append('document_type', 'transcript');
      formDataTranscript.append('consultation_id', consultationId);
      formDataTranscript.append('team_id', currentOrg.id);
      postDeleteEmbedding(formDataTranscript);

      const { error } = await supabase.from('consultations').delete().eq('id', consultationId);
      if (error) {
        deletedConsultationIdsRef.current.delete(consultationId);
        throw error;
      }
      else if (refresh) {
        await fetchHistoryItems();
      }
    };

    // File management
    const onInputClick = (event) => {
        event.target.value = '';
    };
  
    const handleFileChange = (event) => {
      const uploadedFiles = Array.from(event.target.files);
      setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
      setEditNote(true);
    };

    const removeFile = async (fileToRemove) => {
      const filePath = `${id}/${currentConsultation.id}/${fileToRemove.name}`;
    
      const { data: existingFiles, error: listError } = await supabase.storage.from('tikcare-attachments').list(`${id}/${currentConsultation.id}`);
    
      if (listError) {
        setAlert('Error checking file existence!', 'error');
        return;
      }
      const fileExists = existingFiles.some(file => file.name === fileToRemove.name);
      if (!fileExists) {
        setFiles(files.filter((file) => file !== fileToRemove));
        return;
      }
    
      const { error } = await supabase.storage.from('tikcare-attachments').remove([filePath]);
    
      if (error) {
        setAlert('Error removing file!', 'error');
      } else {
        const { error: metadataError } = await supabase
          .from('file_metadata')
          .delete()
          .eq('file_path', filePath);

        if (metadataError) {
          console.error('Error deleting file metadata:', metadataError);
        }
        
        setFiles(files.filter((file) => file !== fileToRemove));
        setHistoryItems(historyItems.filter((item) => item.id !== `file-${currentConsultation.id}-${fileToRemove.name}`));
        setAlert('File removed successfully!', 'success');
      }
    };

    const downloadFile = async (file) => {
      try {
        setIsDownloading(true);
        
        const { data, error } = await supabase.storage
          .from('tikcare-attachments')
          .download(`${id}/${currentConsultation.id}/${file.name}`);
          
        if (error) {
          throw error;
        }

        const key = await deriveKey(id);
        const decryptedBlob = await decryptFile(data, key, file.name);
        
        const url = window.URL.createObjectURL(decryptedBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading file:', error);
        setAlert('Failed to download file', 'error');
      } finally {
        setIsDownloading(false);
      }
    };

    const handleFileSubmit = async () => {
      try {
        const key = await deriveKey(id);
        const uploadableFiles = files.filter(f => f instanceof File);
        const batch = createBatchProgress(setFileUploadProgress);
        const uploadPromises = uploadableFiles.map(async (file) => {
          // Storage keys must be ASCII; the real name is kept in file_metadata.file_name.
          const storageName = toStorageKeySegment(file.name);
          const filePath = `${id}/${currentConsultation.id}/${storageName}`;
          const encrypted = await encryptFile(file, key, storageName);
          batch.add(filePath, encrypted.size);
          try {
            await uploadWithProgress('tikcare-attachments', filePath, encrypted, {
              cacheControl: '3600',
              upsert: true,
              onProgress: batch.onProgress(filePath),
            });
          } catch (error) {
            console.error('Error uploading file:', error);
            const msg = (error.message && error.message.startsWith('Invalid key')) ?
            'Upload failed: file name may have special characters. Please rename and try again.' :
            'Error saving files! Please ensure file names are unique or valid.';
            throw new Error(msg);
          }

          // Save file metadata to new table
          const { error: metadataError } = await supabase
            .from('file_metadata')
            .insert({
              file_path: filePath,
              uploaded_by: userDetails.id,
              uploader_name: userDetails.name,
              patient_id: id,
              consultation_id: currentConsultation.id,
              file_name: file.name,
              file_size: file.size
            });
          
          if (metadataError) {
            console.error('Error saving file metadata:', metadataError);
          }
        });

        try {
          await Promise.all(uploadPromises);
        } finally {
          batch.done();
        }
        await viewUploadedFiles(currentConsultation.id);
        await fetchHistoryItems();
      } catch (err) {
        console.error('Error handling file submit:', err);
        setAlert(err.message || 'Error saving files! Please try again.', 'error');
      }
    };

    const viewUploadedFiles = async (fileID) => {
      try {
        const { data, error } = await supabase.storage.from('tikcare-attachments').list(`${id}/${fileID}`, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });
    
        if (error) {
          console.error('Error fetching files:', error);
          return;
        }
        setFiles(data);
      } catch (err) {
        console.error('Unexpected error fetching files:', err);
      }
    };
    
    // Audio functions
    async function captureSystemAudio(diagnostics) {
      const owner = getCaptureOwner();
      try {
        if (!supportsMeetingCapture()) throw new Error('Use desktop Google Chrome to transcribe a Google Meet tab.');
        const stream = owner.own(await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' },
          audio: true,
          systemAudio: 'exclude',
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'exclude',
        }));
        const surface = stream.getVideoTracks?.()[0]?.getSettings?.().displaySurface;
        if ((surface && surface !== 'browser') || !stream.getAudioTracks().some(track => track.readyState !== 'ended')) {
          owner.release(stream);
          throw new Error('Select the Google Meet tab (not a window or screen) and enable Share tab audio. No transcription has started.');
        }
        setSystemStream(stream);
        return stream;
      } catch (error) {
        diagnostics?.fail('call_audio', error);
        if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
          throw new Error('Tab sharing was cancelled or blocked. Start again, select the Google Meet tab and enable Share tab audio.');
        }
        throw error;
      }
    }
  
    async function captureMicrophoneAudio(diagnostics) {
      try {
        const micStream = await getCaptureOwner().acquire({
          audio: { deviceId: selectedMicId, ...(shouldSaveAudio() ? { channelCount: 1 } : {}) },
        });
        observeDiagnosticStream(micStream, 'live:microphone', selectedMicId);
        setMicStream(micStream);
        return micStream;
      } catch (error) {
        diagnostics?.fail('microphone', error);
        console.error("Error capturing microphone audio:", error);
        return null;
      }
    }
  
    function combineAudioStreams(systemStream, micStream, audioContext) {
      const destination = audioContext.createMediaStreamDestination();
      
      if (systemStream) {
        const systemSource = audioContext.createMediaStreamSource(systemStream);
        systemSource.connect(destination);
      }
    
      if (micStream) {
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(destination);
      }
    
      return destination.stream;
    }

    async function getMicrophones() {
      let stream;
      const owner = getCaptureOwner();
      try {
        stream = await owner.acquire({audio: true});
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!recordingPageMountedRef.current) return;
        const audioDevices = devices.filter(device => (device.kind === 'audioinput' && device.deviceId !== 'default' && device.deviceId !== 'communications'));
        const options = audioDevices.map(device => ({ value: device.deviceId, label: device.label }));
        setInputSources(options);
        // Pre-select the browser's active/default microphone (the device getUserMedia resolved to)
        const activeDeviceId = stream.getAudioTracks()[0]?.getSettings().deviceId;
        const defaultOption = options.find(option => option.value === activeDeviceId) || options[0];
        if (defaultOption) {
          setInputSource(prev => prev || defaultOption);
          setSelectedMicId(prev => prev ?? defaultOption.value);
        }
      } catch (error) {
        console.error('Error getting media devices:', error);
      } finally {
        owner.release(stream);
      }
    }

    const convertToGMT8Date = (utcTimeStamp) => {
      const date = new Date(utcTimeStamp);
      const formattedDate = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric'})
      return formattedDate;
    }

    const convertToGMT8Time = (utcTimeStamp) => {
      const date = new Date(utcTimeStamp);
      const formattedTime = date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return formattedTime;
    }

    // Generate a color for highlighting based on index
    const generateHighlightColor = (index) => {
      const hue = (index * 137.508) % 360;
      return `hsl(${hue}, 70%, 80%)`;
    };

    // Fetch chat history from Supabase
    const fetchChatMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('textmessages')
          .select('*')
          .eq('patient_id', id)
          .order('created_at', { ascending: true });
          
        if (error) {
          console.error('Error fetching chat messages:', error);
          return;
        }
        
        if (data && data.length > 0) {
          // Decrypt the messages
          const decryptionPromises = data.map(async (message) => {
            const [
              messageResult,
              senderidResult,
              receiveridResult,
              fileResult,
              sourcesResult
            ] = await Promise.all([
              supabase.rpc('decrypt_text', { encrypted_data: message.message }),
              supabase.rpc('decrypt_text', { encrypted_data: message.senderid }),
              supabase.rpc('decrypt_text', { encrypted_data: message.receiverid }),
              message.file 
                ? supabase.rpc('decrypt_text', { encrypted_data: message.file })
                : Promise.resolve({ data: null, error: null }),
              message.sources
                ? supabase.rpc('decrypt_text', { encrypted_data: message.sources })
                : Promise.resolve({ data: '[]', error: null }),
            ]);

            if (messageResult.error || senderidResult.error || receiveridResult.error || fileResult.error || sourcesResult.error) {
              return message;
            }

            message.message = messageResult.data;
            message.senderid = senderidResult.data;
            message.receiverid = receiveridResult.data;
            message.file = fileResult.data;

            try {
              message.sources = sourcesResult.data ? JSON.parse(sourcesResult.data) : [];
            } catch (parseError) {
              console.error('Failed to parse sources JSON:', parseError);
              message.sources = [];
            }

            return message;
          });

          const decryptedMessages = await Promise.all(decryptionPromises);
          setChatMessages(decryptedMessages);
        } 
      } catch (error) {
        console.error('Error in fetchChatMessages:', error);
      }
    };

    const formatChatMessages = () => {
      if (!chatMessages || chatMessages.length === 0) {
        return [];
      }
      
      // An answer that never completed is not history — see isCompletedTurn.
      return chatMessages
        .filter(isCompletedTurn)
        .map(message => {
          return {
            type: message.isUser ? 'user' : 'ai',
            message: message.message,
            sender_id: message.senderid,
            patient_id: id,
            created_at: message.created_at
          };
        });
    };

    // Blocking transport, using the same report context as the streaming path.
    const handleSendMessageBlocking = async (options) => {
      setIsAnswerSourceOpen(false);
      if (!newMessage.trim()) return;
      setResponseSources([]);
      
      // Add user message to the chat
      const userMessage = {
        message: newMessage,
        isUser: true,
        created_at: new Date().toISOString(),
        senderid: userDetails.id,
        receiverid: 'ai-assistant',
        patient_id: id
      };
      
      setChatMessages(prev => [...prev, userMessage]);
      const currentMessage = newMessage;
      setNewMessage('');
      
      try {
        // Save message to Supabase
        const { error } = await supabase
          .from('textmessages')
          .insert([userMessage]);
          
        if (error) {
          console.error('Error saving user message:', error);
        }
    
        // Add processing message to chat
        setIsMessageProcessing(true);
        const processingMessageId = Date.now();
        const processingMessage = {
          id: processingMessageId,
          message: "Let me think...",
          isUser: false,
          created_at: new Date().toISOString(),
          senderid: 'ai-assistant',
          receiverid: userDetails.id,
          patient_id: id,
          isProcessing: true
        };
        
        setChatMessages(prev => [...prev, processingMessage]);
        const formattedMessages = formatChatMessages();
        let params = {
          question: currentMessage,
          chat_history: formattedMessages,
          patient_id: id,
          team_id: currentOrg.id,
        }
        const legacyReportId = mainView === 'Consultation' && !firstView
          ? isPopupReport ? selectedReport?.id : sectionSummary?.id : null;
        params.report_id = sophieRequestReportId(options, legacyReportId, answerSource);
        if (options?.sourceSelection !== undefined) params.source_selection = options.sourceSelection;

        try {
          // console.log('The popup report:', isPopupReport);
          // console.log('The selected report:', selectedReport);
          // console.log('The current consultation:', currentConsultation);
          // console.log('The params:', params);

          const response = await axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/askanything`, params, {
            headers: {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*'
            },
            withCredentials: false
          });
          // console.log('AI response:', response);
          
          // Remove processing message
          setIsMessageProcessing(false);
          setChatMessages(prev => prev.filter(msg => msg.id !== processingMessageId));
          
          // Check for successful status
          if (response.status === 200) {
            if(response.data.report_content && response.data.report_id) {
              if (Object.prototype.hasOwnProperty.call(options, 'reportContext')) {
                await saveSophieWriteReport(response.data, options, supabase, setHistoryItems);
              } else if (isPopupReport) {
                const updatedContent = response.data.report_content;
                setSelectedReport({
                  ...selectedReport,
                  originalData: {
                    ...selectedReport.originalData,
                    content: updatedContent
                  }
                });
                setEditReportContent(true);
                // Persist to database
                await supabase
                  .from('generatedreports')
                  .update({ content: updatedContent, updated_at: new Date().toISOString() })
                  .eq('id', selectedReport.id);
                // Keep historyItems in sync so the All Files preview reflects the change
                setHistoryItems(prev => prev.map(item =>
                  item.id === selectedReport.id
                    ? { ...item, originalData: { ...item.originalData, content: updatedContent } }
                    : item
                ));
              } else {
                setSectionSummary({
                  ...sectionSummary,
                  content: response.data.report_content
                });
                setEditNote(true);
                // Persist to database + refresh RAG embedding
                await saveReport(response.data.report_content);
                // Keep historyItems in sync so the All Files preview reflects the change
                setHistoryItems(prev => prev.map(item =>
                  item.id === sectionSummary.id
                    ? { ...item, originalData: { ...item.originalData, content: response.data.report_content } }
                    : item
                ));
              }
            }

            if(response.data?.sources && response.data.sources.length > 0) {
              setResponseSources(response.data.sources);
            } 
            // Add AI response to chat
            const aiMessage = {
              message: (response.data.answer || '').replace(/Please use the ['']save[''] option to confirm these changes\.?/gi, '').trim(),
              isUser: false,
              created_at: new Date().toISOString(),
              senderid: 'ai-assistant',
              receiverid: userDetails.id,
              patient_id: id,
              sources: response.data.sources || [],
            };
            
            setChatMessages(prev => [...prev, aiMessage]);
            
            // Save AI message to Supabase
            await supabase
              .from('textmessages')
              .insert([aiMessage]);
          } else {
            throw new Error('Failed to get a successful response from AI service');
          }
        } catch (apiError) {
          console.error('API request failed:', apiError);
          
          // Remove processing message
          setIsMessageProcessing(false);
          setChatMessages(prev => prev.filter(msg => msg.id !== processingMessageId));
          
          throw apiError;
        }
          
      } catch (error) {
        console.error('Error in handleSendMessage:', error);
        
        // Show error message
        const errorMessage = {
          message: "Sorry, I encountered an error processing your request. Please try again.",
          isUser: false,
          created_at: new Date().toISOString(),
          senderid: 'ai-assistant',
          receiverid: userDetails.id,
          patient_id: id
        };
        
        // Replace processing message with error message if it exists
        setChatMessages(prev => {
          const filteredMessages = prev.filter(msg => !msg.isProcessing);
          return [...filteredMessages, errorMessage];
        });
    
        await supabase
          .from('textmessages')
          .insert([errorMessage]);
      }
    };

    // Token-by-token variant of handleSendMessageBlocking. Same turn, same
    // params, same persistence point — only the transport differs: a POST read
    // through fetch + SSE framing instead of one blocking axios call. See
    // tikcare_rag docs/STREAMING-DESIGN.md §3 and §6.
    const handleSendMessageStreaming = async (options) => {
      setIsAnswerSourceOpen(false);
      if (!newMessage.trim()) return;
      setResponseSources([]);

      // Add user message to the chat
      const userMessage = {
        message: newMessage,
        isUser: true,
        created_at: new Date().toISOString(),
        senderid: userDetails.id,
        receiverid: 'ai-assistant',
        patient_id: id
      };

      setChatMessages(prev => [...prev, userMessage]);
      const currentMessage = newMessage;
      setNewMessage('');

      const processingMessageId = Date.now();
      // The one bubble this turn owns: it starts as the "Let me think..."
      // placeholder, becomes the streaming answer, and is replaced by the
      // canonical answer on `done` (or marked cut off if `done` never arrives).
      const patchStreamingMessage = (patch) => {
        setChatMessages(prev => prev.map(msg => (msg.id === processingMessageId ? { ...msg, ...patch } : msg)));
      };

      try {
        // Save message to Supabase
        const { error } = await supabase
          .from('textmessages')
          .insert([userMessage]);

        if (error) {
          console.error('Error saving user message:', error);
        }

        // Add processing message to chat
        setIsMessageProcessing(true);
        const processingMessage = {
          id: processingMessageId,
          message: "Let me think...",
          isUser: false,
          created_at: new Date().toISOString(),
          senderid: 'ai-assistant',
          receiverid: userDetails.id,
          patient_id: id,
          isProcessing: true
        };

        setChatMessages(prev => [...prev, processingMessage]);
        const formattedMessages = formatChatMessages();
        let params = {
          question: currentMessage,
          chat_history: formattedMessages,
          patient_id: id,
          team_id: currentOrg.id,
        }
        const legacyReportId = mainView === 'Consultation' && !firstView
          ? isPopupReport ? selectedReport?.id : sectionSummary?.id : null;
        params.report_id = sophieRequestReportId(options, legacyReportId, answerSource);
        if (options?.sourceSelection !== undefined) params.source_selection = options.sourceSelection;

        try {
          // ragAxiosAuth.js patches axios only, so this fetch has to carry the
          // Supabase JWT itself or the RAG boundary answers 401.
          const headers = await ragStreamHeaders();

          const result = await streamAskSophie({
            url: `${RAG_STREAM_URL}${ASK_SOPHIE_STREAM_PATH}`,
            params,
            headers,
            // Stage labels only ever replace the placeholder copy, never the
            // answer: the backend sends every `status` before the first delta.
            onStage: (stage, label) => {
              const text = STAGE_LABELS[stage];
              if (!text) return; // unknown stage: leave the current label alone
              setChatMessages(prev => prev.map(msg => (
                msg.id === processingMessageId && msg.isProcessing
                  ? { ...msg, stage, message: text }
                  : msg
              )));
            },
            onDelta: (accumulated) => {
              patchStreamingMessage({ isProcessing: false, isStreaming: true, message: accumulated });
            },
            // The `report` event is deliberately not applied here. It repeats
            // in `done`, and the report patch below writes to generatedreports —
            // doing it twice would be two DB writes for one answer.
          });

          if (!shouldPersistAnswer(result)) {
            // No `done` event: truncated stream, or an error raised after the
            // 200 was already on the wire. Keep whatever text arrived — the
            // doctor may have read something useful — mark it unmistakably cut
            // off, and insert NOTHING into textmessages. A partial clinical
            // answer re-read later, out of context, with no status column to
            // warn anyone, reads as Sophie's finished opinion (§6.2).
            console.error('Ask Sophie stream did not complete:', result?.reason || result?.outcome, result?.error || '');
            setIsMessageProcessing(false);
            const partialText = (result?.partial || '').trim();
            patchStreamingMessage({
              isProcessing: false,
              isStreaming: false,
              isTruncated: true,
              // With zero deltas there is nothing to show, so fall back to the
              // backend's own user-facing sentence from the error event.
              message: partialText || result?.data?.answer || '',
            });
            return;
          }

          const data = result.data;

          // Replace the streamed bubble with the canonical answer: `done.answer`
          // is the whole answer and wins over the accumulated deltas.
          setIsMessageProcessing(false);
          setChatMessages(prev => prev.filter(msg => msg.id !== processingMessageId));

          if(data.report_content && data.report_id) {
            if (Object.prototype.hasOwnProperty.call(options, 'reportContext')) {
              await saveSophieWriteReport(data, options, supabase, setHistoryItems);
            } else if (isPopupReport) {
              const updatedContent = data.report_content;
              setSelectedReport({
                ...selectedReport,
                originalData: {
                  ...selectedReport.originalData,
                  content: updatedContent
                }
              });
              setEditReportContent(true);
              // Persist to database
              await supabase
                .from('generatedreports')
                .update({ content: updatedContent, updated_at: new Date().toISOString() })
                .eq('id', selectedReport.id);
              // Keep historyItems in sync so the All Files preview reflects the change
              setHistoryItems(prev => prev.map(item =>
                item.id === selectedReport.id
                  ? { ...item, originalData: { ...item.originalData, content: updatedContent } }
                  : item
              ));
            } else {
              setSectionSummary({
                ...sectionSummary,
                content: data.report_content
              });
              setEditNote(true);
              // Persist to database + refresh RAG embedding
              await saveReport(data.report_content);
              // Keep historyItems in sync so the All Files preview reflects the change
              setHistoryItems(prev => prev.map(item =>
                item.id === sectionSummary.id
                  ? { ...item, originalData: { ...item.originalData, content: data.report_content } }
                  : item
              ));
            }
          }

          if(data?.sources && data.sources.length > 0) {
            setResponseSources(data.sources);
          }
          // Add AI response to chat
          const aiMessage = {
            message: (data.answer || '').replace(/Please use the ['']save[''] option to confirm these changes\.?/gi, '').trim(),
            isUser: false,
            created_at: new Date().toISOString(),
            senderid: 'ai-assistant',
            receiverid: userDetails.id,
            patient_id: id,
            sources: data.sources || [],
          };

          setChatMessages(prev => [...prev, aiMessage]);

          // Save AI message to Supabase — only ever reached with a `done`
          // event in hand, so only a complete answer is ever persisted.
          await supabase
            .from('textmessages')
            .insert([aiMessage]);
        } catch (apiError) {
          // Pre-stream failures only (no session, network error, 4xx/5xx before
          // the first byte). Once the stream is open, failures come back as a
          // result above and never land here.
          console.error('API request failed:', apiError);

          // Remove processing message
          setIsMessageProcessing(false);
          setChatMessages(prev => prev.filter(msg => msg.id !== processingMessageId));

          throw apiError;
        }

      } catch (error) {
        console.error('Error in handleSendMessageStreaming:', error);

        // Show error message
        const errorMessage = {
          message: "Sorry, I encountered an error processing your request. Please try again.",
          isUser: false,
          created_at: new Date().toISOString(),
          senderid: 'ai-assistant',
          receiverid: userDetails.id,
          patient_id: id
        };

        // Replace processing message with error message if it exists
        setChatMessages(prev => {
          const filteredMessages = prev.filter(msg => !msg.isProcessing);
          return [...filteredMessages, errorMessage];
        });

        await supabase
          .from('textmessages')
          .insert([errorMessage]);
      }
    };

    // Both transports use the report captured when this question was sent.
    const handleSendMessage = async (options = {}) => {
      if (RAG_STREAM_ENABLED) return handleSendMessageStreaming(options);
      return handleSendMessageBlocking(options);
    };

    const saveChatMessageAsReport = async (messageContent) => {
      try {
        if (!messageContent || !messageContent.trim()) {
          setAlert('No content to save as report', 'error');
          return false;
        }

        setConsultationLoading(true);
        
        // Generate unique report ID and metadata
        const reportId = uuidv4();
        const reportDate = new Date().toISOString();
        const reportTitle = `AI Assistant Report - ${convertToGMT8Date(reportDate)}`;
        
        // Format the message content as HTML
        const formattedContent = `<p>${messageContent.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '</p><p>')}</p>`;
        
        // Save report to database
        const { error } = await supabase.from('generatedreports').insert({
          id: reportId,
          created_at: reportDate,
          updated_at: reportDate,
          patientId: id,
          consultationId: null,
          content: formattedContent,
          title: reportTitle,
          created_by: userDetails?.name || 'Unknown User',
          created_by_user_id: userDetails?.id || null,
          locked: false,
          documents: [],
          template_id: null
        }).select('*').single();
        
        if (error) throw error;
        
        // Create history item object
        const newReportItem = {
          id: reportId,
          createdAt: reportDate,
          title: reportTitle,
          type: 'Document',
          originalData: {
            content: formattedContent,
            consultationId: null,
            templateId: null
          },
          createdBy: userDetails?.name || 'Unknown User',
          createdByUserId: userDetails?.id || null,
          isSelected: false,
          parentId: null,
          isSubItem: false,
          isLocked: false,
          isShared: false,
          documents: []
        };
        
        // Add to RAG system for embedding
        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', formattedContent);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', reportTitle);
        
        // Add embedding in background (non-blocking)
        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).then(response => {
          if (response.data.status === 'failed') {
            console.error('Failed to add embedding for chat report');
          }
        }).catch(error => {
          console.error('Background embedding failed:', error);
        });
        
        // Update history items
        setHistoryItems(prev => [newReportItem, ...prev]);
        
        setAlert('Chat message saved as report successfully!', 'success');
        return true;
        
      } catch (error) {
        console.error('Error saving chat message as report:', error);
        setAlert('Failed to save message as report', 'error');
        return false;
      } finally {
        setConsultationLoading(false);
      }
    };

    const handleSaveChatMessageAsReport = (messageContent) => {
      setSaveReportDialog({
        open: true,
        messageContent: messageContent,
        onConfirm: () => saveChatMessageAsReport(messageContent)
      });
    };

    // Text selection is now handled by the floating "Highlight" button in MedicalScribeTranscription.js

    const flashHighlightNodes = (index) => {
      // Scroll to the first node of this chunk (has the id)
      const anchor = document.getElementById(`highlight-${index}`);
      if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Flash ALL nodes belonging to this chunk (share the same data-index)
      const allNodes = document.querySelectorAll(`[data-highlight="true"][data-index="${index}"]`);
      allNodes.forEach((node) => {
        node.style.transition = 'background-color 0.3s';
        const originalColor = node.style.backgroundColor;
        node.style.backgroundColor = '#ffff00';
        setTimeout(() => { node.style.backgroundColor = originalColor; }, 500);
      });
    };

    const navigateToNextHighlight = () => {
      if (highlightCount === 0) return;
      const nextIndex = currentHighlightIndex >= highlightCount ? 1 : currentHighlightIndex + 1;
      setCurrentHighlightIndex(nextIndex);
      flashHighlightNodes(nextIndex);
    };

    const navigateToPreviousHighlight = () => {
      if (highlightCount === 0) return;
      const prevIndex = currentHighlightIndex <= 1 ? highlightCount : currentHighlightIndex - 1;
      setCurrentHighlightIndex(prevIndex);
      flashHighlightNodes(prevIndex);
    };

    // Handle text highlighting in section textfields
    const handleTextHighlight = async (sourceContext = null) => {
      const highlightedText = sourceContext?.query ?? selectedText;
      if (!highlightedText) {
        setAlert('No text selected for highlighting!', 'error');
        return;
      }
      setRetrieving(true);
      try {
        const formData = new FormData();
        const selectedSources = Array.isArray(sourceContext?.sourceItems)
          ? await prepareCheckSources(sourceContext.sourceItems, retrieveAttachmentFile) : null;
        if(isPopupReport && !selectedSources) {
          reportDocFiles.forEach((file) => {
            formData.append('files', file);
          });
        }
        formData.append('notes', selectedSources ? selectedSources.notes : isPopupReport ? '' : transcription);
        formData.append('highlighted_text', highlightedText);

        // /scribe/sources replaced the unauthenticated /retrieve alias (retired with a
        // 410 in the 2026-08-18 provider lockdown). It is a signed-in-only route, not a
        // wallet operation: no credits are debited for Check Sources.
        const response = await authedFetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/scribe/sources`, {
          method: 'POST',
          body: formData, // browser sets the multipart boundary; do not set Content-Type
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `Check Sources failed (${response.status})`);
        }
        const payload = await response.json();

        let data;
        if (typeof payload.source_text === "string") {
          data = JSON.parse(payload.source_text);
        } else {
          data = payload.source_text;
        }
        if (!Array.isArray(data)) data = [];

        console.log("the highlight response data is: ", data)

        if (!selectedSources) {
          setHighlightedTexts(data);
          setHighlightCount(data.length);
          setCurrentHighlightIndex(data.length > 0 ? 1 : 0);
          handleLeftViewChange('Transcription');
          transcriptionTextFieldRef.current?.focus();
        }

        return selectedSources ? { segments: data, sources: selectedSources.sources } : data;
      } catch (error) {
        console.error('Error highlighting text:', error);
        setAlert(error.message || 'Could not find sources for the selected text', 'error');
        setHighlightedTexts([]);
        setHighlightCount(0);
        setCurrentHighlightIndex(0);
      } finally {
        setRetrieving(false);
      }
    };

    // Create highlighted version of transcription text
    const renderHighlightedTranscription = (text) => {
      if (!text || highlightedTexts.length === 0) return text;
      // Create a temporary DOM element to work with the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text.replace(/\n/g, '<br>'); // Convert newlines to <br> tags
      
      // Extract text nodes that need highlighting
      const textNodes = [];
      const walkNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim() !== '') {
            textNodes.push(node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          node.childNodes.forEach(walkNode);
        }
      };
      walkNode(tempDiv);
      
      // Normalize each API chunk the same way as the document lines (whitespace and
      // punctuation removed, lower-cased — see sourceMatch.normalizeForMatch) so a quote
      // matches an ASR transcript that has spaces between words and different punctuation.
      // chunk index → normalized string for comparison
      const normalizedChunks = highlightedTexts.map((chunk) => normalizeForMatch(chunk));
      // Belt and braces with the backend's sanitiser: a chunk that is most of the
      // transcript (the model echoing the document back) would satisfy
      // chunk.includes(line) for every line and highlight everything; a fragment
      // shorter than 4 chars would match anywhere. Blank them so the loop skips them.
      const transcriptLength = normalizeForMatch(tempDiv.textContent).length;
      normalizedChunks.forEach((chunk, i) => {
        if (chunk.length < 4 || (transcriptLength >= 200 && chunk.length > transcriptLength * 0.5)) {
          normalizedChunks[i] = '';
        }
      });

      // chunk index → 1-based highlight display index (assigned on first match)
      const chunkToHighlightIdx = new Map();
      // highlight display indices whose nav id="highlight-N" has already been stamped
      const idAssigned = new Set();
      let nextHighlightIdx = 0;

      // Tracks the previous match so the immediately-following node can match the
      // remainder of the same chunk (handles content lines after any prefix).
      // matchedText is the normalizedSentence that was matched, used to compute
      // the remaining portion of the chunk to compare against.
      let followThrough = null; // { chunkIdx, matchedText }

      const markNode = (textNode, chunkIdx) => {
        if (!chunkToHighlightIdx.has(chunkIdx)) {
          nextHighlightIdx++;
          chunkToHighlightIdx.set(chunkIdx, nextHighlightIdx);
        }
        const highlightIdx = chunkToHighlightIdx.get(chunkIdx);
        const highlightColor = generateHighlightColor(highlightIdx);
        const markElement = document.createElement('mark');
        markElement.dataset.index = highlightIdx;
        markElement.dataset.highlight = 'true';
        markElement.style.backgroundColor = highlightColor;
        markElement.textContent = textNode.textContent;
        if (!idAssigned.has(highlightIdx)) {
          markElement.id = `highlight-${highlightIdx}`;
          idAssigned.add(highlightIdx);
        }
        textNode.parentNode.replaceChild(markElement, textNode);
      };

      textNodes.forEach((textNode) => {
        const normalizedSentence = normalizeForMatch(textNode.textContent);
        if (!normalizedSentence) return;

        // Capture and immediately reset follow-through — only valid for one node
        const ft = followThrough;
        followThrough = null;

        // Follow-through: the previous node matched chunk ft.chunkIdx.
        // Compute the remainder of that chunk after the already-matched prefix,
        // then compare the current sentence against the remainder (exact + fuzzy).
        // This is format-agnostic — no speaker label pattern assumed.
        if (ft !== null) {
          const ftChunk = normalizedChunks[ft.chunkIdx];

          // Find where the previous match sat in the chunk to isolate the remainder
          const prevMatchPos = ftChunk.indexOf(ft.matchedText);
          const remainder = prevMatchPos >= 0
            ? ftChunk.slice(prevMatchPos + ft.matchedText.length).trim()
            : ftChunk; // fallback: fuzzy-matched prev node, use full chunk

          if (remainder.length > 0) {
            // Exact check on remainder (no length guard — handles "超多。" etc.)
            if (remainder.includes(normalizedSentence) || normalizedSentence.includes(remainder)) {
              markNode(textNode, ft.chunkIdx);
              return;
            }
            // Fuzzy check on remainder (handles simplified/traditional differences)
            if (normalizedSentence.length >= 4 && remainder.length >= 4) {
              const longer = normalizedSentence.length > remainder.length ? normalizedSentence : remainder;
              const shorter = normalizedSentence.length > remainder.length ? remainder : normalizedSentence;
              const dist = levenshtein.get(longer, shorter, { useCollator: true });
              if (1 - dist / longer.length >= 0.85) {
                markNode(textNode, ft.chunkIdx);
                return;
              }
            }
          }
        }

        // Normal matching with length guard on the chunk.includes(sentence) direction
        for (let i = 0; i < normalizedChunks.length; i++) {
          const normalizedChunk = normalizedChunks[i];
          if (!normalizedChunk) continue;
          const isExactMatch =
            normalizedSentence.includes(normalizedChunk) ||
            (normalizedSentence.length >= 4 && normalizedChunk.includes(normalizedSentence));

          const isFuzzyMatch = (() => {
            if (isExactMatch) return false;
            const longer = normalizedSentence.length > normalizedChunk.length
              ? normalizedSentence : normalizedChunk;
            const shorter = normalizedSentence.length > normalizedChunk.length
              ? normalizedChunk : normalizedSentence;
            const dist = levenshtein.get(longer, shorter, { useCollator: true });
            const similarity = 1 - dist / longer.length;
            return similarity >= 0.85;
          })();

          if (isExactMatch || isFuzzyMatch) {
            markNode(textNode, i);
            followThrough = { chunkIdx: i, matchedText: normalizedSentence };
            break;
          }
        }
      });
      
      return tempDiv.innerHTML;
    };
    
    // Clear highlights
    const clearHighlights = () => {
      setSelectedText('');
      setHighlightedTexts([]);
      setHighlightCount(0);
      setCurrentHighlightIndex(0);
    };

    // History view states
    const [historyFilter, setHistoryFilter] = useState({
      consultations: true,
      documents: true,
      attachments: true
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [expandedRows, setExpandedRows] = useState({});
    const [historyItems, setHistoryItems] = useState([]);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const reportGenerationBusyRef = useRef(false);
    
    // Fetch all history items (consultations, documents, and attachments)
    const fetchHistoryItems = async ({ background = false } = {}) => {
      if (!background) {
        setConsultationLoading(true);
      }
      
      try {
        // Fetch consultations
        const [consultations, generatedReports, patientAttachments] = await Promise.all([
          fetchConsultationData(),
          fetchGeneratedReports(),
          fetchPatientAttachmentsAsFiles()
        ]);
        
        // Process consultations into history items
        let allHistoryItems = [];
        
        if (consultations) {
          // Map consultations to history items
          const consultationItems = consultations.map(consultation => ({
            id: consultation.id,
            createdAt: consultation.createdAt,
            sessionDate: consultation.session_date,
            title: consultation.chiefComplaint || 'Untitled Consultation',
            type: 'Consultation',
            originalData: consultation,
            createdBy: consultation.created_by || userDetails?.name || 'Unknown User',
            createdByUserId: consultation.created_by_user_id || null,
            isSelected: false,
            parentId: null,
            isSubItem: false,
            isShared: consultation.isShared || false,
            share_expires_at: consultation.share_expires_at,
          }));

          allHistoryItems.push(...consultationItems);
          
          // For each consultation, check for documents and attachments
          for (const consultation of consultations) {
            //Fetch reports for this consultation
            const consultationReports = await fetchConsultationReports(consultation.id);
            if (consultationReports && consultationReports.length > 0) {
              const reportItems = consultationReports.map(report => ({
                id: report.id,
                createdAt: report.created_at,
                title: report.title,
                type: 'Document',
                originalData: { content: report.content, consultationId: consultation.id, templateId: report.template_id },
                createdBy: report.created_by || userDetails?.name || 'Unknown User',
                createdByUserId: report.created_by_user_id || null,
                isSelected: false,
                parentId: consultation.id,
                isSubItem: true,
                isLocked: report.locked,
                isShared: report.isShared,
                share_expires_at: report.share_expires_at,
                documents: report.documents
              }));
              allHistoryItems.push(...reportItems);
            }
            
            // Fetch attachments for this consultation
            const attachments = await fetchAttachmentsForConsultationAsFiles(consultation.id);
            if (attachments && attachments.length > 0) {
              const attachmentItems = attachments.map(fileMetadata => ({
                id: `file-${consultation.id}-${fileMetadata.name}`,
                createdAt: consultation.createdAt,
                title: fileMetadata.name,
                type: 'Attachment',
                originalData: fileMetadata,
                createdBy: fileMetadata.uploadedBy || '',
                createdByUserId: fileMetadata.uploadedByUserId || null,
                isSelected: false,
                parentId: consultation.id,
                isSubItem: true,
                path: fileMetadata.path
              }));
              allHistoryItems.push(...attachmentItems);
            }
          }
        }
        
        // Fetch patient-level attachments
        if (patientAttachments && patientAttachments.length > 0) {
          const patientAttachmentItems = patientAttachments.map(fileMetadata => ({
            id: patientAttachmentId(fileMetadata),
            createdAt: fileMetadata.updated_at,
            title: fileMetadata.name,
            type: 'Attachment',
            originalData: fileMetadata,
            createdBy: fileMetadata.uploadedBy || '',
            createdByUserId: fileMetadata.uploadedByUserId || null,
            isSelected: false,
            parentId: null,
            isSubItem: false,
            path: fileMetadata.path
          }));
          
          allHistoryItems.push(...patientAttachmentItems);
        }

        if(generatedReports && generatedReports.length > 0) {
          const filteredReports = generatedReports.filter(report => report.consultationId === null);
          const reportItems = filteredReports.map(report => ({
            id: report.id,
            createdAt: report.created_at,
            title: report.title,
            type: 'Document',
            originalData: { content: report.content, templateId: report.template_id },
            createdBy: report.created_by,
            createdByUserId: report.created_by_user_id || null,
            isSelected: false,
            parentId: null,
            isSubItem: false,
            isLocked: report.locked,
            isShared: report.isShared,
            share_expires_at: report.share_expires_at,
            documents: report.documents
          }));
          allHistoryItems.push(...reportItems);
        }

        // Sort items by date (newest first)
        allHistoryItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setHistoryItems(allHistoryItems);
        return allHistoryItems;
      } catch (error) {
        console.error('Error fetching history items:', error);
        setAlert('Failed to fetch history items', 'error');
      } finally {
        if (!background) {
          setConsultationLoading(false);
        }
      }
    };

    const fetchConsultationReports = async (consultationId) => {
      try {
        const { data, error } = await supabase.from('generatedreports').select('*').eq('consultationId', consultationId).order('updated_at', { ascending: false });
        if (error) throw error;

        const decryptionPromises = data.map(async (report) => {
          const [titleResult, contentResult, createdbyResult] = await Promise.all([
            supabase.rpc('decrypt_text', { encrypted_data: report.title }),
            supabase.rpc('decrypt_text', { encrypted_data: report.content }),
            supabase.rpc('decrypt_text', { encrypted_data: report.created_by })
          ]);
          if(titleResult.error || contentResult.error || createdbyResult.error) {
            return report;
          }
          report.title = titleResult.data;
          report.content = contentResult.data;
          report.created_by = createdbyResult.data;

          return report;
        });
        const decryptedReports = await Promise.all(decryptionPromises);
        return decryptedReports;
      } catch (error) {
        console.error(`Error fetching reports for consultation ${consultationId}:`, error);
        return [];
      }
    }

    const getMimeType = (fileName) => {
      const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
      const mimeTypes = {
          pdf: 'application/pdf',
          docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          txt: 'text/plain',
          mp3: 'audio/mpeg',
          wav: 'audio/wav',
          webm: 'audio/webm',
          m4a: 'audio/m4a',
          aac: 'audio/aac',
          ogg: 'audio/ogg',
          mpeg: 'audio/mpeg',
          mpga: 'audio/mpeg',
          mp4a: 'audio/mp4',
          wma: 'audio/x-ms-wma',
          flac: 'audio/flac'
      };
  
      return mimeTypes[extension];
    };

    // Fetch attachments for a specific consultation
    const fetchAttachmentsForConsultationAsFiles = async (consultationId) => {
      try {
        // First get the list of file metadata
        const { data: fileList, error: listError } = await supabase.storage
          .from('tikcare-attachments')
          .list(`${id}/${consultationId}`);
        
        if (listError) throw listError;
        
        // Get uploader information from file_metadata table
        const filePaths = fileList.map(file => `${id}/${consultationId}/${file.name}`);
        
        // First, fetch encrypted data
        const { data: fileMetadata, error: metadataError } = await supabase
          .from('file_metadata')
          .select('file_path, uploader_name, uploaded_by, uploaded_at, file_size, file_name')
          .in('file_path', filePaths);

        if (metadataError) {
          console.error('Error fetching file metadata:', metadataError);
        }

        // Decrypt the metadata
        const decryptedMetadata = [];
        if (fileMetadata && fileMetadata.length > 0) {
          for (const meta of fileMetadata) {
            try {
              const [uploaderNameResult, fileNameResult] = await Promise.all([
                supabase.rpc('decrypt_text', { encrypted_data: meta.uploader_name }),
                supabase.rpc('decrypt_text', { encrypted_data: meta.file_name })
              ]);

              if (uploaderNameResult.error || fileNameResult.error) {
                console.error('Error decrypting file metadata:', {
                  uploaderNameError: uploaderNameResult.error,
                  fileNameError: fileNameResult.error
                });
                continue;
              }

              decryptedMetadata.push({
                file_path: meta.file_path,
                uploader_name: uploaderNameResult.data,
                uploaded_by: meta.uploaded_by,
                file_name: fileNameResult.data,
                uploaded_at: meta.uploaded_at,
                file_size: meta.file_size
              });
            } catch (decryptError) {
              console.error('Error in decryption process:', decryptError);
            }
          }
        }

        // Create a map for quick lookup
        const metadataMap = {};
        decryptedMetadata.forEach(meta => {
          metadataMap[meta.file_path] = meta;
        });

        const fileObjects = fileList.map(fileInfo => {
          const filePath = `${id}/${consultationId}/${fileInfo.name}`;
          const metadata = metadataMap[filePath];

          return {
            // Display name: the original upload name; the storage key stays in `path`.
            name: metadata?.file_name || fileInfo.name,
            updated_at: metadata?.uploaded_at || fileInfo.updated_at,
            size: metadata?.file_size || fileInfo.metadata?.size || 0,
            path: filePath,
            uploadedBy: metadata?.uploader_name || null,
            uploadedByUserId: metadata?.uploaded_by || null,
            isMetadataOnly: true
          };
        });
        
        return fileObjects;
      } catch (error) {
        console.error(`Error fetching attachment metadata for consultation ${consultationId}:`, error);
        return [];
      }
    };

    // Fetch patient-level attachments
    const fetchPatientAttachmentsAsFiles = async () => {
      try {
        const { data: fileList, error: listError } = await supabase.storage
          .from('tikcare-attachments')
          .list(`${id}`);
        
        if (listError) throw listError;
        
        const filesOnly = fileList.filter(item => 
          item.metadata && item.metadata.size !== undefined && item.metadata.size > 0
        );
        
        // Get uploader information from file_metadata table
        const filePaths = filesOnly.map(file => `${id}/${file.name}`);
        
        // First, fetch encrypted data
        const { data: fileMetadata, error: metadataError } = await supabase
          .from('file_metadata')
          .select('file_path, uploader_name, uploaded_by, uploaded_at, file_size, file_name')
          .in('file_path', filePaths);

        if (metadataError) {
          console.error('Error fetching file metadata:', metadataError);
        }

        // Decrypt the metadata
        const decryptedMetadata = [];
        if (fileMetadata && fileMetadata.length > 0) {
          for (const meta of fileMetadata) {
            try {
              const [uploaderNameResult, fileNameResult] = await Promise.all([
                supabase.rpc('decrypt_text', { encrypted_data: meta.uploader_name }),
                supabase.rpc('decrypt_text', { encrypted_data: meta.file_name })
              ]);

              if (uploaderNameResult.error || fileNameResult.error) {
                console.error('Error decrypting file metadata:', {
                  uploaderNameError: uploaderNameResult.error,
                  fileNameError: fileNameResult.error
                });
                continue;
              }

              decryptedMetadata.push({
                file_path: meta.file_path,
                uploader_name: uploaderNameResult.data,
                uploaded_by: meta.uploaded_by,
                file_name: fileNameResult.data,
                uploaded_at: meta.uploaded_at,
                file_size: meta.file_size
              });
            } catch (decryptError) {
              console.error('Error in decryption process:', decryptError);
            }
          }
        }

        // Create a map for quick lookup
        const metadataMap = {};
        decryptedMetadata.forEach(meta => {
          metadataMap[meta.file_path] = meta;
        });

        // Return file metadata only, don't download the actual files
        const fileObjects = filesOnly.map(fileInfo => {
          const filePath = `${id}/${fileInfo.name}`;
          const metadata = metadataMap[filePath];

          return {
            // Display name: the original upload name; the storage key stays in `path`.
            name: metadata?.file_name || fileInfo.name,
            updated_at: metadata?.uploaded_at || fileInfo.updated_at,
            size: metadata?.file_size || fileInfo.metadata?.size || 0,
            path: filePath,
            uploadedBy: metadata?.uploader_name || null,
            uploadedByUserId: metadata?.uploaded_by || null,
            isMetadataOnly: true
          };
        });
        
        return fileObjects;
      } catch (error) {
        console.error(`Error fetching patient attachments metadata:`, error);
        return [];
      }
    };

    const formatPlainTextToHtml = (text) => {
      if (!text) return '';
      
      // 1. Escape HTML entities to prevent XSS
      const escapeHtml = (unsafe) => {
        return unsafe
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };
      
      const escapedText = escapeHtml(text);
      
      // 2. Split the text by lines
      const lines = escapedText.split('\n');
      
      // 3. Wrap each non-empty line in a paragraph
      const formattedLines = lines.map(line => {
        if (line.trim() === '') {
          return '<p>&nbsp;</p>'; // Empty line becomes a paragraph break
        } else {
          return `<p>${line}</p>`;
        }
      });
      
      return formattedLines.join('');
    };


    const setReportDocuments = async (item) => {
      if (!item || !item.documents || !item.documents.length) {
        setReportDocs([]);
        return;
      }
      
      try {
        const documentItems = [];
        const documentFiles = [];
        
        // Process each document ID referenced in the report
        for (const documentId of item.documents) {
          // Find the corresponding item in history items
          const historyItem = historyItems.find(item => item.id === documentId);
          
          if (!historyItem) continue;
          
          let content = null;
          const fileName = `${historyItem.title.replace(/[^a-zA-Z0-9]/g, '_')}.docx`;
          
          if (historyItem.type === 'Attachment') {
            try {
              const file = await retrieveAttachmentFile(historyItem.path, historyItem.title);
              
              if (canReviewOcr(historyItem)) {
                const prepared = await prepareCheckSources([historyItem], retrieveAttachmentFile);
                content = formatPlainTextToHtml(prepared.sources[0].text);
              } else if (file.type === "text/plain") {
                content = `<pre>${await file.text()}</pre>`;
              }

              documentItems.push({
                id: historyItem.id,
                name: historyItem.title,
                type: historyItem.type,
                content: content,
                filetype: file.type,
                originalData: file,
                // Storage key: report generation references it server-side.
                path: historyItem.path,
              });
              documentFiles.push(file);
            } catch (downloadError) {
              console.error(`Failed to download attachment ${historyItem.title}:`, downloadError);
              setAlert(`Failed to load attachment: ${historyItem.title}`, 'warning');
              documentItems.push({ id: historyItem.id, name: historyItem.title, type: 'Attachment', content: null,
                path: historyItem.path, sourceError: true });
              continue;
            }
          } 
          else if (historyItem.type === 'Document' || historyItem.type === 'Consultation') {
            let textContent = '';
            
            if (historyItem.type === 'Document') {
              textContent = historyItem.originalData.content || '';
            } 
            else if (historyItem.type === 'Consultation') {
              const rawTranscription = (historyItem.originalData.transcription ?? '') + '\n\n' + (historyItem.originalData.notes ?? '');
              textContent = formatPlainTextToHtml(rawTranscription);
            }
            const plainTextContent = textContent.replace(/<[^>]+>/g, '');
            
            // Create a File object with text content
            const file = new File(
              [plainTextContent], 
              fileName.replace('.docx', '.txt'), 
              { 
                type: 'text/plain',
                lastModified: new Date().getTime()
              }
            );
            
            documentItems.push({
              id: historyItem.id,
              name: historyItem.title,
              type: historyItem.type,
              content: textContent
            });
            documentFiles.push(file);
          }
        }
        
        setReportDocs(documentItems);
        setReportDocFiles(documentFiles);
        
      } catch (error) {
        console.error('Error setting report documents:', error);
        setAlert('Failed to load report source documents', 'error');
      }
    };

    const fetchGeneratedReports = async () => {
      try {
        const { data, error } = await supabase.from('generatedreports').select('*').eq('patientId', id);
        if (error) throw error;

        const decryptionPromises = data.map(async (report) => {
          const [titleResult, contentResult, createdbyResult] = await Promise.all([
            supabase.rpc('decrypt_text', { encrypted_data: report.title }),
            supabase.rpc('decrypt_text', { encrypted_data: report.content }),
            supabase.rpc('decrypt_text', { encrypted_data: report.created_by })
          ]);
          if(titleResult.error || contentResult.error || createdbyResult.error) {
            return report;
          }
          report.title = titleResult.data;
          report.content = contentResult.data;
          report.created_by = createdbyResult.data;

          return report;
        });
        const decryptedReports = await Promise.all(decryptionPromises);
        return decryptedReports;
      } catch (error) {
        console.error('Error fetching generated reports:', error);
        return [];
      }
    }

    // Toggle row expansion
    const toggleRowExpansion = (rowId) => {
      setExpandedRows(prev => ({
        ...prev,
        [rowId]: !prev[rowId]
      }));
    };

    // Background indexing alerts belong only to the client/organization that uploaded.
    const attachmentAlertScope = useRef(null);
    useEffect(() => {
      attachmentAlertScope.current = {};
      return () => { attachmentAlertScope.current = null; };
    }, [id, currentOrg.id, userDetails?.id]);

    // Handle upload for patient-level attachments
    const handlePatientFileUpload = async (event) => {
      const uploadedFiles = Array.from(event.target.files);
      const alertScope = attachmentAlertScope.current;
      let batch;
      
      try {
        setIsUploading(true);
        setConsultationLoading(false);
        if (!id || !userDetails?.id) throw new Error('Please sign in and open a client before uploading files.');
        const { duplicates: duplicateFiles, uploads: newFiles } = partitionPatientUploads(uploadedFiles, historyItems, id, userDetails.id);

        if (duplicateFiles.length > 0) {
          const duplicateNames = duplicateFiles.map(file => file.name).join(', ');
          setAlert(`File(s) with the same name already exist: ${duplicateNames}`, 'warning');
          
          if (newFiles.length === 0) {
            return;
          }
        }

        const key = await deriveKey(id);
        
        batch = createBatchProgress(setFileUploadProgress);
        const uploadedPaths = new Set();
        const uploadPromises = newFiles.map(async (file) => {
          // Storage keys must be ASCII; the real name is kept in file_metadata.file_name.
          const storageName = newPatientStorageName(file.name);
          const filePath = `${id}/${storageName}`;
          uploadedPaths.add(filePath);
          const encrypted = await encryptFile(file, key, storageName);
          // Registered before the upload starts so the bar's denominator is the whole
          // selection immediately (utils/storageUpload).
          batch.add(filePath, encrypted.size);
          await uploadWithProgress('tikcare-attachments', filePath, encrypted, {
            cacheControl: '3600',
            upsert: false,
            onProgress: batch.onProgress(filePath),
          });

          // Save file metadata to new table
          const { error: metadataError } = await supabase
            .from('file_metadata')
            .insert({
              file_path: filePath,
              uploaded_by: userDetails.id,
              uploader_name: userDetails.name,
              patient_id: id,
              consultation_id: null,
              file_name: file.name,
              file_size: file.size
            });
          
          if (metadataError) {
            // Only remove this newly created object; never touch an existing file.
            await supabase.storage.from('tikcare-attachments').remove([filePath]);
            throw new Error('Could not save file details. Please try uploading again.');
          }
          
          return {
            name: file.name,
            path: filePath,
            created_at: new Date().toISOString()
          };
        });
        
        const uploadedSources = await Promise.all(uploadPromises);

        const formData = new FormData();
        formData.append('source_paths', JSON.stringify(uploadedSources.map(source => source.path)));
        newFiles.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('patient_id', id);
        formData.append('document_type', 'attachment');
        formData.append('report_content', null);
        formData.append('report_id', null);
        formData.append('team_id', currentOrg.id);

        if (attachmentAlertScope.current === alertScope) setAlert('Files uploaded. Preparing them for Ask Sophie.', 'info');
        const showIndexingResult = data => {
          if (attachmentAlertScope.current !== alertScope) return;
          const alert = attachmentIndexingAlert(data, newFiles.map(file => file.name));
          setAlert(alert.text, alert.type);
        };
        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).then(response => showIndexingResult(response.data))
          .catch(() => showIndexingResult({ status: 'failed' }));
        const refreshedItems = await fetchHistoryItems();
        return (refreshedItems || []).filter(item => item.type === 'Attachment' && uploadedPaths.has(item.path));
      } catch (error) {
        if(error.message && error.message.startsWith('Invalid key')) {
          setAlert('Upload failed: file name may have special characters. Please rename and try again.', 'error');
        } else {
          setAlert(`Error uploading files: ${error.message}`, 'error');
        }
        return false;
      } finally {
        batch?.done();
        setIsUploading(false);
      }
    };

    // Delete history item
    const deleteHistoryItem = async (item, { suppressLoading = false, preserveReports = false } = {}) => {
      try {
        if (!suppressLoading) setConsultationLoading(true);

        if (item.type === 'Consultation') {
          await removeConsultation(item.originalData, { preserveReports, refresh: !suppressLoading });
        } 
        else if (item.type === 'Document') {
          const formData = new FormData();
          formData.append('patient_id', id);
          formData.append('document_type', 'report');
          formData.append('report_id', item.id);
          formData.append('team_id', currentOrg.id);

          const { error } = await supabase.from('generatedreports').delete().eq('id', item.id);
          if (error) throw error;
          axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_embedding`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }        
        else if (item.type === 'Attachment') {
          const formData = new FormData();
          try {
            const file = await retrieveAttachmentFile(item.path, item.title);
            formData.append('files', file);
          } catch (downloadError) {
            console.error(`Failed to download file for embedding deletion: ${downloadError}`);
          }
          formData.append('patient_id', id);
          formData.append('document_type', 'attachment');
          formData.append('source_paths', JSON.stringify([item.path]));
          formData.append('report_id', null);
          formData.append('team_id', currentOrg.id);
          axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_embedding`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          const { error } = await supabase.storage.from('tikcare-attachments').remove([item.path]);
          if (error) throw error;

          const { error: metadataError } = await supabase
            .from('file_metadata')
            .delete()
            .eq('file_path', item.path);
          if (metadataError) {
            console.error('Error deleting file metadata:', metadataError);
          }
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        setAlert(`Error deleting ${item.type.toLowerCase()}. Please try again.`, 'error');
        throw error;
      } finally {
        if (!suppressLoading) setConsultationLoading(false);
      }
    };


    const deleteScopeRef = useRef(id);
    deleteScopeRef.current = id;
    const deleteRequestRef = useRef(0);
    // Confirmation belongs here so every Write delete checks report references.
    const requestDeleteHistoryItems = async (itemIds, activeReportId = null, { canDelete = () => true, onDeleted, isCurrent = () => true } = {}) => {
      const requestId = ++deleteRequestRef.current;
      const deletionIds = Array.isArray(itemIds) ? itemIds : selectedItems;
      if (!confirmNavigation()) return;
      const targets = historyItems.filter(item => deletionIds.includes(item.id));
      if (!targets.length) { setAlert('No items selected for deletion', 'warning'); return; }
      let referencingReports;
      try { referencingReports = await findReferencingReports(supabase, id, deletionIds); }
      catch {
        setAlert('Could not check which reports use this source. No files were deleted. Please try again.', 'error');
        return;
      }
      if (deleteScopeRef.current !== id || deleteRequestRef.current !== requestId) return;
      const baseMessage = targets.length === 1
        ? `Delete “${targets[0].title}”? This cannot be undone.`
        : `Delete ${targets.length} selected file(s)? This cannot be undone.`;
      const impactMessage = referencingReports.length
        ? `\n\nThis source is used in ${referencingReports.length} report(s): ${referencingReports.slice(0, 3).map(report => report.title).join(', ')}${referencingReports.length > 3 ? ', and more' : ''}. Deleting it will not change their existing report text, but the original source will no longer be available to review or use when regenerating those reports.`
        : '';
      let deleting = false;
      setOpenDialog({
        open: true,
        content: baseMessage + impactMessage,
        confirmAction: async () => {
          if (deleting || deleteScopeRef.current !== id || !canDelete()) return;
          deleting = true;
          try {
            const result = await deleteSelectedHistoryItems(deletionIds, { preserveSelection: true, preserveReports: true });
            const deletedItems = result?.deletedItems || [];
            const removedIds = new Set(deletedItems.map(item => item.id));
            if (!removedIds.size) return;
            if (deleteScopeRef.current === id) onDeleted?.(deletedItems);
            let selections = new Map();
            try {
              selections = await markDeletedReportSources(supabase, id, deletedItems, (report, error) => {
                console.error('Could not mark deleted report source:', error);
                setAlert(`The file was deleted, but “${report.title}” could not mark its source as unavailable. Reopen the report to refresh its source list.`, 'warning');
              });
            } catch (error) {
              console.error('Could not refresh report sources:', error);
              setAlert('The file was deleted, but its report sources could not be refreshed. Reopen the report to review unavailable sources.', 'warning');
            }
            const active = referencingReports.find(report => report.id === activeReportId);
            const fallbackIds = active ? (active.documents?.length ? active.documents : [active.consultationId]) : null;
            const sourceTitles = new Map(deletedItems.map(item => [item.id, item.title]));
            if (deleteScopeRef.current !== id || !isCurrent()) return;
            setSelectedItems(current => activeReportId && !removedIds.has(activeReportId)
              ? selections.get(activeReportId) || (fallbackIds || current).map(sourceId => removedIds.has(sourceId) ? deletedSourceId(sourceId, sourceTitles.get(sourceId)) : sourceId)
              : current.filter(sourceId => !removedIds.has(sourceId)));
            await fetchHistoryItems({ background: true });
          } finally { deleting = false; }
        },
      });
    };

    // Mass delete selected history items using existing deleteHistoryItem function
    const deleteSelectedHistoryItems = async (itemIds, { preserveSelection = false, preserveReports = false } = {}) => {
      const deletionIds = Array.isArray(itemIds) ? itemIds : selectedItems;
      if (!confirmNavigation()) return;
      if (deletionIds.length === 0) {
        setAlert('No items selected for deletion', 'warning');
        return;
      }

      let deletedItems = [];
      try {
        setConsultationLoading(true);

        const selectedItemsData = historyItems.filter(item => deletionIds.includes(item.id));

        const deletionPromises = selectedItemsData.map(async (item) => {
          try {
            await deleteHistoryItem(item, { suppressLoading: true, preserveReports });
            return { success: true, item };
          } catch (error) {
            console.error(`Error deleting ${item.type}:`, error);
            return { success: false, item, error };
          }
        });

        const results = await Promise.allSettled(deletionPromises);
        deletedItems = results.filter(r => r.status === 'fulfilled' && r.value.success).map(r => r.value.item);

        if (!preserveSelection) clearAllSelections();
        await fetchHistoryItems();

        const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;

        if (failed === 0) {
          setAlert(`Successfully deleted ${successful} item(s).`, 'success');
        } else if (successful === 0) {
          setAlert(`Failed to delete all ${failed} item(s). Please try again.`, 'error');
        } else {
          setAlert(`Deleted ${successful} item(s) successfully. ${failed} item(s) failed to delete.`, 'warning');
        }
        return { deletedItems };
      } catch (error) {
        console.error('Error in mass deletion:', error);
        setAlert('An unexpected error occurred during deletion', 'error');
        return { deletedItems };
      } finally {
        setConsultationLoading(false);
      }
    };

    const clearAllSelections = () => {
      setSelectedItems([]);
      setHistoryItems(prev => prev.map(item => ({
        ...item,
        isSelected: false
      })));
      setSelectAll(false);
    };

    // Toggle selection of a history item
    const toggleItemSelection = (itemId) => {
      setHistoryItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, isSelected: !item.isSelected } : item
      ));
      
      setSelectedItems(prev => {
        const isItemSelected = prev.includes(itemId);
        return isItemSelected 
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId];
      });
    };

    // Toggle selection of all visible items
    const toggleSelectAllItems = () => {
      const newSelectAll = !selectAll;
      setSelectAll(newSelectAll);
      
      const filteredItems = getFilteredHistoryItems();
      
      setHistoryItems(prev => prev.map(item => {
        const isVisible = filteredItems.some(filteredItem => filteredItem.id === item.id);
        return isVisible ? { ...item, isSelected: newSelectAll } : item;
      }));
      
      setSelectedItems(newSelectAll ? filteredItems.map(item => item.id) : []);
    };

    // Get filtered history items based on filters and search
    const getFilteredHistoryItems = () => {
      return historyItems.filter(item => {
        // Filter by type
        const typeMatch =
          (historyFilter.consultations && item.type === 'Consultation') ||
          (historyFilter.documents && (item.type === 'Document')) ||
          (historyFilter.attachments && (item.type === 'Attachment'));

        // Admins see all files; non-admins only see their own (null = legacy, show for backward compat)
        const ownerMatch = isAdminUser || !item.createdByUserId || item.createdByUserId === userDetails?.id;

        // Filter by search query
        const searchMatch = searchQuery === '' ||
          item.title.toLowerCase().includes(searchQuery.toLowerCase());

        return typeMatch && ownerMatch && searchMatch;
      });
    };

    // Sort items
    const requestSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    };

    const setUserDefaultTemplate = async (templateId) => {
      if (!userDetails?.id || !templateId) return;
      await supabase.from('templates').update({ default: null }).eq('userid', userDetails.id).eq('default', 'report');
      await supabase.from('templates').update({ default: 'report' }).eq('id', templateId);
    };

    // Generate report from selected consultation
    const generateReportFromConsultation = async (selectedTemplate) => {
      if (!subscriptionInfo.is_active) {
        setAlert('Your subscription is not active. Please activate your subscription to generate reports.', 'error');
        return;
      }
      
      if (!currentConsultation) {
        setAlert('No consultation selected', 'error');
        return;
      }
      
      if (!selectedTemplate) {
        setAlert('No template selected', 'error');
        return;
      }

      //if both transcription and remarks are empty, show alert
      if ((!transcriptionRef.current || transcriptionRef.current.trim() === '') &&
          (!remarksRef.current || remarksRef.current.trim() === '' || remarksRef.current === '<p></p>')) {
        
        // Allow switching template if it's a new consultation with no report generated yet
        if (!sectionSummary.id) {
          setRightOptions(
            [
              { value: selectedTemplate.title, label: selectedTemplate.title },
              // { value: 'Medical Coding', label: 'Medical Coding' },
            ]
          );
          setRightView(selectedTemplate.title);
          setSelectedTemplate(selectedTemplate);
          setDefaultTemplate(selectedTemplate);
          setDefaultTemplateContent(selectedTemplate.content);
          await setUserDefaultTemplate(selectedTemplate.id);
          setAlert(`Template switched to ${selectedTemplate.title}`, 'success');
          return;
        }

        setAlert('Both transcription and remarks are empty. Please provide consultation details to generate a report.', 'warning');
        return;
      }
      
      try {
        setConsultationLoading(true);
        
        // Prepare consultation data
        const consultationNotes = `${transcriptionRef.current ?? ""}\n\n${remarksRef.current ?? ""}`;
        
        // Extract clean template text
        // const cleanTemplateText = extractParagraphText(selectedTemplate.content);
        const cleanTemplateText = selectedTemplate.content;

        // Prepare form data for API call
        const formData = new FormData();
        formData.append('notes', consultationNotes);
        formData.append('template', cleanTemplateText || '');
        formData.append('output_language', outputLanguage);
        formData.append('spelling_format', spellingFormat);
        
        // Generate the report
        const response = await postGenerateReport(formData, 'generate', setPiiWarming, getReportLanguageSettings);
        
        const formattedSummaryHTML = requireReportContent(response.data.summary);
        // const formattedSummaryHTML = formatAIResponseToHTML(formattedSummary, selectedTemplate.content);
        
        // Generate unique report ID and metadata
        const reportId = uuidv4();
        const reportDate = new Date().toISOString();
        const reportTitle = `${selectedTemplate.title} - ${selectedReportDateLabel()}`;
        
        // Prepare source documents array (current consultation and any files)
        const sourceDocuments = [currentConsultation.id];
        // if (files && files.length > 0) {
        //   files.forEach(file => {
        //     sourceDocuments.push(`file-${currentConsultation.id}-${file.name}`);
        //   });
        // }
        
        // Save report to database
        const { error } = await supabase.from('generatedreports').insert({
          id: reportId,
          created_at: reportDate,
          updated_at: reportDate,
          patientId: id,
          consultationId: currentConsultation.id,
          content: formattedSummaryHTML,
          title: reportTitle,
          created_by: userDetails?.name || 'Unknown User',
          created_by_user_id: userDetails?.id || null,
          locked: false,
          documents: sourceDocuments,
          template_id: selectedTemplate.id
        }).select('*').single();

        if (error) throw error;

        // Generate does not go through saveConsultation, so the transcript the
        // report was built from is indexed here (the refs are what was sent).
        indexConsultationTranscript(currentConsultation);

        // Keep the existing consultation name, including clinician edits.

        // Ensure sectionSummary/edit mode reflect the new report
        setSectionSummary({ id: reportId, content: formattedSummaryHTML });
        sectionSummaryIdRef.current = reportId;
        setEditNote(true);
        
        // Create history item object
        const newReportItem = {
          id: reportId,
          createdAt: reportDate,
          title: reportTitle,
          type: 'Document',
          originalData: {
            content: formattedSummaryHTML,
            consultationId: currentConsultation.id,
            templateId: selectedTemplate.id
          },
          createdBy: userDetails?.name || 'Unknown User',
          createdByUserId: userDetails?.id || null,
          isSelected: false,
          parentId: currentConsultation.id,
          isSubItem: true,
          isLocked: false,
          isShared: false,
          documents: sourceDocuments
        };
        
        // Add to RAG system for embedding
        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', formattedSummaryHTML);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', `${reportTitle}`);
        
        // Add embedding in background (non-blocking)
        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }).then(response => {
          if (response.data.status === 'failed') {
            setAlert('Report generated but failed to add to search index', 'warning');
          }
        }).catch(error => {
          console.error('Background embedding failed:', error);
        });
        
        // Set up report documents for viewing
        await setReportDocuments(newReportItem);
        await fetchHistoryItems();
        const consultationIndex = pastConsultations.findIndex(c => c.id === currentConsultation.id);
        if (consultationIndex !== -1) {
          viewPastConsultation(consultationIndex, newReportItem);
        }
        track(EVENTS.NOTE_GENERATED, { template_id: selectedTemplate.id, success: true });
      } catch (error) {
        console.error('Error generating report from consultation:', error);
        track(EVENTS.NOTE_GENERATED, { template_id: selectedTemplate?.id, success: false });
        // Surface the real reason (empty transcript, out of credits, under
        // review) instead of a dead-end generic message.
        setAlert(error?.message ? `Failed to generate report: ${error.message}` : 'Failed to generate report', 'error');
      } finally {
        setConsultationLoading(false);
      }
    };

    // Generate comprehensive report from selected items
    const generateComprehensiveReport = async ({ useSelectedSources = false, preparedSources = null, templateOverride = null } = {}) => {
      if (reportGenerationBusyRef.current) return;
      const reportTemplate = templateOverride || selectedTemplate;
      const useSavedReportSources = reportTemplateSelected && !useSelectedSources;
      if(!subscriptionInfo.is_active) {
        setAlert('Your subscription is not active. Please activate your subscription to generate reports.', 'error');
        return;
      }
      // console.log("the selected report is", selectedReport);
      // console.log("the report docs are", reportDocs);
      // console.log('report template selected:', reportTemplateSelected);
      // console.log("the tempalte selected is", selectedTemplate);
      // return;
      if(!useSavedReportSources) {
        if (selectedItems.length === 0) {
          setAlert('Please select items to include in the report', 'warning');
          return;
        }
      } else {
        setConsultationLoading(true);
      }
      
      reportGenerationBusyRef.current = true;
      setIsGeneratingReport(true);
      try {
        // Prepare data for API call
        let notes = "";
        let attachmentItems = [];
        let selectedItemsData = [];

        if(useSavedReportSources) {
          reportDocs.forEach(item => {
            if (!hasReportInputText(item.content)) return;
            if (item.type === 'Consultation') {
              const currentConsultation = `
              Consultation: ${item.name}:\n\n
              ${item.content}\n\n
              `
              notes += currentConsultation;
            } else if (item.type === 'Document') {
              const currentDocument = `
              Document: ${item.name}\n\n
              ${item.content}\n\n
              `
              notes += currentDocument;
            }
          });
          attachmentItems = reportDocs.filter((docItem) => docItem.type === 'Attachment');
        }
        else {
          selectedItemsData = useSelectedSources ? preparedSources : historyItems.filter(item => selectedItems.includes(item.id) && !isAudioFileFormat(item.title));
          if (useSelectedSources) reportSourceManifest(selectedItemsData || [], selectedItems);

          // Process each selected item
          for (const item of selectedItemsData) {
            if (item.type === 'Consultation') {
              if (!hasReportInputText(item.originalData.transcription) && !hasReportInputText(item.originalData.notes)) continue;
              const currentConsultation = `
              Consultation on ${convertToGMT8Date(item.originalData.createdAt)}:\n\n
              ${item.originalData.transcription || ''}\n\n
              ${item.originalData.notes || ''}\n\n
              `
              notes += currentConsultation;
            }
            else if (item.type === 'Document') {
              if (!hasReportInputText(item.originalData.content)) continue;
              const currentDocument = `
              Document: ${item.title}\n\n
              ${item.originalData.content}\n\n
              `
              notes += currentDocument;
            }
            else if (item.type === 'Attachment') {
              attachmentItems.push(item);
            }
          }
        }
        // const cleanTemplateText = extractParagraphText(selectedTemplate.content);
        const cleanTemplateText = reportTemplate?.content;
        // console.log("the clean template text is", cleanTemplateText);

        const formData = new FormData();
        if (useSelectedSources) formData.append('source_manifest', JSON.stringify(reportSourceManifest(selectedItemsData, selectedItems)));
        else if (useSavedReportSources) {
          const manifest = reportSourceManifest(reportDocs, selectedReport?.documents || []);
          if (manifest.length) formData.append('source_manifest', JSON.stringify(manifest));
        }
        const { refs, skipped, text: sourceText } = await prepareReportSourceAttachments(attachmentItems, retrieveAttachmentFile, { preparedWord: true });
        notes += `\n\n${sourceText}`;
        if (skipped.length) {
          setAlert(skippedAttachmentMessage(skipped), 'warning');
        }
        refs.forEach((ref) => formData.append('file_references', ref));
        formData.append('notes', notes);
        formData.append('template', cleanTemplateText || '');
        formData.append('output_language', outputLanguage);
        formData.append('spelling_format', spellingFormat);

        const response = await postGenerateReport(formData, 'comprehensive_report', setPiiWarming, getReportLanguageSettings);
        const formattedSummaryHTML = requireReportContent(response.data.summary);
        // const formattedSummaryHTML = formatAIResponseToHTML(formattedSummary, selectedTemplate.content);

        // Save the generated report
        const reportId = uuidv4();
        const reportDate = new Date().toISOString();
        const sourceDocuments = useSavedReportSources ? selectedReport.documents : selectedItemsData.map(item => item.id);

        // Add this table to supabase
        const { error } = await supabase.from('generatedreports').insert({
          id: reportId,
          created_at: reportDate,
          updated_at: reportDate,
          patientId: id,
          content: formattedSummaryHTML,
          title: `${reportTemplate.title} - ${selectedReportDateLabel()}`,
          created_by: userDetails?.name || 'Unknown User',
          locked: false,
          documents: sourceDocuments,
          template_id: reportTemplate.id
        });
        
        if (error) throw error;

        const item = {
          id: reportId,
          createdAt: reportDate,
          title: `${reportTemplate.title} - ${selectedReportDateLabel()}`,
          type: 'Document',
          originalData: { content: formattedSummaryHTML, templateId: reportTemplate.id },
          createdBy: userDetails?.name || 'Unknown User',
          isSelected: false,
          parentId: null,
          isSubItem: false,
          isLocked: false,
          isShared: false,
          documents: sourceDocuments
        }

        await setReportDocuments(item);

        const formDataReport = new FormData();
        formDataReport.append('patient_id', id);
        formDataReport.append('document_type', 'report');
        formDataReport.append('report_content', formattedSummaryHTML);
        formDataReport.append('report_id', reportId);
        formDataReport.append('team_id', currentOrg.id);
        formDataReport.append('file_name', `${reportTemplate.title} - ${selectedReportDateLabel()}`);

        axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
        }).then(response => {
          if (response.data.status === 'failed') {
            setAlert("Failed to add embedding for the generated report", 'error');
          }
        }).catch(error => {
          console.error('Background Error adding embedding:', error);
        });

        handleMenuClose();
        if (!useSelectedSources) clearAllSelections();
        clearHighlights();
        setMainView('Consultation');
        setFirstView(false);
        setPopupReport(!useSelectedSources);
        setSelectedReport(item);
        setEditReportContent(true);
        setHistoryItems(prev => [
          item,
          ...prev
        ]);
        setAlert('Report generated successfully!', 'success');
        return item;
      } catch (error) {
        console.error('Error generating report:', error);
        // Surface the real reason (empty transcript, out of credits, under
        // review) instead of a dead-end generic message.
        setAlert(error?.message ? `Failed to generate report: ${error.message}` : 'Failed to generate report', 'error');
      } finally {
        setIsGeneratingReport(false);
        reportGenerationBusyRef.current = false;
        setConsultationLoading(false);
        setReportTemplateSelected(false);
      }
    };

    const handleItemLock = async (item) => {
      if(item.isLocked) {
        setAlert('This item is locked and cannot be unlocked.', 'warning');
        return;
      }
      const { error } = await supabase.from('generatedreports').update({ locked: !item.isLocked, updated_at: new Date().toISOString() }).eq('id', item.id);
      if (error) {
        console.error('Error updating report lock status:', error);
        setAlert('Failed to update report lock status', 'error');
      }
      setHistoryItems(prev => prev.map(historyItem =>
        historyItem.id === item.id ? { ...historyItem, isLocked: !historyItem.isLocked } : historyItem
      ));

    }

    const { handleItemShare, sharingDialog } = useDocumentSharing({ historyItems, setHistoryItems, setAlert, scopeKey: id });


    // Action menu for history items
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    
    // New states for template selection
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectingReportTemplate, setSelectingReportTemplate] = useState(false);
    const [defaultTemplate, setDefaultTemplate] = useState(null);
    const [defaultTemplateContent, setDefaultTemplateContent] = useState(null);
    const [showTemplateEditor, setShowTemplateEditor] = useState(false);
    const [templateSortConfig, setTemplateSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
    const [rightOptions, setRightOptions] = useState([
        { value: 'Select Template', label: 'Select Template' },
        // { value: 'Medical Coding', label: 'Medical Coding' },
    ]);
    const [reportTemplateSelected, setReportTemplateSelected] = useState(false);
    
    // Template editor instance
    const templateEditor = useEditor({
      extensions: [
        StarterKit,
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        TextStyle,
        Underline,
        ImageResize,
      ],
      content: '',
      editable: false,
    });
    
    // Function to fetch templates
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const { data, error } = await supabase
          .from('templates')
          .select('*')
          .eq('userid', userDetails.id)
          .order(templateSortConfig.key, { ascending: templateSortConfig.direction === 'asc' });
          
        if (error) throw error;

        const decryptionPromises = data.map(async (template) => {
          const [titleResult, contentResult, createdByResult] = await Promise.all([
            supabase.rpc('decrypt_text', { encrypted_data: template.title }),
            supabase.rpc('decrypt_text', { encrypted_data: template.content }),
            supabase.rpc('decrypt_text', { encrypted_data: template.created_by })
          ]);
          if(titleResult.error || contentResult.error || createdByResult.error) {
            return template;
          }
          template.title = titleResult.data;
          template.content = contentResult.data;
          template.created_by = createdByResult.data;

          return template;
        });

        const decryptedTemplates = await Promise.all(decryptionPromises);

        // Find the default template
        const templates = decryptedTemplates || [];
        const defaultTemplate = templates.find(t => t.default === "report") || (templates.length > 0 ? templates[0] : null);
        // const defaultTemplateTitle = defaultTemplate ? defaultTemplate.title : 'No templates available';
        // if(currentConsultation) {
        //   await fetchSectionSummary(currentConsultation.id, defaultTemplateTitle);
        // }
        setTemplates(templates);
        setSelectedTemplate(defaultTemplate || (templates.length > 0 ? templates[0] : null));
        setDefaultTemplate(defaultTemplate || (templates.length > 0 ? templates[0] : null));

        // const cleanTemplateText = defaultTemplate ? extractParagraphText(defaultTemplate.content) : (templates.length > 0 ? templates[0].content : null);
        const cleanTemplateText = defaultTemplate ? defaultTemplate.content : (templates.length > 0 ? templates[0].content : null);
        setDefaultTemplateContent(cleanTemplateText || '');
        
        // Update editor content if we have a selected template
        if (templateEditor && defaultTemplate?.content) {
          templateEditor.commands.setContent(defaultTemplate.content);
        }
      } 
      catch (error) {
        console.error('Error fetching templates:', error);
      }
      finally {
        setTemplatesLoading(false);
      }
    };
    
    const handleUploadTemplate = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      event.target.value = '';

      const allowedTypes = [
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/pdf'
      ];
      if (!allowedTypes.includes(file.type)) {
        setAlert('Please upload a .txt, .docx, or .pdf file', 'error');
        return;
      }

      setTemplatesLoading(true);
      try {
        let content = '';
        const fileName = file.name.split('.').slice(0, -1).join('.');

        if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          const arrayBuffer = await file.arrayBuffer();
          content = (await mammoth.convertToHtml({ arrayBuffer })).value;
        } else if (file.type === 'text/plain') {
          content = `<pre>${await file.text()}</pre>`;
        } else if (file.type === 'application/pdf') {
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/convert`, { method: 'POST', body: fd });
          if (!res.ok) { setAlert('Failed to process PDF file', 'error'); setTemplatesLoading(false); return; }
          await fetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/clear_uploads`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
          const arrayBuffer = await (await res.blob()).arrayBuffer();
          content = (await mammoth.convertToHtml({ arrayBuffer })).value;
        }

        const { data: inserted, error } = await supabase
          .from('templates')
          .insert([{ title: fileName, content, userid: userDetails.id, created_by: userDetails.name }])
          .select()
          .single();

        if (error) { setAlert('Failed to save template', 'error'); return; }

        setAlert(`Template "${fileName}" saved successfully!`, 'success');
        await fetchTemplates();
        const uploadedTemplate = { ...inserted, title: fileName, content };
        setSelectedTemplate(uploadedTemplate);
        return uploadedTemplate;
      } catch (err) {
        console.error('Error in handleUploadTemplate:', err);
        setAlert('An unexpected error occurred', 'error');
      } finally {
        setTemplatesLoading(false);
      }
    };

    // Function to request sorting of templates
    const requestTemplateSort = (key) => {
      const direction = templateSortConfig.key === key && templateSortConfig.direction === 'asc' ? 'desc' : 'asc';
      setTemplateSortConfig({ key, direction });
    };
    
    // Function to handle template selection
    const handleTemplateSelect = (template) => {
      track(EVENTS.TEMPLATE_SELECTED, { template_id: template?.id });
      setSelectedTemplate(template);
      if (templateEditor && template?.content) {
        templateEditor.commands.setContent(template.content);
      }
    };

    const templateOwnerRef = useRef(userDetails?.id);
    templateOwnerRef.current = userDetails?.id;
    const onTemplateSaved = template => {
      if (!templateOwnerRef.current || template.userid !== templateOwnerRef.current) return;
      setTemplates(previous => previous.some(item => item.id === template.id)
        ? previous.map(item => item.id === template.id ? template : item)
        : [...previous, template]);
    };

    const homePracticeTemplateBusyRef = useRef(false);
    const ensureHomeworkPracticeTemplate = async () => {
      if (homePracticeTemplateBusyRef.current) return null;
      homePracticeTemplateBusyRef.current = true;
      try {
        const template = await prepareHomePracticeTemplate(supabase, userDetails);
        setTemplates(previous => previous.some(item => item.id === template.id)
          ? previous.map(item => item.id === template.id ? template : item)
          : [...previous, template]);
        handleTemplateSelect(template);
        return template;
      } catch (error) {
        setAlert('Could not prepare the Home practice template. Please try again.', 'error');
        return null;
      } finally {
        homePracticeTemplateBusyRef.current = false;
      }
    };
    
    // Function to go back to template selection from template editor
    const handleBackToTemplateSelector = () => {
      setShowTemplateEditor(false);
      fetchTemplates(); // Refresh templates in case changes were made
    };
    
    // Effect to load templates when the selector is shown
    useEffect(() => {
      if ((showTemplateSelector && !showTemplateEditor) || !selectedTemplate) {
        fetchTemplates();
      }
    }, [showTemplateSelector, showTemplateEditor, templateSortConfig]);
    
    const handleMenuOpen = (event, item) => {
      setAnchorEl(event.currentTarget);
      setSelectedItem(item);
    };
    
    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedItem(null);
    };
    
    const handleViewItem = async (item) => {
      if (!confirmNavigation()) return;
      if(isRecognizing) {
        setAlert('Your current transcription is still ongoing. Please stop it or wait until it is finished before viewing another item.', 'warning');
        return;
      }
      handleMenuClose();
      setHighlightMode(false);
      setEditNote(false);
      setEditReportContent(false);
      setEditTranscription(false);
      setEditRemarks(false);
      setAddedCodes([]);
      setSuggestedCodes([]);
      clearHighlights();
      setTimer(0);
      if(chatMessages.length > 0) {
        setIsAnswerSourceOpen(true);
      } else {
        setIsAnswerSourceOpen(false);
      }
      
      if (item.type === 'Consultation') {
        setPopupReport(false);
        const index = pastConsultations.findIndex(c => c.id === item.id);
        if (index !== -1) {
          viewPastConsultation(index, item);
        }
      } 
      else if (item.type === 'Document') {
        await setReportDocuments(item);
        setMainView('Consultation');
        setFirstView(false);
        setPopupReport(true);
        setSelectedReport(item);
        setEditReportContent(true);
      } 
      else if (item.type === 'Attachment') {
        // Download the file
        downloadAttachment(item);
      } 
    };
    
    const handleDeleteItem = async (item) => {
      if (!confirmNavigation()) return;
      handleMenuClose();
      await deleteHistoryItem(item);
      setAlert(`${item.type} deleted successfully!`, 'success');

      // If the deleted item is currently open in the side-by-side view, clear it
      if (item.type === 'Consultation' && currentConsultation?.id === item.id) {
        setCurrentConsultation(null);
        setTranscription('');
        setRemarks('');
      } else if (item.type === 'Document' && isPopupReport && selectedReport?.id === item.id) {
        setPopupReport(false);
        setSelectedReport(null);
      }

      await fetchHistoryItems();
    };




const handleDownloadReport = async (item) => {
  handleMenuClose();
  try {
    // First check if we have HTML content to export
    if (!item || !item.originalData || !item.originalData.content) {
      setAlert('No content available to download', 'error');
      return;
    }
    
    // Get user ID for template layout lookup
    const userId = userDetails?.id;
    if (!userId) {
      setAlert('User information not available', 'error');
      return;
    }
    
    // Prepare document content
    let documentContent = item.originalData.content;
    let headerHtml = '';
    let footerHtml = '';
    
    // Fetch template layout if available
    const { data: templateLayout, error } = await supabase
      .from('templatelayout')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
      
    if (error) {
      console.error('Error fetching template layout:', error);
    }
    
    // If template layout exists, prepare header and footer HTML
    if (templateLayout) {
      const headerLayout = JSON.parse(templateLayout.header_layout || '[]');
      const footerLayout = JSON.parse(templateLayout.footer_layout || '[]');
      const organizationData = JSON.parse(templateLayout.organization_data || '{}');
      
      // Generate header HTML
      headerHtml = await generateLayoutHTML(
        headerLayout, 
        templateLayout.logo_url,
        templateLayout.signature_url,
        templateLayout.custom_image1_url,
        templateLayout.custom_image2_url,
        organizationData
      );
      
      // Generate footer HTML
      footerHtml = await generateLayoutHTML(
        footerLayout,
        templateLayout.logo_url,
        templateLayout.signature_url,
        templateLayout.custom_image1_url,
        templateLayout.custom_image2_url,
        organizationData
      );
    }

    // Create a new window for the print preview
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      setAlert('Pop-up blocked. Please allow pop-ups to preview document.', 'error');
      return;
    }
    
    // Begin writing HTML to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${item.title || 'Document'}</title>
        <meta charset="utf-8" />
        <style>
          @page {
            margin: 20mm 15mm;
            size: A4;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            color: #333;
          }
          .container {
            max-width: 210mm;
            margin: 0 auto;
          }
          .pdf-header {
            width: 100%;
            margin-bottom: 20px;
            padding-bottom: 10px;
            /* Removed border-bottom */
          }
          .pdf-content {
            margin: 20px 0;
            /* No borders by default */
          }
          .pdf-footer {
            width: 100%;
            margin-top: 20px;
            padding-top: 10px;
            /* Removed border-top */
          }
          /* General tables in body content */
          .pdf-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 16px 0;
          }
          .pdf-content td, .pdf-content th {
            border: 1px solid #D1D5DB;
            padding: 8px 12px;
            text-align: left;
          }
          .pdf-content th {
            background-color: #F9FAFB;
            font-weight: 600;
          }
          /* Layout tables in header/footer must NOT have borders */
          .pdf-header table, .pdf-footer table {
            border-collapse: collapse;
            border: none !important;
          }
          .pdf-header td, .pdf-header th, .pdf-footer td, .pdf-footer th {
            border: none !important;
          }
          @media print {
            .no-print {
              display: none;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .container {
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
    `);
        
        // Add export options at the top
        printWindow.document.write(`
          <div class="export-options no-print">
            <h3>Document Preview</h3>
            <p>Choose your preferred export format:</p>
            <button id="pdf-export-btn" class="export-btn pdf-btn">Export as PDF</button>
            <button id="word-export-btn" class="export-btn word-btn">Export as Word Document</button>
            <button id="excel-export-btn" class="export-btn excel-btn">Export as Excel</button>
          </div>
        `);
        
        // Add header if available
        if (headerHtml) {
          printWindow.document.write(`<div class="pdf-header">${headerHtml}</div>`);
        }
        
        // Add main content
        printWindow.document.write(`<div class="pdf-content">${documentContent}</div>`);
        
        // Add footer if available
        if (footerHtml) {
          printWindow.document.write(`<div class="pdf-footer">${footerHtml}</div>`);
        }
        
        // Finish the HTML document
        printWindow.document.write(`
              </div>
              <script>
                // Ensure layout tables remain border-free
                document.querySelectorAll('.pdf-header table, .pdf-header td, .pdf-header th, .pdf-footer table, .pdf-footer td, .pdf-footer th').forEach(el => {
                  el.style.border = 'none';
                });
                
                // PDF Export Button
                document.getElementById('pdf-export-btn').addEventListener('click', function() {
                  document.querySelector('.export-options').style.display = 'none';
                  window.print();
                  document.querySelector('.export-options').style.display = 'block';
                });
                
                // Word Export Button
                document.getElementById('word-export-btn').addEventListener('click', function() {
                  // Collect all the content needed for Word export
                  const docTitle = "${item.title || 'Document'}";
                  const headerContent = document.querySelector('.pdf-header') ? document.querySelector('.pdf-header').innerHTML : '';
                  const mainContent = document.querySelector('.pdf-content').innerHTML;
                  const footerContent = document.querySelector('.pdf-footer') ? document.querySelector('.pdf-footer').innerHTML : '';
                  
                  // Send message back to parent window to handle the Word export
                  window.opener.postMessage({
                    type: 'export-to-word',
                    data: {
                      title: docTitle,
                      header: headerContent,
                      content: mainContent,
                      footer: footerContent
                    }
                  }, '*');
                });
                
                // Excel Export Button
                document.getElementById('excel-export-btn').addEventListener('click', function() {
                  // 1. Check if the parent window still exists
                  if (!window.opener || window.opener.closed) {
                    alert("The original window was closed. Cannot export to Excel.");
                    return;
                  }

                  const docTitle = "${item.title || 'Document'}";
                  
                  // 2. Check if there are any tables inside the PDF content
                  const tables = document.querySelectorAll('.pdf-content table');
                  
                  if (!tables || tables.length === 0) {
                    alert("No tabular data found to export to Excel.");
                    return;
                  }

                  // 3. Send the entire content wrapper's HTML, which contains all tables
                  const contentHtml = document.querySelector('.pdf-content').innerHTML;

                  // 4. Secure the postMessage by specifying the origin
                  const targetOrigin = window.location.origin; 
                  
                  window.opener.postMessage({
                    type: 'export-to-excel',
                    data: {
                      title: docTitle,
                      content: contentHtml
                    }
                  }, targetOrigin);
                });
              </script>
            </body>
            </html>
          `);
        
        printWindow.document.close();
        
        setAlert('Document preview opened in a new window', 'success');

      } catch (error) {
        console.error('Error preparing document for preview:', error);
        setAlert('Failed to prepare document preview', 'error');
      }
    };
    
    // Helper function to generate HTML for header or footer from layout
    const generateLayoutHTML = (layout, logoUrl, signatureUrl, custom1Url, custom2Url, organizationData) => {
      if (!layout || layout.length === 0) return '';
      
      let html = '<table style="width:100%; border-collapse: collapse; border: none;">';
      
      // Create 5 rows (for the 5 rows in your grid)
      for (let row = 0; row < 5; row++) {
        html += '<tr>';
        
        // Each row has 3 columns
        for (let col = 0; col < 3; col++) {
          // Calculate the index in the original layout array
          const index = col * 5 + row;
          const gridItem = layout[index];
          
          // Set alignment based on column
          const textAlign = col === 2 ? 'right' : 'left';
          
          html += `<td style="width:33%; vertical-align:top; text-align:${textAlign}; padding:8px; border:none;">`;
          
          // Add content if this grid position has an item
          if (gridItem) {
            const itemType = gridItem.id.replace(/^[hf]-/, "");
            switch(itemType) {
              case "name":
                html += `<p style="font-weight: normal; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.name || 'Organization Name'}</p>`;
                break;
              case "email":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.email || 'Email'}</p>`;
                break;
              case "phone":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.phone || 'Phone Number'}</p>`;
                break;
              case "address":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.address || 'Address'}</p>`;
                break;
              case "practice-type":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.practice_type || 'Practice Type'}</p>`;
                break;
              case "practice-title":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.practice_title || 'Practice Title'}</p>`;
                break;
              case "website":
                html += `<p style="font-size: 12px; margin: 2px 0; white-space: pre-line; text-align: inherit;">${organizationData?.website || 'Website'}</p>`;
                break;
              case "logo":
                if (logoUrl) {
                  html += `<img src="${logoUrl}" alt="Logo" style="max-height: 60px; max-width: 180px; height: auto; width: auto; margin: 2px 0; display: block;" width="180" height="60" />`;
                } else {
                  html += `<div style="background-color: #eee; height: 60px; width: 100%; display: flex; align-items: center; justify-content: center; margin: 2px 0;">
                    <span style="color: #757575; font-size: 12px;">Logo</span>
                  </div>`;
                }
                break;
              case "signature":
                if (signatureUrl) {
                  html += `<img src="${signatureUrl}" alt="Signature" style="max-height: 40px; max-width: 120px; height: auto; width: auto; margin: 2px 0; display: block;" width="120" height="40" />`;
                } else {
                  html += `<div style="background-color: #eee; height: 60px; width: 100%; display: flex; align-items: center; justify-content: center; margin: 2px 0;">
                    <span style="color: #757575; font-size: 12px;">Signature</span>
                  </div>`;
                }
                break;
              case "custom1":
                if (custom1Url) {
                  html += `<img src="${custom1Url}" alt="Custom Image 1" style="max-height: 40px; max-width: 120px; height: auto; width: auto; margin: 2px 0; display: block;" width="120" height="40" />`;
                } else {
                  html += `<div style="background-color: #eee; height: 60px; width: 100%; display: flex; align-items: center; justify-content: center; margin: 2px 0;">
                    <span style="color: #757575; font-size: 12px;">Custom Image 1</span>
                  </div>`;
                }
                break;
              case "custom2":
                if (custom2Url) {
                  html += `<img src="${custom2Url}" alt="Custom Image 2" style="max-height: 40px; max-width: 120px; height: auto; width: auto; margin: 2px 0; display: block;" width="120" height="40" />`;
                } else {
                  html += `<div style="background-color: #eee; height: 60px; width: 100%; display: flex; align-items: center; justify-content: center; margin: 2px 0;">
                    <span style="color: #757575; font-size: 12px;">Custom Image 2</span>
                  </div>`;
                }
                break;
              default:
                html += `<p style="font-size: 12px; margin: 2px 0; text-align: inherit;">${gridItem.content || ''}</p>`;
            }
          }
          
          html += '</td>';
        }
        
        html += '</tr>';
      }
      
      html += '</table>';
      return html;
    };

    
    const downloadAttachment = async (item) => {
      try {
        setIsDownloading(true);
        const { data, error } = await supabase.storage
          .from('tikcare-attachments')
          .download(item.path);
          
        if (error) throw error;

        const key = await deriveKey(id);
        const decryptedBlob = await decryptFile(data, key, item.title);

        const url = URL.createObjectURL(decryptedBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = item.title;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading file:', error);
        setAlert('Failed to download file', 'error');
      } finally {
        setIsDownloading(false);
      }
    };

    // `displayName` is the original upload name (history item title); the storage
    // key's last segment may be an ASCII-safe rewrite of it and is what the IV
    // was derived from.
    const retrieveAttachmentFile = async (path, displayName = null) => {
      try {
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('tikcare-attachments')
          .download(path);

        if (downloadError) throw downloadError;

        const storageName = path.split('/').pop();
        const fileName = displayName || storageName;
        const mimeType = getMimeType(fileName);

        const key = await deriveKey(id);
        const decryptedBlob = await decryptFile(fileData, key, storageName);
        
        // Convert to File object
        const file = new File([decryptedBlob], fileName, { type: mimeType });
        return file;
      } catch (error) {
        console.error(`Error downloading attachment from path ${path}:`, error);
        throw error;
      }
    };

    const changeChiefComplaint = async (consultationId, newChiefComplaint) => {
      try {
        if (!newChiefComplaint || !newChiefComplaint.trim()) {
          setAlert('Please enter a valid title', 'error');
          return false;
        }

        const { error } = await supabase
          .from('consultations')
          .update({ 
            chiefComplaint: newChiefComplaint.trim(),
            updatedAt: new Date().toISOString()
          })
          .eq('id', consultationId);

        if (error) {
          console.error('Error updating consultation title:', error);
          setAlert('Failed to update consultation title', 'error');
          return false;
        }

        // Update the local state
        setPastConsultations(prev => 
          prev.map(consultation => 
            consultation.id === consultationId 
              ? { ...consultation, chiefComplaint: newChiefComplaint.trim() }
              : consultation
          )
        );

        // Update history items
        setHistoryItems(prev => 
          prev.map(item => 
            item.id === consultationId && item.type === 'Consultation'
              ? { ...item, title: newChiefComplaint.trim() }
              : item
          )
        );

        // Update current consultation if it's the one being edited
        setCurrentConsultation(prev => prev?.id === consultationId
          ? { ...prev, chiefComplaint: newChiefComplaint.trim() }
          : prev);

        setAlert('Consultation title updated successfully!', 'success');
        return true;
      } catch (error) {
        console.error('Error in changeChiefComplaint:', error);
        setAlert('An unexpected error occurred', 'error');
        return false;
      }
    };

    const updateConsultationSessionDate = async (value) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      if (!currentConsultation?.id) {
        // Check the migration before creating an otherwise empty consultation.
        const { error: schemaError } = await supabase.from('consultations').select('session_date').limit(1);
        if (schemaError) {
          setAlert('Session date is not available until the database migration is applied.', 'error');
          return false;
        }
      }
      const consultation = currentConsultation?.id ? currentConsultation : await insertConsultation();
      if (!consultation?.id) return false;
      const { error } = await supabase.from('consultations')
        .update({ session_date: value, updatedAt: new Date().toISOString() })
        .eq('id', consultation.id);
      if (error) {
        console.error('Could not update session date:', error);
        setAlert('Could not save the session date. The database migration may still be needed.', 'error');
        return false;
      }
      setCurrentConsultation(previous => previous?.id === consultation.id ? { ...previous, session_date: value } : previous);
      setPastConsultations(previous => previous.map(item => item.id === consultation.id ? { ...item, session_date: value } : item));
      setHistoryItems(previous => previous.map(item => item.id === consultation.id && item.type === 'Consultation'
        ? { ...item, sessionDate: value, originalData: { ...item.originalData, session_date: value } }
        : item));
      return true;
    };

    const changeReportTitle = async (reportId, newTitle) => {
      try {
        if (!newTitle || !newTitle.trim()) {
          setAlert('Please enter a valid title', 'error');
          return false;
        }
        const { error } = await supabase
          .from('generatedreports')
          .update({ title: newTitle.trim(), updated_at: new Date().toISOString() })
          .eq('id', reportId);
        if (error) {
          setAlert('Failed to update document title', 'error');
          return false;
        }
        setHistoryItems(prev =>
          prev.map(item =>
            item.id === reportId && item.type === 'Document'
              ? { ...item, title: newTitle.trim() }
              : item
          )
        );
        // The right-hand document panel keeps its own selected object. Keep it in sync with
        // the list update above so a successful rename is not immediately painted back as
        // the old title on the next render.
        setSelectedReport(prev => {
          if (!prev || String(prev.id) !== String(reportId)) return prev;
          return { ...prev, title: newTitle.trim(), originalData: { ...prev.originalData, title: newTitle.trim() } };
        });
        setLatestConsultationReport(prev =>
          prev && String(prev.id) === String(reportId) ? { ...prev, title: newTitle.trim() } : prev
        );
        setAlert('Document title updated successfully!', 'success');
        return true;
      } catch (error) {
        console.error('Error in changeReportTitle:', error);
        setAlert('An unexpected error occurred', 'error');
        return false;
      }
    };

    // Function to extract only text content from paragraph tags in HTML
    const extractParagraphText = (htmlContent) => {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = htmlContent;

      // Only consider block-level/textual containers to avoid double-counting inline tags
      const textualElements = tempElement.querySelectorAll(
        'p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, pre'
      );

      const elementTexts = Array.from(textualElements)
        .map(element => {
          // Prevent duplicate text for paragraphs inside list items, blockquotes, or table cells
          // because the parent container (li, blockquote, td, th) already captures the text content.
          if (element.tagName === 'P' && (
            element.closest('li') || 
            element.closest('blockquote') || 
            element.closest('td') || 
            element.closest('th')
          )) {
            return '';
          }

          const text = element.textContent.trim();

          if (!text) return '';

          if (element.tagName.match(/^H[1-6]$/)) return `\n${text}\n`;
          if (element.tagName === 'BLOCKQUOTE') return `\n"${text}"\n`;
          if (element.tagName === 'LI') return `• ${text}`;
          if (element.tagName === 'TD' || element.tagName === 'TH') return `${text}\t`;
          if (element.tagName === 'PRE') return `\n${text}\n`;

          return text;
        })
        .filter(Boolean);

      return elementTexts.join('\n').replace(/\n{3,}/g, '\n\n');
    };

    const formatAIResponseToHTML = (aiResponseText, existingHTMLContent = '') => {
      // Create a temporary DOM element to parse any existing HTML content
      const tempElement = document.createElement('div');
      tempElement.innerHTML = existingHTMLContent;
      
      const existingElements = [];
      // const elementsToPreserve = tempElement.querySelectorAll('img, table, ul, ol, blockquote, pre, h1, h2, h3, h4, h5, h6');
      const elementsToPreserve = tempElement.querySelectorAll('img, table');

      elementsToPreserve.forEach(element => {
        existingElements.push(element.outerHTML);
      });
      
      const paragraphs = aiResponseText.split(/\n{2,}/).filter(p => p.trim() !== '');
      const formattedParagraphs = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
      const combinedHTML = formattedParagraphs + existingElements.join('');
      
      return combinedHTML;
    };

    const checkDefaultPatientInsightsTemplate = async () => {
      try {
        const { data, error } = await supabase
          .from('templates')
          .select('*')
          .eq('userid', userDetails.id)
          .eq('default', 'insights')
          .maybeSingle();
        
        if (error) throw error;

        if (data && data.content) {
          const { data: decryptedContent, error: decryptError } = await supabase.rpc('decrypt_text', { encrypted_data: data.content });
          if (decryptError) {
            console.error('Error decrypting default patient insights template:', decryptError);
            return null;
          }
          return decryptedContent;
        } else {
          return null;
        }
      } catch (error) {
        console.error('Error fetching default patient insights template:', error);
      }
    }

    const generatePatientInsights = async (isRegenerate) => {
      if(!isRegenerate && patient.insights) {
        setPatientsInsightsData(patient.insights);
        setPopupPatientInsights(true);
        return;
      }

      if(!subscriptionInfo.is_active) {
        setAlert('Your subscription is not active. Please activate your subscription to generate reports.', 'error');
        return;
      }
      setPatientInsightsLoading(true);
      try {
        const selectedItemsData = historyItems.filter(item => item.type !== 'Document');
        // Prepare data for API call
        let notes = "";
        const attachmentItems = [];

        for (const item of selectedItemsData) {
          if (item.type === 'Consultation') {
            if (!hasReportInputText(item.originalData.transcription) && !hasReportInputText(item.originalData.notes)) continue;
            const currentConsultation = `
            Consultation on ${convertToGMT8Date(item.originalData.createdAt)}:\n
            ${item.originalData.transcription || ''}\n\n
            ${item.originalData.notes || ''}\n\n
            `
            notes += currentConsultation;
          } else if (item.type === 'Attachment' && !isAudioFileFormat(item.title)) {
            attachmentItems.push(item);
          }
        }

        const formData = new FormData();
        const { refs, skipped, text: sourceText } = await prepareReportSourceAttachments(attachmentItems, retrieveAttachmentFile);
        notes += `\n\n${sourceText}`;
        if (skipped.length) {
          setAlert(skippedAttachmentMessage(skipped), 'warning');
        }
        refs.forEach((ref) => formData.append('file_references', ref));
        formData.append('notes', notes);

        const defaultPatientInsightTemplate = await checkDefaultPatientInsightsTemplate();
        // console.log('defaultPatientInsightTemplate:', defaultPatientInsightTemplate);
        // console.log('the default template content:', defaultTemplateContent);
        if (defaultPatientInsightTemplate) {
          // const cleanTemplateText = extractParagraphText(defaultPatientInsightTemplate);
          const cleanTemplateText = defaultPatientInsightTemplate;
          formData.append('template', cleanTemplateText || '');
        } else {
          formData.append('template', defaultTemplateContent || '');
        }
        formData.append('output_language', outputLanguage);
        formData.append('spelling_format', spellingFormat);

        const response = await postGenerateReport(formData, isRegenerate ? 'regenerate' : 'patient_insights', setPiiWarming, getReportLanguageSettings);
        const formattedSummary = response.data.summary.replace(/\*\*|###/g, '');
        
        // Save the generated insights to the patient record
        const { error: updateError } = await supabase
          .from('patients')
          .update({ insights: formattedSummary, updatedAt: new Date().toISOString() })
          .eq('id', id);
        
        if (updateError) {
          console.error('Error saving patient insights:', updateError);
          setAlert('Generated insights but failed to save to database', 'warning');
        } else {
          if (patient) {
            setPatient(prev => ({
              ...prev,
              insights: formattedSummary
            }));
          }
          setAlert('Patient insights generated and saved successfully!', 'success');
        }
        
        setPatientsInsightsData(formattedSummary);
        setPopupPatientInsights(true);
      } catch (error) {
        console.error('Error fetching patient insights:', error);
        setAlert(error.message || 'Failed to fetch patient insights', 'error');
      } finally {
        setPatientInsightsLoading(false);
      }
    };

    const updatePatientInsights = async () => {
      try {
        setPatientInsightsLoading(true);
        
        // Save the edited insights to the patient record
        const { error: updateError } = await supabase
          .from('patients')
          .update({ 
            insights: patientsInsightsData,
            updatedAt: new Date().toISOString()
          })
          .eq('id', id);
        
        if (updateError) {
          console.error('Error updating patient insights:', updateError);
          setAlert('Failed to save patient insights', 'error');
          return false;
        }
        
        // Update local patient state if available
        if (patient) {
          setPatient(prev => ({
            ...prev,
            insights: patientsInsightsData
          }));
        }
        
        setAlert('Patient insights updated successfully!', 'success');
        return true;
        
      } catch (error) {
        console.error('Error in updatePatientInsights:', error);
        setAlert('Failed to update patient insights', 'error');
        return false;
      } finally {
        setPatientInsightsLoading(false);
      }
    };

    const updatePatient = async ( firstName, lastName, phoneNumber) => {
      // .select() so an RLS-filtered update (0 rows) is not reported as success
      const { data, error } = await supabase.from('patients').update({ name: `${lastName} ${firstName}`, firstName, lastName, phoneNumber }).eq('id', id).select('id');
      if (error){
        console.error(error);
        setAlert('Error updating patient information. Please try again.', 'error');
      }
      else if (!data || data.length === 0) {
        setAlert('Patient could not be updated — you may not have access to this record.', 'error');
      }
      else {
        setAlert('Patient information updated successfully!', 'success');
      }
    }

    const deletePatient = async () => {
      let { data, error } = await supabase.from('patients').delete().eq('id', id).select('id');
      if (error){
        console.error(error);
        setAlert('Error deleting patient. Please try again.', 'error');
      }
      else if (!data || data.length === 0) {
        setAlert('Patient could not be deleted — you may not have access to this record.', 'error');
      }
      else {
        setAlert('Patient deleted successfully!', 'success');
        navigate('/consultation');
      }
    }

    return {
      // Basic data
      id,
      userDetails,
      isAdminUser,
      userMode,
      
      // View states
      mainView,
      leftView,
      rightView,
      setRightView,
      setRightOptions,
      handleMainViewChange,
      handleLeftViewChange,
      handleRightViewChange,
      firstView,
      
      // Language
      language,
      setLanguage,
      
      // View options
      mainOptions: [
        { value: 'Consultation', label: 'Consultation' },
        { value: 'History', label: 'History' },
      ],
      leftOptions: [
        { value: 'Transcription', label: 'Transcription' },
        { value: 'Manual notes', label: 'Manual notes' }
      ],
      
      // Popup states
      isPopupReport,
      setPopupReport,
      isPopupPdfFill,
      setPopupPdfFill,
      
      // Consultation data
      currentConsultation,
      pastConsultations,
      sectionSummary,
      setSectionSummary,
      
      // Edit states
      editNote,
      setEditNote,
      
      // Content
      transcription,
      remarks,
      selectedReport,
      editReportContent,
      editPatientInsight,
      setTranscription,
      setRemarks,
      setSelectedReport,
      setEditReportContent,
      setEditPatientInsight,
      
      // Loading states
      loading,
      savingConsultation,
      transcriptionStage,
      recordedTranscriptionRecovery: recordedTranscriptionRecovery?.scope === recordedScope ? recordedTranscriptionRecovery : null,
      retryRecordedTranscriptionSave,
      checkRecordedTranscriptionResult,
      piiWarming,
      isRefiningTranscript,
      refineTranscriptError,
      refineTranscriptFailure,
      noSpeechDetected,
      hasCompletedTranscription,
      liveRecognitionInterrupted,
      liveSync,
      liveConnectionWarning,
      transcriptRevealToken,
      isPendingRefinement,
      refinedPreview,
      reportDateSelection,
      setReportDateSelection,
      setRefinedPreview,
      applyStreamingSpeakerLabels,
      acceptRefinement,
      discardRefinement,
      pendingRefineGate,
      refinePendingDialog,
      closeRefinePendingDialog,
      refineRegenerateDialog,
      closeRefineRegenerateDialog,
      regenerateAfterRefine,
      handleSaveTranscription,
      handleSaveRemarks,
      consultationLoading,
      codeLoading,
      patientInsightsLoading,
      patientsInsightsData,
      setPatientsInsightsData,
      generatePatientInsights,
      updatePatientInsights,
      popupPatientInsights,
      setPopupPatientInsights,
      
      // Speech recognition
      isRecognizing,
      isStarting,
      startupStage,
      isSettling,
      sttFromMic,
      
      // References
      textFieldRef,
      transcriptionRef,
      remarksRef,
      
      // File management
      files,
      handleFileChange,
      removeFile,
      downloadFile,
      handleFileSubmit,
      onInputClick,
      
      // Audio settings
      inputSource,
      inputSources,
      setInputSource,
      setSelectedMicId,
      isTeleHealthActive,
      setIsTeleHealthActive,
      
      // Medical coding
      addedCodes,
      suggestedCodes,
      selectedOption,
      setSelectedOption,
      
      // Actions
      handleCopy,
      addNewConsultation,
      insertConsultation,
      resetConsultationUI,
      viewPastConsultation,
      removeConsultation,
      
      // Dialog
      openDialog,
      setOpenDialog,
      
      // Utils
      convertToGMT8Date,
      convertToGMT8Time,
      setIsRecognizing,
      setAlert,
      handleItemLock,
      handleItemShare,
      sharingDialog,
      
      // Functions for child components
      fetchAddedCodes,
      fetchSuggestedCodes,
      saveSuggestedCodes,
      setAddedCodes,

      //Options
      MEDICAL_OPTIONS,
      languages,
      transcriptionLanguages,

      //Highlighting
      handleTextHighlight,
      renderHighlightedTranscription,
      clearHighlights,
      highlightMode,
      setHighlightMode,   
      highlightedTexts,
      highlightCount,
      transcriptionTextFieldRef,
      selectedText,
      setSelectedText,
      currentHighlightIndex,
      navigateToNextHighlight,
      navigateToPreviousHighlight,
      retrieving,
      reportDocs,


      //Chat
      chatMessages,
      newMessage,
      setNewMessage,
      handleSendMessage,
      isChatOpen,
      setIsChatOpen,
      isChatLogoOpen,
      setIsChatLogoOpen,
      chatBoxRef,

      // History view
      historyFilter,
      setHistoryFilter,
      searchQuery,
      setSearchQuery,
      selectAll,
      toggleSelectAllItems,
      sortConfig,
      requestSort,
      expandedRows,
      toggleRowExpansion,
      historyItems,
      getFilteredHistoryItems,
      toggleItemSelection,
      handlePatientFileUpload,
      isGeneratingReport,
      generateReportFromConsultation,
      generateComprehensiveReport,
      fetchHistoryItems,

      //options
      rightOptions,
      extractParagraphText,

      //templates
      selectedTemplate,
      defaultTemplate,
      setSelectedTemplate,
      setShowTemplateSelector,
      reportTemplateSelected,
      setReportTemplateSelected,
      showTemplateSelector,
      showTemplateEditor,
      requestTemplateSort,
      templateSortConfig,
      templates,
      handleTemplateSelect,
      ensureHomeworkPracticeTemplate,
      onTemplateSaved,
      templateEditor,
      handleBackToTemplateSelector,
      setShowTemplateEditor,
      selectingReportTemplate,
      setSelectingReportTemplate,

      //menu options
      anchorEl,
      handleMenuClose,
      handleMenuOpen,
      handleViewItem,
      handleDeleteItem,
      handleDownloadReport,
      selectedItem,

      //patient edit
      formSubmitted,
      setFormSubmitted,
      updatePatient,
      patient,
      setPatient,
      teamMembersData,
      deletePatient,

      setHistoryItems,
      responseSources,
      setResponseSources,
      openSource,
      setOpenSource,
      regenerateReportConsultation,
      subscriptionInfo,
      handleTranscribeAudio,
      isSupportedTranscriptionFile,
      isAudioFileFormat,
      audioOption,
      setAudioOption,
      lastRecording,
      setLastRecording,
      uploadProgress,
      retryRecordingUpload,
      shouldSaveAudio,
      shouldTranscribeNow,
      deleteSelectedHistoryItems,
      requestDeleteHistoryItems,
      clearAllSelections,
      selectedItems,
      setSelectedItems,
      editChiefComplaint,
      setEditChiefComplaint,
      changeChiefComplaint,
      updateConsultationSessionDate,
      latestComplaint,

      timer,
      setTimer,
      regenerateHistoryReport,

      saveChatMessageAsReport,
      handleSaveChatMessageAsReport,
      saveReportDialog,
      setSaveReportDialog,

      answerSource,
      setAnswerSource,
      isAnswerSourceOpen,
      setIsAnswerSourceOpen,
      currentOrg,
      isDownloading,
      templatesLoading,
      latestConsultationReport,
      isUploading,
      fileUploadProgress,

      showTranscriptionLanguagePopup,
      setShowTranscriptionLanguagePopup,
      selectedAudioItemForTranscription,
      performAudioTranscription,
      transcriptionLanguage,
      setTranscriptionLanguage,
      setSelectedAudioItemForTranscription,
      setDefaultTemplate,

      // Attachment file access (for preview modal)
      retrieveAttachmentFile,
      downloadAttachment,
      changeReportTitle,
      handleUploadTemplate,
      saveTextOnly,
      transcriptSaveStatus,
      cancelActiveRefinement,
      saveTranscriptBeforeLeaving,
      saveSelectedConsultationSource,
    };
}
